// 🌐 i18n
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, switchLanguage, getCurrentLanguageLabel, getPreferredLanguage } from './config/languages';
// 将这两个导入移到文件顶部，与其他导入语句放在一起
import IconMapper from './components/IconMapper';
import AgentChat from './components/AgentChat/AgentChat';
import { ADVANCED_MODE_CONFIG } from './config/advancedMode';
import { 
  EXPERT_SERVICE_URL, 
  AIDRAWIO_URL, 
  EDUGATE_API_URL,
  CCMA_WS_URL,
  CCMA_API_URL,
  DIFY_BASE_URL,
  GVA_API_URL,
  getAgentIframeUrl,
  HEP_AUTHING_LOGOUT_URL
} from './config/serviceConfig';
import FunASRClient from './utils/FunASRClient';
import { downloadText, toMarkdown } from './utils/youlaberChatlogExport';
import { captureYbgLocalLogin, completeLocalLogin, getPendingLocalLogin } from './utils/localLogin';
import { resolveBrandLogoUrl } from './utils/brandLogo';
import { buildAccentThemeStyle, resolveBrandMediaUrl } from './utils/brandTheme';
import './styles/workbenchDialog.css';

// 导入聊天界面的CSS模块
import styles from './ChatInterface.module.css';
// 导入文件面板的CSS模块
import fileStyles from './FilePanel.module.css';
import VirtualMessageList from './components/VirtualMessageList';
// 导入文件内容渲染组件
import { 
  renderFileContent, 
  getLanguage, 
  splitContentWithTables,
  detectContentType,
  handleFileLineSelection as fcHandleFileLineSelection,
  handleDiffLineClick as fcHandleDiffLineClick,
  handleCommentChange as fcHandleCommentChange,
  sendSelectedContentToBackend 
} from './FileContentRender';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useReducer,
  useMemo,
  startTransition,
  lazy,
  Suspense,
} from "react"
import HomeworkDialog from './HomeworkDialog';
import TaskSelectionDialog from './components/TaskSelectionDialog';
import NotificationCenter from './components/NotificationCenter';
import UserCoursesPanel from './components/UserCoursesPanel';
import CourseManagementDialog from './components/CourseManagementDialog';
import CoursePortal from './components/CoursePortal';
import TrainingCountdown, { startTrainingTimerClock } from './components/TrainingCountdown';
import AISecretaryPanel from "./AISecretaryPanel"
import "./App.css"
import CoreMarkdown from './components/core/CoreMarkdown';
//import "./optimized-styles.css"
import "./temp-styles.css" // 导入临时样式文件修复布局问题
import "./draft-enhanced-styles.css" // 导入Draft消息和Mermaid图表增强样式
//import "./ChatInterface.css" // 导入聊天界面专用样式，实现用户消息靠右等新设计
import { FiPrinter } from 'react-icons/fi'; // 添加打印图标
// 🚀 优化：使用直接路径导入减少 bundle 体积
import Tooltip from "@mui/material/Tooltip"
import Avatar from "@mui/material/Avatar"
import Grid from "@mui/material/Grid"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Stack from "@mui/material/Stack"
import PersonIcon from "@mui/icons-material/Person"
import UserIcon from "@mui/icons-material/Person"
import SwitchLeftIcon from "@mui/icons-material/SwitchLeft"
import LightbulbIcon from "@mui/icons-material/Lightbulb"
import MemoryIcon from "@mui/icons-material/Memory"
import TranslateIcon from "@mui/icons-material/Translate"
import KeyboardIcon from "@mui/icons-material/Keyboard"
import ComputerIcon from "@mui/icons-material/Computer"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import MoreVertIcon from "@mui/icons-material/MoreVert"
// 在其他 import 语句后面添加
import RoleSelector from "./RoleSelector"
import ExpertSelector from "./ExpertSelector"
import Modal from "./Modal"
import ProjectProgressBar from "./ProjectProgressBar"
import { FaCog } from "react-icons/fa"
import {
  MicroSceneBubbleList,
  MicroSceneDetailModal,
  MicroSceneCard,
} from "./MicroScene"
import LevelIcon from "./components/LevelIcon"
import LevelUpAnimation from "./components/LevelUpAnimation"
import AchievementUnlocked from "./components/AchievementUnlocked"
import FloatingXPBar from "./components/FloatingXPBar"
import TodayYouEntryRow from "./components/TodayYouEntryRow"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import IconButton from "@mui/material/IconButton"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import MessageRenderer from "./components/MessageRenderer";
import ModalLayer from "./components/ModalLayer";
import useNotificationState from "./hooks/useNotificationState";
import useNotificationHandlers from "./hooks/useNotificationHandlers";
import useModalState from "./hooks/useModalState";
import useMessageState from "./hooks/useMessageState";
import useMessageHandlers from "./hooks/useMessageHandlers";
import useMicroSceneBubbles from "./hooks/useMicroSceneBubbles";
import useMessageHistoryTracking from "./hooks/useMessageHistoryTracking";
import useWsCommonMessageHandlers from "./hooks/useWsCommonMessageHandlers";
import useWsTaskSelectionHandler from "./hooks/useWsTaskSelectionHandler";
import useWsExpertDialogHandler from "./hooks/useWsExpertDialogHandler";
import useWsArchiveHandlers from "./hooks/useWsArchiveHandlers";
import useWsEditorEvents from "./hooks/useWsEditorEvents";
import useWsRoleStatusHandler from "./hooks/useWsRoleStatusHandler";
import useWsMessageHandlers from "./hooks/useWsMessageHandlers";
import useWsActionAnswerHandler from "./hooks/useWsActionAnswerHandler";
import useWsGameProfileHandler from "./hooks/useWsGameProfileHandler";
import useWsTodayYouHandler from "./hooks/useWsTodayYouHandler";
import useWsPostMessageEffects from "./hooks/useWsPostMessageEffects";
import useWsMessageDispatcher from "./hooks/useWsMessageDispatcher";
import useWebSocketLifecycle from "./hooks/useWebSocketLifecycle";
import useFilePanelHandlers from "./hooks/useFilePanelHandlers";
import useDraftAndConflictHandlers from "./hooks/useDraftAndConflictHandlers";
import useFileContentInteractions from "./hooks/useFileContentInteractions";
import useFileUploadHandlers from "./hooks/useFileUploadHandlers";
import useExpertSelection from "./hooks/useExpertSelection";
import useTokenState from "./hooks/useTokenState";
import useSelfAssessmentState from "./hooks/useSelfAssessmentState";
import useCapabilityProfileState from "./hooks/useCapabilityProfileState";
import useResearchCenterState from "./hooks/useResearchCenterState";
import useFileManagementState from "./hooks/useFileManagementState";
import useSessionState from "./hooks/useSessionState";
import useExpertState from "./hooks/useExpertState";
import useUIState from "./hooks/useUIState";
import useArchiveState from "./hooks/useArchiveState";
import useDiagramState from "./hooks/useDiagramState";
import useDiscussState from "./hooks/useDiscussState";
import useGroupChatState from "./hooks/useGroupChatState";
import useDigitalHumanState from "./hooks/useDigitalHumanState";
import useGameState from "./hooks/useGameState";
import useTaskSelectionState from "./hooks/useTaskSelectionState";
import useVoiceState from "./hooks/useVoiceState";
import useInputState from "./hooks/useInputState";
import useAgentState from "./hooks/useAgentState";
import useModelSelectionState from "./hooks/useModelSelectionState";
import useLearningState from "./hooks/useLearningState";
import useRoleFilterState from "./hooks/useRoleFilterState";
import usePasswordDialogState from "./hooks/usePasswordDialogState";
import useClawPasswordState from "./hooks/useClawPasswordState";
import useCCState from "./hooks/useCCState";
import useWsCCHandler from "./hooks/useWsCCHandler";
import CCDeviceSelector from "./components/CCPanel/CCDeviceSelector";
import YouLaberPanel from "./components/CCPanel/YouLaberPanel";
import YouLaberFullscreenOverlay from "./components/CCPanel/YouLaberFullscreenOverlay";
import CCPermissionDialog from "./components/CCPanel/CCPermissionDialog";
import Breadcrumb from "./components/Layout/Breadcrumb";
import TopHeader from "./components/Layout/TopHeader";
import useMiscState from "./hooks/useMiscState";
import useAttachmentState from "./hooks/useAttachmentState";
// useExpertSelection 已在上方导入
import usePasswordHandlers from "./hooks/usePasswordHandlers";

import ErrorModal from "./ErrorModal"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { v4 as uuidv4 } from "uuid"
import CloseIcon from "@mui/icons-material/Close"
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
// 导入作业对话框组件
import CasCallback from './components/CasCallback';
// 添加菜单项图标
import HelpIcon from "@mui/icons-material/Help"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import ScienceIcon from "@mui/icons-material/Science"
import TokenIcon from "@mui/icons-material/Token"
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import SchoolIcon from "@mui/icons-material/School"
import KeyIcon from "@mui/icons-material/Key"
import HistoryIcon from "@mui/icons-material/History"
import RestoreIcon from "@mui/icons-material/Restore"
import ArchiveIcon from "@mui/icons-material/Archive"
import SettingsIcon from "@mui/icons-material/Settings"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import Divider from "@mui/material/Divider"
import { FiCopy, FiX, FiChevronRight } from 'react-icons/fi';
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DescriptionIcon from "@mui/icons-material/Description"
import CheckIcon from "@mui/icons-material/Check"
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import MenuIcon from "@mui/icons-material/Menu"
import FlowTypeDialog from './FlowTypeDialog'; // 导入FlowTypeDialog组件
import YuyanProjectDialog from './components/YuyanProjectDialog'; // 导入双创项目对话框
// 在文件顶部，导入 LazyMermaidRenderer 组件
import AuthPage from './components/Auth/AuthPage';
import FullscreenDrawingView from './components/FullscreenDrawingView';
import DiscussionPanel from './components/DiscussionPanel';
import GroupChatPanel from './components/GroupChatPanel';

// 🚀 动态导入重型组件，减少主 Bundle 体积
const ResearchCenter = lazy(() => import('./components/ResearchCenter'));
const DigitalHumanDialog = lazy(() => import('./components/DigitalHumanDialog'));
const DigitalHumanSimulationDialog = lazy(() => import('./components/DigitalHumanSimulationDialog'));

/** 登录后按学生 ID 补查机构品牌（CAS 等通道用） */
async function fetchStudentBranding(studentId) {
  if (!studentId) {
    return { organizationId: null, branding: null };
  }
  try {
    const response = await fetch(
      `${EDUGATE_API_URL}/api/student-branding?student_id=${encodeURIComponent(studentId)}`
    );
    if (!response.ok) {
      return { organizationId: null, branding: null };
    }
    const result = await response.json();
    const data = result.data || {};
    return {
      organizationId: data.organizationId || null,
      branding: data.branding || null,
    };
  } catch (error) {
    console.error('获取机构品牌失败:', error);
    return { organizationId: null, branding: null };
  }
}

/**
 * 🔧 模块级别常量 - 避免每次渲染创建新对象/函数
 * 解决 rerender-memo-with-default-value 规则违反问题
 */
const EMPTY_OPTIONS = Object.freeze({});
const NOOP_TOGGLE = () => {};
const GUIDE_KEY = 'YBG_Guide';
const GUIDE_VERSION = 'v1';
const GUIDE_AGENT = Object.freeze({
  id: GUIDE_KEY,
  name: '邮百工导览员',
  description: '帮你从“找答案”进入“练判断”，快速选择合适的邮百工入口。',
  category: '入场导览',
  icon: 'HelpOutline',
  iconColor: 'guide',
  flow: ['plaza'],
});
const GUIDE_DOC_LINKS = Object.freeze([
  {
    title: '邮百工入门手册',
    subtitle: '造物初体验',
    url: 'https://ccnmho04amw8.feishu.cn/docx/WVnmdDDnJodxV9xsxN1cNT2ynYd?from=from_copylink',
  },
  {
    title: '青铜、双创流程说明',
    subtitle: '综合训练流程简介',
    url: 'https://ccnmho04amw8.feishu.cn/docx/RoofdeOtCoOht7xgntHcnAgPnwd?from=from_copylink',
  },
  {
    title: '教师端使用指南',
    subtitle: '教师训练管理',
    url: 'https://ccnmho04amw8.feishu.cn/docx/ZBkydZUXHocYLZxuBDeccLOqnab?from=from_copylink',
  },
  {
    title: '学生提交作业指南',
    subtitle: '作业提交与评价',
    url: 'https://kcn236f644lj.feishu.cn/wiki/EC0bw19dbiwcAXkjli3cVaMbnMg?from=from_copylink',
  },
]);
const GUIDE_WELCOME_CONTENT = `欢迎来到邮百工。这里和普通 AI 助教不太一样：你进入的不是一个聊天框，而是教师设计的工程剧本和多智能体组织起来的虚拟工程社会。AI 不只是回答问题，而是担任导师、专家、陪练、实验员和评审，通过追问、质疑、约束、演练和验证来陪你练工程判断。

你这次更像是哪种情况？

:::choice-group field="user_intent" label="你这次更像是哪种情况？" mode="single"
- 四种训练方式怎么选 (value="choose_training_mode")
- 综合训练流程很多，怎么选 (value="choose_flow")
- 先看懂邮百工功能构成 (value="understand_functions")
- 看懂YouLabber如何把方案变成实验 (value="youlaber_intro")
- 看懂AI评价与能力画像怎么来的 (value="ai_evaluation")
- 我想直接开始体验 (value="quick_try")
- 我是老师，想了解教师端 (value="teacher_info")
- 我已经进来了，但不知道点哪里 (value="lost")
- 我不理解为什么 AI 不直接回答 (value="confused_no_answer")
- 其他想法 (value="other")
:::`;

/**
 * 🔧 提升到模块级别的 CopyButton 组件
 * 解决 rendering-hoist-jsx 规则违反问题
 * 之前在 renderMessageContent 内部定义，导致每次渲染都创建新组件类型
 */
const MessageCopyButton = React.memo(({ content, msg, copyToClipboard, t }) => {
  const getFormattedContent = () => {
    // 直接获取消息文本内容的安全方法
    const getTextSafely = (content) => {
      if (!content) return '';
      return typeof content === 'object' 
        ? JSON.stringify(content, null, 2)  
        : String(content || '').replace(/\\n/g, '\n');
    };
    
    // 对所有消息添加基本的内容提取保障
    if (msg?.context) {
      return getTextSafely(msg.context);
    }
    
    // 以下是原有的特殊处理逻辑
    if (msg?.role === "Expert" && typeof content === 'string') {
      const lines = content.split('\n');
      return lines.slice(0, -7).join('\n');
    }
    
    if ((msg?.role === "Draft" || msg?.type === "msg") && Array.isArray(content)) {
      const addedContents = content
        .filter(item => item.type === "added")
        .map(item => {
          if (!item.content) return '';
          if (typeof item.content === 'string') {
            return item.content.replace(/\\n/g, '\n').trim();
          }
          return JSON.stringify(item.content, null, 2);
        })
        .filter(Boolean)
        .join('\n');
      
      return addedContents || getTextSafely(content);
    }
    
    return getTextSafely(content);
  };
  
  const textToCopy = getFormattedContent();
  
  return (
    <button 
      className="copy-button"
      onClick={() => copyToClipboard(textToCopy)}
      title={t('copy_content')}
    >
      <span role="img" aria-label={t('copy_aria')}>📋</span>
    </button>
  );
});

// 顶层定义：稳定 FullscreenAgentView 组件类型，避免随 App 重渲染而卸载重建
// 移除外层 header，返回功能整合到 AgentChat 头部
const FullscreenAgentView = React.memo(({
  agent,
  onClose,
  user,
  expertServiceUrl,
  flowName = '',
  onA2UIAction,
  welcomeContentOverride = '',
  className = '',
  projectName = '训练广场',
  variant = 'default',
  guideDocLinks = [],
  headerAccessory = null,
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  return (
    <div
      className={`partial-fullscreen-overlay ${variant === 'guide' ? 'guide-fullscreen-overlay' : 'agent-fullscreen-overlay'} ${className}`}
      style={buildAccentThemeStyle(user?.branding, variant === 'guide' ? 'guide' : 'agent')}
    >
      <div className="fullscreen-agent-container">
        <AgentChat
          agent={agent}
          user={user}
          expertServiceUrl={expertServiceUrl}
          onBack={onClose}
          onA2UIAction={onA2UIAction}
          welcomeContentOverride={welcomeContentOverride}
          flowName={flowName}
          projectName={projectName}
          variant={variant}
          guideDocLinks={guideDocLinks}
          headerAccessory={headerAccessory}
        />
      </div>
    </div>
  );
});

// 注册语言
// 访客服务URL - 使用远程服务 - MOVED TO AuthPage.js
// const AUTH_SERVER_URL = "http://49.233.10.4:5013"
// 访客服务URL - 使用本地服务
//const AUTH_SERVER_URL = "http://127.0.0.1:5013"
// 短信服务URL - 使用远程服务 - MOVED TO AuthPage.js
// const REMOTE_SMS_SERVER_URL = "http://49.233.10.4:5013"

// 添加 Object.hasOwn polyfill
if (!Object.hasOwn) {
  Object.hasOwn = function(object, property) {
    if (object == null) {
      throw new TypeError('Cannot convert undefined or null to object');
    }
    return Object.prototype.hasOwnProperty.call(Object(object), property);
  };
}

// 🔧 缓存与纯函数：提升内容处理与折叠判断性能
const PREPROCESS_CACHE_LIMIT = 200;
const preprocessContentCache = new Map();

const preprocessContentWithCache = (content) => {
  if (typeof content !== 'string') return content;
  if (preprocessContentCache.has(content)) {
    return preprocessContentCache.get(content);
  }

  // 移除 [CONTENT] 和 [/CONTENT] 标签
  let result = content.replace(/\[CONTENT\]|\[\/CONTENT\]/g, "");

  // 将 JSON 字符串转换为格式化的 JSON
  const jsonRegex = /\{[\s\S]*?\}/g;
  result = result.replace(jsonRegex, (match) => {
    try {
      const parsed = JSON.parse(match);
      return "```json\n" + JSON.stringify(parsed, null, 2) + "\n```";
    } catch (e) {
      return match;
    }
  });

  // 确保每个主要部分之间有足够的换行
  result = result.replace(/\n## /g, "\n\n## ");

  preprocessContentCache.set(content, result);
  if (preprocessContentCache.size > PREPROCESS_CACHE_LIMIT) {
    const oldestKey = preprocessContentCache.keys().next().value;
    preprocessContentCache.delete(oldestKey);
  }

  return result;
};

const shouldCollapseByValue = (value) => {
  if (value == null) return false;
  if (typeof value === 'string') {
    const lineCount = value.split('\n').length;
    return lineCount > 15 || value.length > 400;
  }
  if (Array.isArray(value)) return value.length > 6;
  if (typeof value === 'object') return true;
  return false;
};


function AppRouter() {
  // 检查当前路径
  const path = window.location.pathname;
  if (path === '/cas-callback') {
    return <CasCallback />;
  }
  if (path === '/legal/viewer') {
    return <LegalViewerPage />;
  }
  return <App />;
}

function LegalViewerPage() {
  const params = new URLSearchParams(window.location.search);
  const file = params.get('file');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!file) {
      setError('缺少文件参数');
      setLoading(false);
      return;
    }

    const title = decodeURIComponent(file).replace(/\.md$/, '');
    document.title = `邮百工 · ${title}`;

    fetch(`/legal/${encodeURIComponent(file)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('文件加载失败，请关闭后重试。');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  const title = file ? decodeURIComponent(file).replace(/\.md$/, '') : '法律文件';

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', background: '#f6f8fb', color: '#111827' }}>
      <header style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', background: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
        <span style={{ fontWeight: 700, color: '#4f46e5' }}>邮百工</span>
        <span style={{ margin: '0 10px', color: '#9ca3af' }}>|</span>
        <span style={{ color: '#111827' }}>{title}</span>
      </header>
      <main style={{ maxWidth: 920, margin: '20px auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24, color: '#111827', lineHeight: 1.7 }}>
        {loading && <div style={{ color: '#6b7280' }}>加载中...</div>}
        {!loading && error && <div style={{ color: '#dc2626' }}>{error}</div>}
        {!loading && !error && (
          <div style={{ color: '#111827' }}>
            <CoreMarkdown content={content} />
          </div>
        )}
      </main>
    </div>
  );
}

const PENDING_ARCHIVE_RECOVERY_KEY = 'ybg.pendingArchiveRecovery';

function App() {
  // 🌐 i18n hook
  const { t, i18n } = useTranslation();
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  
  // 移除路径检查代码,直接开始组件逻辑
  // isExpertDropdownOpen 已迁移到 useExpertState hook
  
  // 🖨️ 打印/导出PDF处理函数 - 使用DOM操作确保只打印文件内容
  const handlePrintFile = useCallback(async () => {
    // 1. 找到打印根元素
    const printRoot = document.querySelector('[data-print-root="file-panel"]');
    if (!printRoot) {
      console.error('找不到打印根元素');
      return;
    }

    // 2. 克隆打印内容（避免修改原始DOM）
    const printContent = printRoot.cloneNode(true);
    
    // 3. 移除克隆内容中的按钮和交互元素
    printContent.querySelectorAll('button, input, textarea, [class*="fileActions"]').forEach(el => el.remove());

    // 4. 创建打印专用容器
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.cssText = `
      position: fixed;
      left: 0;
      top: 0;
      width: 100%;
      height: auto;
      background: #fff;
      color: #000;
      z-index: 2147483647;
      padding: 20px;
      box-sizing: border-box;
      overflow: visible;
    `;
    
    // 5. 添加打印样式
    const printStyle = document.createElement('style');
    printStyle.id = 'print-style';
    printStyle.textContent = `
      @media print {
        body > *:not(#print-container) {
          display: none !important;
        }
        #print-container {
          position: static !important;
          width: 100% !important;
          height: auto !important;
          overflow: visible !important;
          padding: 0 !important;
        }
        #print-container * {
          background: #fff !important;
          color: #000 !important;
          box-shadow: none !important;
          text-shadow: none !important;
          max-width: none !important;
        }
        #print-container h1, #print-container h2, #print-container h3 {
          color: #000 !important;
          page-break-after: avoid;
        }
        #print-container table {
          width: 100% !important;
          border-collapse: collapse !important;
        }
        #print-container th, #print-container td {
          border: 1px solid #333 !important;
          padding: 6px !important;
          color: #000 !important;
          background: #fff !important;
        }
        #print-container th {
          background: #eee !important;
        }
        #print-container pre, #print-container code {
          background: #f5f5f5 !important;
          color: #333 !important;
          white-space: pre-wrap !important;
          page-break-inside: avoid;
        }
        #print-container img {
          width: 100% !important;
          max-width: 100% !important;
          page-break-inside: avoid;
        }
        #print-container [data-mermaid-source] {
          width: 100% !important;
          max-width: 100% !important;
        }
        #print-container [data-mermaid-source] img {
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          display: block !important;
          margin: 0 auto !important;
        }
        #print-container [class*="mermaid"] svg {
          background: #fff !important;
          max-width: 100% !important;
        }
      }
    `;

    // 6. 应用内联样式到克隆的内容（确保白底黑字）
    printContent.style.cssText = 'background:#fff;color:#000;';
    const allElements = printContent.querySelectorAll('*');
    allElements.forEach(el => {
      el.style.background = '#fff';
      el.style.backgroundColor = '#fff';
      el.style.color = '#000';
      el.style.boxShadow = 'none';
      el.style.textShadow = 'none';
      el.style.maxWidth = 'none';
      // 解除高度限制
      if (el.style.maxHeight) el.style.maxHeight = 'none';
      if (el.style.overflow === 'hidden' || el.style.overflow === 'auto' || el.style.overflow === 'scroll') {
        el.style.overflow = 'visible';
      }
    });

    // 6.1 修复 Mermaid SVG 打印对比度（避免深色主题导致打印不可读）
    const normalizeMermaidSvgForPrint = (svg) => {
      if (!svg) return;
      svg.style.background = '#fff';
      svg.style.backgroundColor = '#fff';
      // 移除SVG内部样式，避免覆盖打印颜色
      svg.querySelectorAll('style').forEach((styleEl) => styleEl.remove());

      // 背景矩形
      svg.querySelectorAll('.background, rect.background').forEach((bg) => {
        bg.setAttribute('fill', '#ffffff');
        bg.style.setProperty('fill', '#ffffff', 'important');
        bg.style.opacity = '1';
      });

      const isInside = (el, selector) => Boolean(el && el.closest && el.closest(selector));

      // 节点/子图形状（更激进：除边与箭头外全部强制描边）
      svg.querySelectorAll('rect, polygon, circle, path').forEach((shape) => {
        if (shape.classList?.contains('background') || isInside(shape, 'marker')) return;
        if (isInside(shape, '.edgePath') || isInside(shape, '.flowchart-link')) return;

        // 清理可能覆盖打印样式的内联样式
        shape.removeAttribute('style');

        shape.setAttribute('fill', '#ffffff');
        shape.style.setProperty('fill', '#ffffff', 'important');
        shape.setAttribute('stroke', '#000000');
        shape.style.setProperty('stroke', '#000000', 'important');
        shape.style.opacity = '1';
        shape.style.fillOpacity = '1';
        shape.style.strokeOpacity = '1';
        shape.style.setProperty('stroke-width', '1.2', 'important');
      });

      // 连线
      svg.querySelectorAll('.edgePath path, .flowchart-link').forEach((edge) => {
        edge.setAttribute('stroke', '#000000');
        edge.style.setProperty('stroke', '#000000', 'important');
        edge.style.opacity = '1';
        edge.style.strokeOpacity = '1';
      });

      // 箭头
      svg.querySelectorAll('marker path').forEach((arrow) => {
        arrow.setAttribute('fill', '#000000');
        arrow.style.setProperty('fill', '#000000', 'important');
        arrow.style.opacity = '1';
      });

      // 文本
      svg.querySelectorAll('text, .label, .nodeLabel, .edgeLabel').forEach((textEl) => {
        textEl.setAttribute('fill', '#000000');
        textEl.style.setProperty('fill', '#000000', 'important');
        textEl.style.opacity = '1';
      });
      svg.querySelectorAll('foreignObject *').forEach((el) => {
        el.style.setProperty('color', '#000000', 'important');
        el.style.opacity = '1';
      });

      // 边缘标签背景
      svg.querySelectorAll('.edgeLabel rect, .label rect').forEach((rect) => {
        rect.setAttribute('fill', '#ffffff');
        rect.style.setProperty('fill', '#ffffff', 'important');
        rect.setAttribute('stroke', '#000000');
        rect.style.setProperty('stroke', '#000000', 'important');
        rect.style.opacity = '1';
      });

      // 修正打印偏移：根据内容 bbox 重新设置 viewBox
      try {
        const bbox = svg.getBBox();
        if (bbox && bbox.width > 0 && bbox.height > 0) {
          const padding = 4;
          const viewBox = [
            Math.max(0, bbox.x - padding),
            Math.max(0, bbox.y - padding),
            bbox.width + padding * 2,
            bbox.height + padding * 2,
          ].join(' ');
          svg.setAttribute('viewBox', viewBox);
          svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          svg.style.width = '100%';
          svg.style.height = 'auto';
          svg.style.display = 'block';
          svg.style.margin = '0';
        }
      } catch (e) {
        // getBBox 可能在某些浏览器打印上下文不可用，忽略
      }
    };

    printContent.querySelectorAll('svg').forEach((svg) => {
      const isMermaid = svg.closest('[class*="mermaid"]');
      if (isMermaid) {
        normalizeMermaidSvgForPrint(svg);
      }
    });

    // 6.2 重新渲染 Mermaid（打印专用：禁用 htmlLabels，避免 foreignObject 打印失败）
    const mermaidContainers = printContent.querySelectorAll('[data-mermaid-source]');
    if (mermaidContainers.length > 0) {
      const { preprocessMermaidChart } = await import('./components/core/ContentUtils');
      const mermaid = (await import('mermaid')).default;

      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        securityLevel: 'loose',
        logLevel: 'error',
        suppressErrorRendering: true,
        flowchart: { htmlLabels: false },
        themeVariables: {
          background: '#ffffff',
          primaryColor: '#ffffff',
          secondaryColor: '#ffffff',
          tertiaryColor: '#ffffff',
          primaryTextColor: '#000000',
          secondaryTextColor: '#000000',
          tertiaryTextColor: '#000000',
          primaryBorderColor: '#000000',
          secondaryBorderColor: '#000000',
          tertiaryBorderColor: '#000000',
          lineColor: '#000000',
          textColor: '#000000',
          nodeBorder: '#000000',
          nodeBkg: '#ffffff',
          clusterBkg: '#ffffff',
          clusterBorder: '#000000',
          defaultLinkColor: '#000000',
          titleColor: '#000000',
          edgeLabelBackground: '#ffffff',
          actorBorder: '#000000',
          actorBkg: '#ffffff',
          actorTextColor: '#000000',
          signalColor: '#000000',
          signalTextColor: '#000000',
          labelBoxBkgColor: '#ffffff',
          labelBoxBorderColor: '#000000',
          labelTextColor: '#000000',
          loopTextColor: '#000000',
          noteBkgColor: '#ffffff',
          noteTextColor: '#000000',
          noteBorderColor: '#000000',
          altBackground: '#ffffff',
          fontSize: '14px',
        },
      });

      const svgToDataUrl = (svgEl) => {
        const serializer = new XMLSerializer();
        let source = serializer.serializeToString(svgEl);
        if (!source.includes('http://www.w3.org/2000/svg')) {
          source = source.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        const encoded = encodeURIComponent(source)
          .replace(/%0A/g, '')
          .replace(/%20/g, ' ');
        return `data:image/svg+xml;charset=utf-8,${encoded}`;
      };

      const renderMermaidToPng = async (svgMarkup) => {
        const temp = document.createElement('div');
        temp.style.position = 'fixed';
        temp.style.left = '-99999px';
        temp.style.top = '-99999px';
        temp.style.width = '1px';
        temp.style.height = '1px';
        temp.style.visibility = 'hidden';
        temp.innerHTML = svgMarkup;
        document.body.appendChild(temp);

        const svgEl = temp.querySelector('svg');
        let width = 800;
        let height = 600;
        if (svgEl) {
          try {
            const bbox = svgEl.getBBox();
            if (bbox.width > 0 && bbox.height > 0) {
              const padding = 4;
              const viewBox = [
                Math.max(0, bbox.x - padding),
                Math.max(0, bbox.y - padding),
                bbox.width + padding * 2,
                bbox.height + padding * 2,
              ].join(' ');
              width = Math.max(800, bbox.width + padding * 2);
              height = Math.max(300, bbox.height + padding * 2);
              svgEl.setAttribute('viewBox', viewBox);
              svgEl.setAttribute('preserveAspectRatio', 'xMinYMin meet');
              svgEl.setAttribute('width', String(width));
              svgEl.setAttribute('height', String(height));
            }
          } catch (e) {
            // ignore
          }
        }

        const dataUrl = svgToDataUrl(svgEl || temp.querySelector('svg'));
        document.body.removeChild(temp);

        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const dpiScale = 2;
            const w = img.naturalWidth || width;
            const h = img.naturalHeight || height;
            const canvas = document.createElement('canvas');
            canvas.width = w * dpiScale;
            canvas.height = h * dpiScale;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = () => resolve(dataUrl);
          img.src = dataUrl;
        });
      };

      await Promise.all(Array.from(mermaidContainers).map(async (container, idx) => {
        const source = container.getAttribute('data-mermaid-source');
        if (!source) return;
        const processed = preprocessMermaidChart(source);
        const chartId = `print-mermaid-${Date.now()}-${idx}`;
        const { svg } = await mermaid.render(chartId, processed.content || source);
        const pngUrl = await renderMermaidToPng(svg);
        const img = document.createElement('img');
        img.src = pngUrl;
        img.style.setProperty('width', '100%', 'important');
        img.style.setProperty('max-width', '100%', 'important');
        img.style.height = 'auto';
        img.style.display = 'block';
        img.style.margin = '0 auto';
        container.style.setProperty('width', '100%', 'important');
        container.style.setProperty('max-width', '100%', 'important');
        container.style.setProperty('text-align', 'center', 'important');
        const wrapper = container.closest('[class*="mermaidBlock"], [class*="mermaidWrapper"], [class*="mermaidContainer"]');
        if (wrapper) {
          wrapper.style.setProperty('width', '100%', 'important');
          wrapper.style.setProperty('max-width', '100%', 'important');
        }
        container.innerHTML = '';
        container.appendChild(img);
      }));
    }

    // 7. 组装并添加到DOM
    printContainer.appendChild(printContent);
    document.head.appendChild(printStyle);
    document.body.appendChild(printContainer);

    // 8. 清理函数
    const cleanup = () => {
      const container = document.getElementById('print-container');
      const style = document.getElementById('print-style');
      if (container) container.remove();
      if (style) style.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    // 9. 延迟打印，让DOM更新
    setTimeout(() => {
      window.print();
      // 备用清理（防止afterprint不触发）
      setTimeout(cleanup, 2000);
    }, 100);
  }, [])
  // isBubbleSidebarVisible 已迁移到 useUIState hook
  
  // ✅ 任务选择相关状态 - 使用 hook
  const {
    FlowName,
    setFlowName,
    availableFlowTypes,
    setAvailableFlowTypes,
    taskSelectionDialog,
    setTaskSelectionDialog,
    originalTaskList,
    setOriginalTaskList,
    userSelection,
    setUserSelection,
    showTaskButton,
    setShowTaskButton,
  } = useTaskSelectionState();

  // isFlowTypeDialogOpen, isCourseManagementDialogOpen, isYuyanProjectDialogOpen 已迁移到 useUIState hook
  
  // ✅ 会话/用户相关状态 - 使用 hook
  const {
    sessionId,
    setSessionId,
    userId,
    setUserId,
    currentUser,
    setCurrentUser,
    isLoggedIn,
    setIsLoggedIn,
    showRegister,
    setShowRegister,
    isSessionReady,
    setIsSessionReady,
    connectionStatus,
    setConnectionStatus,
    hasUserSentMessage,
    setHasUserSentMessage,
    projectId,
    setProjectId,
  } = useSessionState();
  
  // 任务选择确认处理函数 - 重构后的清晰逻辑
  const handleTaskSelectionConfirm = (selectedSoftware, selectedHardware) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error("WebSocket is not connected");
      return;
    }
    
    // 更新用户选择状态
    setUserSelection({
      software: selectedSoftware,
      hardware: selectedHardware
    });
    
    // 根据是否为恢复模式或用户主动修改，选择不同的消息类型
    const isUserDirectUpdate = taskSelectionDialog.isRestore || taskSelectionDialog.isUserModification;
    
    const message = {
      type: isUserDirectUpdate ? "update_task_selection" : "task_selection_response",
      data: {
        selected_software: selectedSoftware,
        selected_hardware: selectedHardware
      }
    };
    
    ws.current.send(JSON.stringify(message));
    console.log("发送任务选择结果:", message);
    
    // 关闭对话框
    setTaskSelectionDialog({ open: false, isRestore: false, isUserModification: false, updateId: null });
  };

  // ✅ Token余额相关状态 - 使用 hook
  const {
    tokenBalance,
    setTokenBalance,
    tokenBalanceK,
    setTokenBalanceK,
    totalTokens,
    setTotalTokens,
    tokenGrant,
    setTokenGrant,
    planName,
    setPlanName,
    adminAdjustedBalance,
    setAdminAdjustedBalance,
  } = useTokenState();

  // ✅ 自评相关状态 - 使用 hook
  const {
    selfAssessmentStatus,
    setSelfAssessmentStatus,
    showSelfAssessmentIcon,
    setShowSelfAssessmentIcon,
    shouldBlinkIcon,
    setShouldBlinkIcon,
  } = useSelfAssessmentState();

  // ✅ 能力画像相关状态 - 使用 hook
  const {
    showCapabilityProfileIcon,
    setShowCapabilityProfileIcon,
    currentStudentId,
    setCurrentStudentId,
    capabilityLevel,
    setCapabilityLevel,
    capabilityLevelDetails,
    setCapabilityLevelDetails,
    shouldBlinkCapabilityIcon,
    setShouldBlinkCapabilityIcon,
  } = useCapabilityProfileState();

  // ✅ 游戏化相关状态 - 使用 hook
  const {
    gameProfile,
    setGameProfile,
    gameConfig,
    setGameConfig,
    feedbackDelta,
    setFeedbackDelta,
    levelUpInfo,
    setLevelUpInfo,
    unlockedAchievements,
    setUnlockedAchievements,
  } = useGameState();

  // ✅ 智能体/微场景相关状态 - 使用 hook
  const {
    agents,
    setAgents,
    currentAgentIndex,
    setCurrentAgentIndex,
    selectedMicroScene,
    setSelectedMicroScene,
    fullscreenAgent,
    setFullscreenAgent,
    selectedTraining,
    setSelectedTraining,
  } = useAgentState();

  // ✅ 模型选择相关状态 - 使用 hook
  const {
    modelType,
    setModelType,
    isAdvancedUnlocked,
    setIsAdvancedUnlocked,
    advancedPassword,
    setAdvancedPassword,
  } = useModelSelectionState();

  // ✅ 学习日志/Diff相关状态 - 使用 hook
  const {
    learningLog,
    setLearningLog,
    currentDiffMessageIndex,
    setCurrentDiffMessageIndex,
    activeTrainingMode,
    setActiveTrainingMode,
  } = useLearningState();

  // ✅ 角色过滤相关状态 - 使用 hook
  const {
    activeRoleFilter,
    setActiveRoleFilter,
    expandedGroups,
    setExpandedGroups,
    resetProgressHistory,
    setResetProgressHistory,
  } = useRoleFilterState();

  // ✅ 密码对话框相关状态 - 使用 hook
  const {
    passwordDialogOpen,
    setPasswordDialogOpen,
    passwordForm,
    setPasswordForm,
  } = usePasswordDialogState();

  // ✅ Claw 实验机接入密码弹窗状态
  const {
    clawPasswordDialogOpen,
    setClawPasswordDialogOpen,
    clawPasswordInput,
    setClawPasswordInput,
    clawPasswordError,
    setClawPasswordError,
    clawPasswordLoading,
    setClawPasswordLoading,
    resetClawPasswordState,
  } = useClawPasswordState();

  // ✅ CC 智能实验员状态
  const {
    ccSelectedDevice,
    setCCSelectedDevice,
    ccMessages,
    setCCMessages,
    ccSessionState,
    setCCSessionState,
    ccStatus,
    setCCStatus,
    ccPendingPermission,
    setCCPendingPermission,
    ccPendingQuestion,
    setCCPendingQuestion,
    ccTaskInfo,
    setCCTaskInfo,
    ccInitInfo,
    setCCInitInfo,
    ccStreamingText,
    setCCStreamingText,
    ccStreamingTextRef,
    ccThinkingText,
    setCCThinkingText,
    ccTokenUsage,
    setCCTokenUsage,
    ccFileTree,
    setCCFileTree,
    ccRoster,
    setCCRoster,
    resetCCState,
  } = useCCState();

  // ✅ YouLaber 独立 tab 的 CC 状态实例（与综合训练 ccma 完全隔离）
  // 复用 useCCState 代码、但拥有独立 state；后端通过 source 字段区分两路。
  const {
    ccSelectedDevice: ytSelectedDevice,
    setCCSelectedDevice: setYTSelectedDevice,
    ccMessages: ytMessages,
    setCCMessages: setYTMessages,
    ccSessionState: ytSessionState,
    setCCSessionState: setYTSessionState,
    ccStatus: ytStatus,
    setCCStatus: setYTStatus,
    ccPendingPermission: ytPendingPermission,
    setCCPendingPermission: setYTPendingPermission,
    ccPendingQuestion: ytPendingQuestion,
    setCCPendingQuestion: setYTPendingQuestion,
    ccTaskInfo: ytTaskInfo,
    setCCTaskInfo: setYTTaskInfo,
    ccInitInfo: ytInitInfo,
    setCCInitInfo: setYTInitInfo,
    ccStreamingText: ytStreamingText,
    setCCStreamingText: setYTStreamingText,
    ccStreamingTextRef: ytStreamingTextRef,
    ccThinkingText: ytThinkingText,
    setCCThinkingText: setYTThinkingText,
    ccTokenUsage: ytTokenUsage,
    setCCTokenUsage: setYTTokenUsage,
    ccFileTree: ytFileTree,
    setCCFileTree: setYTFileTree,
    ccRoster: ytRoster,
    setCCRoster: setYTRoster,
    resetCCState: resetYTState,
  } = useCCState();
  // CC 设备选择弹窗控制
  const [ccDeviceSelectorOpen, setCCDeviceSelectorOpen] = useState(false);
  const [ccDeviceTypeFilter, setCCDeviceTypeFilter] = useState("");
  const [ytDevicePasswordError, setYTDevicePasswordError] = useState("");
  const [ccOverlayOpen, setCCOverlayOpen] = useState(false);
  const [ccOverlayFileId, setCCOverlayFileId] = useState(null);
  const [ccOverlayHasUnread, setCCOverlayHasUnread] = useState(false);
  const ccMessageCountRef = useRef(0);
  const ccLastTaskInfoRef = useRef(null);

  // ✅ 密码处理逻辑 - 使用 hook（需要在后面初始化，因为依赖其他状态）
  // handlePasswordChange, handlePasswordSubmit, handleModelTypeChange, 
  // handleAdvancedPasswordSubmit, handleAdvancedPasswordCancel 在下方定义

  // ✅ 杂项状态 - 使用 hook
  const {
    homeworks,
    setHomeworks,
    wsClient,
    setWsClient,
    welcomeMessage,
    setWelcomeMessage,
    params,
    setParams,
  } = useMiscState();

  // ✅ 附件/文件上传状态 - 使用 hook
  const {
    attachedFiles,
    setAttachedFiles,
    isFileUploading,
    setIsFileUploading,
    conflictDialog,
    setConflictDialog,
  } = useAttachmentState();

  // ✅ 通知系统相关状态
  const {
    notification,
    showNotification,
    unreadNotificationCount,
    setUnreadNotificationCount,
    currentToastNotification,
    setCurrentToastNotification,
    notificationCenterOpen,
    setNotificationCenterOpen,
    notifications,
    setNotifications,
  } = useNotificationState();

  // ✅ 通知系统业务逻辑 Hook
  const {
    handleNewNotification,
    fetchNotifications,
    handleMarkAsRead,
    handleMarkAllAsRead,
  } = useNotificationHandlers({
    currentUser,
    notifications,
    setNotifications,
    unreadNotificationCount,
    setUnreadNotificationCount,
    setCurrentToastNotification,
  });

  useEffect(() => {
    if (ccOverlayOpen) {
      setCCOverlayHasUnread(false);
      ccMessageCountRef.current = ccMessages.length;
      return;
    }

    if (ccMessages.length > ccMessageCountRef.current) {
      setCCOverlayHasUnread(true);
    }
    ccMessageCountRef.current = ccMessages.length;
  }, [ccMessages.length, ccOverlayOpen]);

  useEffect(() => {
    if (!ccTaskInfo || ccLastTaskInfoRef.current === ccTaskInfo) return;
    ccLastTaskInfoRef.current = ccTaskInfo;

    if (!ccOverlayOpen) {
      const deviceName = ccSelectedDevice?.device_name || "YouLabber 实验员";
      const isError = ccTaskInfo.is_error === true;
      setCCOverlayHasUnread(true);
      showNotification(
        isError ? `${deviceName} 任务执行失败，请打开实时对话查看` : `${deviceName} 任务已完成`,
        isError ? "error" : "success"
      );
    }
  }, [ccOverlayOpen, ccSelectedDevice, ccTaskInfo, showNotification]);

  const {
    isArchivesModalOpen,
    setIsArchivesModalOpen,
    isSelfAssessmentDialogOpen,
    setIsSelfAssessmentDialogOpen,
    isCapabilityProfileDialogOpen,
    setIsCapabilityProfileDialogOpen,
    isRecruitEncouragementPanelOpen,
    setIsRecruitEncouragementPanelOpen,
    isTokenUsageLogsOpen,
    setIsTokenUsageLogsOpen,
    showGameCenter,
    setShowGameCenter,
  } = useModalState();

  const {
    messages,
    setMessages,
    isViewingHistory,
    setIsViewingHistory,
    selectedDiffLines,
    setSelectedDiffLines,
    diffComments,
    setDiffComments,
    adoptedExpertOpinions,
    setAdoptedExpertOpinions,
    microSceneBubbles,
    setMicroSceneBubbles,
  } = useMessageState();

  // handleWsCommonMessage/handleWsTaskSelection moved below setIsSessionReady initialization

  const {
    handleDiffLineClick,
    handleCommentChange,
    handleOpinionAdoption,
  } = useMessageHandlers({
    setSelectedDiffLines,
    setDiffComments,
    setAdoptedExpertOpinions,
  });
  // unlockedAchievements 已迁移到 useGameState hook

  // 游戏化动画触发函数
  // 收到新delta时立即中断旧动画，切换到新内容（色块+文字全部刷新）
  const triggerInstantFeedback = useCallback((delta) => {
    console.log('🎮 [触发] 即时反馈动画:', delta);
    // 先置null强制React识别变化，再在下一帧设置新delta，确保useEffect重新触发
    setFeedbackDelta(null);
    requestAnimationFrame(() => {
      setFeedbackDelta(delta);
    });
  }, []);

  const triggerLevelUpAnimation = (newLevel) => {
    console.log('🎮 [触发] 升级动画:', newLevel);
    console.log('🎮 [触发] 设置levelUpInfo状态');
    setLevelUpInfo({ newLevel });
  };

  const triggerAchievementUnlocked = (achievements) => {
    console.log('🎮 [触发] 成就解锁动画:', achievements);
    console.log('🎮 [触发] 设置unlockedAchievements状态');
    setUnlockedAchievements(achievements);
  };

  const handleWsGameProfileUpdate = useWsGameProfileHandler({
    setGameProfile,
    triggerInstantFeedback,
    triggerLevelUpAnimation,
    triggerAchievementUnlocked,
  });

  // 手术 3：今日你叙事卡片（拉式入口，不打扰）
  const [todayYouLiveCards, setTodayYouLiveCards] = useState([]);
  const [hasUnreadTodayYou, setHasUnreadTodayYou] = useState(false);
  const handleWsTodayYouCard = useWsTodayYouHandler({
    setTodayYouCards: setTodayYouLiveCards,
    setHasUnreadTodayYou,
  });
  const onMarkTodayYouRead = useCallback(() => {
    setHasUnreadTodayYou(false);
  }, []);
  
  // ✅ 研究中心状态 - 使用 hook
  const {
    researchCenterOpen,
    setResearchCenterOpen,
    researchTasks,
    setResearchTasks,
    unreadResearchCount,
    setUnreadResearchCount,
    achievementWallData,
    setAchievementWallData,
    profileHistoryData,
    setProfileHistoryData,
    leaderboardData,
    setLeaderboardData,
    skillHistoryData,
    setSkillHistoryData,
    growthMapData,
    setGrowthMapData,
  } = useResearchCenterState();

  // isSystemResetting 已迁移到 useUIState hook
  
  // 🔧 新增：系统清理管理器
  const systemCleanupManager = useRef({
    timers: new Set(),
    intervals: new Set(),
    animationFrames: new Set(),
    eventListeners: new Map(),
    
    addTimer(id) { 
      this.timers.add(id);
      return id;
    },
    
    addInterval(id) { 
      this.intervals.add(id);
      return id;
    },
    
    addAnimationFrame(id) { 
      this.animationFrames.add(id);
      return id;
    },
    
    addEventListener(element, type, listener, options) {
      const key = `${element}_${type}_${Date.now()}`;
      this.eventListeners.set(key, { element, type, listener, options });
      element.addEventListener(type, listener, options);
      return key;
    },
    
    cleanup() {
      // 清理所有定时器
      this.timers.forEach(id => clearTimeout(id));
      this.intervals.forEach(id => clearInterval(id));
      this.animationFrames.forEach(id => cancelAnimationFrame(id));
      
      // 清理事件监听器
      this.eventListeners.forEach(({ element, type, listener, options }) => {
        try {
          element.removeEventListener(type, listener, options);
        } catch (error) {
          console.warn('清理事件监听器失败:', error);
        }
      });
      
      // 清空集合
      this.timers.clear();
      this.intervals.clear();
      this.animationFrames.clear();
      this.eventListeners.clear();
      
      console.log('系统清理管理器：所有资源已清理');
    }
  }).current;

  // 🔧 新增：完全重置系统的核心函数
  const performCompleteSystemReset = useCallback(async () => {
    console.log('🚨🚨🚨 [系统重置] 开始执行完全系统重置...');
    console.log('🚨 [系统重置] 调用堆栈:', new Error().stack);
    console.log('🚨 [系统重置] 重置前状态:', { projectState, messages: messages.length, files: Object.keys(files).length });
    
    // 阶段1：设置重置状态，暂停用户交互
    setIsSystemResetting(true);
    setIsMessageInputEnabled(false);
    setIsLoading(true);
    
    try {
      // 阶段2：清理所有异步操作和副作用
      systemCleanupManager.cleanup();
      
      // 阶段3：保存WebSocket消息处理器但不暂停
      // 注意：不暂停WebSocket消息处理，以确保后端发送的进度更新能被接收
      const originalOnMessage = ws.current?.onmessage;
      
      // 阶段4：分批重置状态，避免React批处理问题
      await new Promise(resolve => {
        // 第一批：核心数据状态
        setMessages([]);
        setFiles({});
        setActiveFile(null);
        setFileNameCounts({});
        
        systemCleanupManager.addTimer(setTimeout(() => {
          // 第二批：交互状态
          setSelectedRoles([]);
          setMicroSceneBubbles([]);
          setLearningLog([]);
          setSelectedDiffLines({});
          setCurrentDiffMessageIndex(null);
          setDiffComments({});
          
          systemCleanupManager.addTimer(setTimeout(() => {
            // 第三批：专家和意见状态
            setAdoptedExpertOpinions({});
            setSelectedExpert(null);
            setIsInExpertMode(false);
            setCurrentExpertCommand('');
            
            systemCleanupManager.addTimer(setTimeout(() => {
              // 第四批：UI状态
              setShouldFlash(false);
              setSelectedFileLines({});
              setActiveFileContent(null);
              
              resolve();
            }, 50));
          }, 50));
        }, 50));
      });
      
      // 阶段5：重置项目状态和进度条历史
      // 手动重置项目状态，避免clearProjectStateAndHistory的重复触发
      setProjectState("");
      
      // 清空sessionStorage中保存的历史记录
      try {
        sessionStorage.removeItem('projectStateHistory');
      } catch (error) {
        console.error('清空状态历史出错:', error);
      }
      
      // 🔧 触发进度条历史重置
      setResetProgressHistory(true);
      systemCleanupManager.addTimer(setTimeout(() => {
        setResetProgressHistory(false);
      }, 100));
      
      // 阶段6：等待一个短暂的延迟确保所有状态更新完成
      await new Promise(resolve => {
        systemCleanupManager.addTimer(setTimeout(resolve, 100));
      });
      
      // 阶段7：确保WebSocket消息处理正常（现在不需要恢复了）
      // WebSocket消息处理器保持不变，确保能接收后端的进度更新
      
      console.log('✅ 完全系统重置完成');
      console.log('重置后状态:', { projectState, messages: messages.length, files: Object.keys(files).length });
      console.log('进度条现在应该准备好接收新的状态更新');
      return true;
      
    } catch (error) {
      console.error('系统重置过程中发生错误:', error);
      return false;
    } finally {
      // 阶段8：恢复系统交互（无论成功与否）
      setIsSystemResetting(false);
      setIsLoading(false);
      setIsMessageInputEnabled(true);
    }
  }, [systemCleanupManager]);

  // 🔧 新增：增强版回退函数，复用完全重置逻辑
  const performEnhancedBackToPreviousPhase = useCallback(async (targetPhase) => {
    if (!targetPhase || targetPhase.trim() === "") {
      return false;
    }
    
    console.log(`🔄 开始回退到阶段: ${targetPhase}`);
    
    // 🔧 设置恢复模式标志，禁止自动文件切换
    setIsRestoringArchive(true);
    isRestoringArchiveRef.current = true;
    console.log("🔄 阶段回退：设置恢复模式 isRestoringArchive = true (state + ref)");
    
    // 执行完全系统重置
    const resetSuccess = await performCompleteSystemReset();
    if (!resetSuccess) {
      showNotification('系统重置失败，无法执行回退', 'error');
      // 重置失败，清除恢复模式标志
      setIsRestoringArchive(false);
      isRestoringArchiveRef.current = false;
      return false;
    }
    
    // 重置成功后，发送回退请求
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: "back_to_previous_phase",
        previous_phase: targetPhase.trim(),
      }));
      
      // 记录用户行为
      logAction("ENHANCED_BACK_TO_PREVIOUS_PHASE", {
        previousPhase: targetPhase.trim(),
      });
      
      showNotification(`正在回退到${targetPhase}阶段...`, 'info');
      return true;
    } else {
      showNotification('WebSocket连接异常，无法发送回退请求', 'error');
      // 连接异常，清除恢复模式标志
      setIsRestoringArchive(false);
      isRestoringArchiveRef.current = false;
      return false;
    }
  }, [performCompleteSystemReset]);

  // 🔧 新增：管理定时器的包装函数
  const managedSetTimeout = useCallback((callback, delay) => {
    const id = setTimeout(callback, delay);
    systemCleanupManager.addTimer(id);
    return id;
  }, [systemCleanupManager]);

  const managedSetInterval = useCallback((callback, delay) => {
    const id = setInterval(callback, delay);
    systemCleanupManager.addInterval(id);
    return id;
  }, [systemCleanupManager]);

  const managedRequestAnimationFrame = useCallback((callback) => {
    const id = requestAnimationFrame(callback);
    systemCleanupManager.addAnimationFrame(id);
    return id;
  }, [systemCleanupManager]);

  // 🔧 新增：测试完全重置功能
  const testCompleteSystemReset = useCallback(async () => {
    console.log('开始测试完全系统重置功能...');
    const success = await performCompleteSystemReset();
    if (success) {
      showNotification('完全系统重置测试成功！', 'success');
      console.log('完全系统重置测试成功');
    } else {
      showNotification('完全系统重置测试失败', 'error');
      console.log('完全系统重置测试失败');
    }
  }, [performCompleteSystemReset]);

  // 🎭 检查数字人服务可用性（定义在 useEffect 之前）
  const checkExpertService = useCallback(async () => {
    let timeoutId = null;
    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 3000); // 3秒超时
      
      const response = await fetch(`${EXPERT_SERVICE_URL}/health`, {
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // ✅ 在所有异步操作完成后才清除定时器
      clearTimeout(timeoutId);
      timeoutId = null;
      
      const isAvailable = data.status === 'healthy';
      
      // 立即同步更新 ref（确保后续检查能立即获取到正确值）
      isExpertServiceAvailableRef.current = isAvailable;
      setIsExpertServiceAvailable(isAvailable);
      
      console.log(`🎭 数字人服务状态: ${isAvailable ? '✅ 可用' : '❌ 不可用'}`);
    } catch (error) {
      // 清理超时定时器
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // 立即同步更新 ref
      isExpertServiceAvailableRef.current = false;
      setIsExpertServiceAvailable(false);
      
      // 只在非超时错误时打印警告
      if (error.name !== 'AbortError') {
        console.log('⚠️ 数字人服务检查失败:', error.message);
      } else {
        console.log('⚠️ 数字人服务检查超时');
      }
    }
  }, []); // 空依赖数组，确保函数不会重新创建

  // 🔧 新增：组件卸载时清理所有资源
  useEffect(() => {
    return () => {
      console.log('App组件卸载，清理所有系统资源');
      systemCleanupManager.cleanup();
    };
  }, [systemCleanupManager]);

  // 🎭 组件挂载时检查数字人服务可用性
  useEffect(() => {
    // 防止 StrictMode 导致的重复调用
    if (hasCheckedExpertServiceRef.current) {
      console.log('🎭 数字人服务已检查过，跳过重复调用');
      return;
    }
    
    hasCheckedExpertServiceRef.current = true;
    checkExpertService();
    // 仅在组件挂载时执行一次，不依赖checkExpertService的变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🎯 训练广场智能体列表：课程相关常显 + 其他公开/官方默认折叠。
  const [plazaAgents, setPlazaAgents] = useState([]);
  const [showOtherPlazaAgents, setShowOtherPlazaAgents] = useState(false);
  // 学习路径关卡「查看作业 / 完整评价」直达；菜单入口打开时清空
  const [focusHomeworkTaskId, setFocusHomeworkTaskId] = useState(null);
  const [focusHomeworkMode, setFocusHomeworkMode] = useState(null); // null | 'homework' | 'evaluation' | 'growth'
  const [focusHomeworkCourseId, setFocusHomeworkCourseId] = useState(null);
  const getFlowResourceBinding = useCallback((flowName) => {
    const flow = availableFlowTypes.find(f => f.name === flowName);
    return {
      trainingProcessAssetId: flow?.marketAssetId || flow?.trainingProcessAssetId || '',
      trainingProcessReleaseId: flow?.marketReleaseId || flow?.trainingProcessReleaseId || ''
    };
  }, [availableFlowTypes]);

  const resolveFlowDisplayName = useCallback((flowName) => {
    if (!flowName) return "";
    const flow = availableFlowTypes.find((f) => f.name === flowName);
    if (!flow) return flowName;
    const isZh = !i18n.language || i18n.language === 'zh-CN';
    return (!isZh && flow.displayNameEN) ? flow.displayNameEN : (flow.displayName || flowName);
  }, [availableFlowTypes, i18n.language]);

  const rememberLaunchedFlow = useCallback((flowName, displayName) => {
    if (!flowName) return;
    setAvailableFlowTypes((prev) => {
      const idx = prev.findIndex((f) => f.name === flowName);
      const title = String(displayName || '').trim();
      if (idx >= 0) {
        if (!title || prev[idx].displayName) return prev;
        const next = [...prev];
        next[idx] = { ...prev[idx], displayName: title };
        return next;
      }
      return [...prev, { name: flowName, displayName: title || flowName }];
    });
  }, []);

  useEffect(() => {
    const fetchPlazaAgents = async () => {
      const studentId = currentUser?.unified_user_id || currentUser?.studentID || currentUser?.student_id || currentUser?.username || currentUser?.employeeNumber;
      if (!studentId) {
        setPlazaAgents([]);
        return;
      }
      const params = new URLSearchParams({
        scope: 'enrolled_courses',
        student_id: String(studentId),
      });
      const url = `${EXPERT_SERVICE_URL}/v1/expert/agents?${params.toString()}`;
      try {
        const response = await fetch(url);
        const data = await response.json();
        if (!response.ok) {
          console.warn('[训练广场] API 异常:', response.status, url, data);
          return;
        }
        const agents = data.agents || [];
        console.log('[训练广场] 已加入课程关联智能体:', agents.length, '个');
        setPlazaAgents(agents);
      } catch (err) {
        console.warn('[训练广场] 获取智能体列表失败:', err.message, 'url=', url);
        setPlazaAgents([]);
      }
    };
    fetchPlazaAgents();
  }, [currentUser]);

  // 添加useEffect钩子，从后端API获取流程列表
  useEffect(() => {
    const fetchFlowTypes = async () => {
      try {
        const params = new URLSearchParams();
        const studentId = currentUser?.unified_user_id || currentUser?.studentID || currentUser?.student_id || currentUser?.username || currentUser?.employeeNumber;
        if (studentId) {
          params.set('student_id', String(studentId));
        }
        const query = params.toString();
        const response = await fetch(`${EDUGATE_API_URL}/api/training-process/list${query ? `?${query}` : ''}`);
        const data = await response.json();
        if (data.code === 0 && Array.isArray(data.data) && data.data.length > 0) {
          //console.log("从API获取到流程列表:", data.data);
          
          // 根据用户权限过滤流程列表
          const filteredFlowTypes = data.data.filter(flow => {
            // 如果流程的userPrefix为空，所有用户都可以看到
            if (!flow.userPrefix) {
              return true;
            }
            
            // 如果用户未登录，只显示userPrefix为空的流程
            if (!currentUser) {
              return false;
            }
            
            // 北邮统一认证用户：如果流程有userPrefix限制，不显示
            if (currentUser.isCasUser) {
              return false;
            }
            
            // 校园用户/访客用户：检查username前缀是否匹配
            if (currentUser.username && currentUser.username.startsWith(flow.userPrefix)) {
              return true;
            }
            
            return false;
          });
          
          //console.log("过滤后的流程列表:", filteredFlowTypes);
          setAvailableFlowTypes((prev) => {
            const byName = new Map(filteredFlowTypes.map((flow) => [flow.name, flow]));
            prev.forEach((flow) => {
              if (flow?.name && !byName.has(flow.name)) {
                byName.set(flow.name, flow);
              }
            });
            return Array.from(byName.values());
          });
          
          // 确保"青铜标准综合流程"存在于可用流程中
          const bronzeStandardFlow = filteredFlowTypes.find(flow => flow.name === 'new_user_first_experience');
          if (bronzeStandardFlow) {
            console.log("确认青铜标准综合流程可用:", bronzeStandardFlow.displayName);
          } else {
            console.warn("青铜标准综合流程不在当前用户的可用流程列表中");
          }
        } else {
          console.error('获取流程列表失败或返回空列表:', data.message);
        }
      } catch (error) {
        console.error('获取流程列表错误:', error);
      }
    };
    
    fetchFlowTypes();
  }, [currentUser]); // 添加currentUser作为依赖，当用户状态变化时重新获取流程列表

  // // 添加初始化用户状态的useEffect
  // useEffect(() => {
  //   // 只在CAS登录回调的情况下才自动恢复用户状态
  //   const savedUserInfo = localStorage.getItem('userInfo');
  //   if (savedUserInfo) {
  //     try {
  //       const userInfo = JSON.parse(savedUserInfo);
        
  //       // 只有当用户信息包含openFlowDialog标志时才自动登录
  //       // 这个标志只在CAS回调成功后设置
  //       if (userInfo.openFlowDialog) {
  //         console.log("检测到CAS登录回调，自动恢复用户信息:", userInfo);
  //         setCurrentUser(userInfo);
  //         setIsLoggedIn(true);
  //         setIsFlowTypeDialogOpen(true);
          
  //         // 清除标志，避免重复显示
  //         const updatedUserInfo = { ...userInfo };
  //         delete updatedUserInfo.openFlowDialog;
  //         localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
  //         setCurrentUser(updatedUserInfo);
  //       } else {
  //         // 如果没有openFlowDialog标志，清除localStorage中的用户信息
  //         // 要求用户重新登录
  //         console.log("清除过期的用户信息，要求重新登录");
  //         localStorage.removeItem('userInfo');
  //       }
  //     } catch (error) {
  //       console.error("解析localStorage中的用户信息失败:", error);
  //       localStorage.removeItem('userInfo');
  //     }
  //   }
  // }, []);

  // showHomeworkDialog, isFromMenu, isListArchivesFromMenu 已迁移到 useUIState hook
  // homeworks 已迁移到 useMiscState hook
  
  // ✅ 输入相关状态 - 使用 hook
  const {
    inputValue,
    setInputValue,
    humanInput,
    setHumanInput,
    selectedRoles,
    setSelectedRoles,
    lastMessageType,
    setLastMessageType,
    projectState,
    setProjectState,
  } = useInputState();
  
  const inputRef = useRef(null);
  
  // ✅ 文件管理相关状态 - 使用 hook
  const {
    files,
    setFiles,
    activeFile,
    setActiveFile,
    fileNameCounts,
    setFileNameCounts,
    selectedFileLines,
    setSelectedFileLines,
    activeFileContent,
    setActiveFileContent,
    fileFirstEntryMap,
    setFileFirstEntryMap,
    fileContentExpanded,
    setFileContentExpanded,
    panelWidth,
    setPanelWidth,
  } = useFileManagementState();

  // activeTrainingMode 已迁移到 useLearningState hook
  const parseMicroSceneData = useCallback((context) => {
    try {
      let scenes;
      if (typeof context === "string") {
        // 使用 new Function() 来安全地、宽容地解析类JSON字符串
        // 这是比 eval() 更安全的方法
        const parser = new Function(`return ${context};`);
        scenes = parser();
      } else if (typeof context === "object") {
        scenes = context;
      } else {
        throw new Error("Unexpected data type for Micro_scene");
      }

      // 确保返回的始终是数组
      if (!Array.isArray(scenes)) {
        scenes = [scenes];
      }

      return scenes;
    } catch (error) {
      console.error("Error parsing Micro_scene data with fallback parser:", error);
      console.error("Raw data that failed parsing:", context);
      return []; // 出错时返回空数组，避免应用崩溃
    }
  }, []);

  // params 已迁移到 useMiscState hook

  const paramDescriptions = {
    investment: "投资于AI公司的金额（美）",
    n_round: "模拟次数",
    code_review: "是否进行代码审查",
    run_tests: "是否运行测试",
    implement: "启用或禁用代码实现",
    project_name: '唯一的项目名称，例如"game_2048"',
    inc: "增量模式。使用它与现有仓库配合",
    project_path: "指定项目路径",
    reqa_file: "指定质量保证代码文件",
    max_auto_summarize_code: "最大自动代码摘要次数",
    recover_path: "指定项目恢复路径",
  }
  // anchorEl, isModalOpen 已迁移到 useUIState hook
  // isMenuOpen 定义移到 useUIState hook 调用之后
  // files 已迁移到 useFileManagementState hook
  
  // 🔍 调试：监控files状态变化
  useEffect(() => {

  }, [files]);
  // activeFile, fileNameCounts 已迁移到 useFileManagementState hook
  // selectedRoles, humanInput 已迁移到 useInputState hook
  const ws = useRef(null)
  const wsMessageDispatchRef = useRef(null)
  const filesRef = useRef(files) // 🔧 新增：files状态的ref引用
  
  // 🔧 新增：保持filesRef与files状态同步
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  const chatPanelRef = useRef(null)
  // lastMessageType, projectState 已迁移到 useInputState hook
  // tabsWidth 已迁移到 useUIState hook
  const dividerRef = useRef(null)
  // currentDiffMessageIndex, learningLog 已迁移到 useLearningState hook
  // sessionId, userId, isLoggedIn, showRegister 已迁移到 useSessionState hook
  // ✅ 专家相关状态 - 使用 hook
  const {
    experts,
    setExperts,
    selectedExpert,
    setSelectedExpert,
    isInExpertMode,
    setIsInExpertMode,
    currentExpertCommand,
    setCurrentExpertCommand,
    lastExpertSummaryCheck,
    setLastExpertSummaryCheck,
    isExpertDropdownOpen,
    setIsExpertDropdownOpen,
    expertSearchTerm,
    setExpertSearchTerm,
    selectedExpertCategory,
    setSelectedExpertCategory,
    isExpertDialogReady,
    setIsExpertDialogReady,
    expertDialogContent,
    setExpertDialogContent,
    pendingExperts,
    setPendingExperts,
    expertFlashMap,
    setExpertFlashMap,
    expertUnreadMap,
    setExpertUnreadMap,
  } = useExpertState();

  const lastExpertSummaryCheckRef = useRef({ command: '', timestamp: 0 })
  // 文档分析助手：仅在 ExpertService 内部流转的 conversation_id（多轮追问复用）
  const docAnalystConvIdRef = useRef(null)
  // 文档分析助手：记录各 conversation_id 本 tab 内是否已经展示过"不共享上下文"提醒
  // （应对后端 SessionManager 跨刷新恢复历史时 isFirstReply 判定失效的情况）
  const docAnalystNoticeShownRef = useRef({})

  // ✅ UI 状态 - 使用 hook
  const {
    isLoading,
    setIsLoading,
    isWaitingResponse,
    setIsWaitingResponse,
    isMessageInputEnabled,
    setIsMessageInputEnabled,
    isProcessing,
    setIsProcessing,
    isBubbleSidebarVisible,
    setIsBubbleSidebarVisible,
    isModalOpen,
    setIsModalOpen,
    isDetailModalOpen,
    setIsDetailModalOpen,
    error,
    setError,
    shouldFlash,
    setShouldFlash,
    isFlowTypeDialogOpen,
    setIsFlowTypeDialogOpen,
    isCourseManagementDialogOpen,
    setIsCourseManagementDialogOpen,
    isYuyanProjectDialogOpen,
    setIsYuyanProjectDialogOpen,
    showHomeworkDialog,
    setShowHomeworkDialog,
    isEditNameModalOpen,
    setIsEditNameModalOpen,
    showAdvancedPasswordDialog,
    setShowAdvancedPasswordDialog,
    digitalHumanOpen,
    setDigitalHumanOpen,
    isSimDialogOpen,
    setIsSimDialogOpen,
    fileExpertDigitalHumanOpen,
    setFileExpertDigitalHumanOpen,
    anchorEl,
    setAnchorEl,
    isFromMenu,
    setIsFromMenu,
    isListArchivesFromMenu,
    setIsListArchivesFromMenu,
    isSystemResetting,
    setIsSystemResetting,
    isAISecretaryVisible,
    setAISecretaryVisible,
    isChatPanelVisible,
    setIsChatPanelVisible,
    chatPanelWidth,
    setChatPanelWidth,
    messagePanelWidth,
    setMessagePanelWidth,
    tabsWidth,
    setTabsWidth,
    isMainDragging,
    setIsMainDragging,
  } = useUIState();

  // isMenuOpen 依赖 anchorEl，必须在 useUIState 之后定义
  const isMenuOpen = Boolean(anchorEl);

  // selectedMicroScene, agents, currentAgentIndex 已迁移到 useAgentState hook
  // messagePanelWidth 已迁移到 useUIState hook
  const messagePanelRef = useRef(null)
  // ✅ 存档相关状态 - 使用 hook
  const {
    archivesList,
    setArchivesList,
    selectedArchive,
    setSelectedArchive,
    archiveId,
    setArchiveId,
    editingArchive,
    setEditingArchive,
    newProjectName,
    setNewProjectName,
    isEditing,
    setIsEditing,
    isRestoringArchive,
    setIsRestoringArchive,
  } = useArchiveState();
  const [pendingArchiveRecoveryVersion, setPendingArchiveRecoveryVersion] = useState(0);

  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  // isAISecretaryVisible 已迁移到 useUIState hook
  // wsClient 已迁移到 useMiscState hook
  // panelWidth 已迁移到 useFileManagementState hook
  // isEditNameModalOpen, isProcessing, showAdvancedPasswordDialog 已迁移到 useUIState hook
  // modelType, isAdvancedUnlocked, advancedPassword 已迁移到 useModelSelectionState hook
  // isRestoringArchive 已迁移到 useArchiveState hook
  const isRestoringArchiveRef = useRef(false); // 🔧 使用 ref 同步追踪恢复模式，避免异步状态问题
  // isSessionReady 已迁移到 useSessionState hook

  const handleWsCommonMessage = useWsCommonMessageHandlers({
    setIsSessionReady,
    setError,
    setArchiveId,
    setMessages,
    setClawPasswordDialogOpen,
    setClawPasswordError,
    setClawPasswordLoading,
    setCCDeviceSelectorOpen,
    setCCSelectedDevice,
    setYTSelectedDevice,
    setYTSessionState,
    setYTDevicePasswordError,
    showNotification,
    setArchivesList,
  });

  const handleWsTaskSelection = useWsTaskSelectionHandler({
    setOriginalTaskList,
    setUserSelection,
    setShowTaskButton,
    setTaskSelectionDialog,
    taskSelectionDialog,
  });

  const handleWsEditorEvents = useWsEditorEvents();

  const handleWsRoleStatus = useWsRoleStatusHandler({
    setSelectedRoles,
    isRestoringArchive,
    setIsRestoringArchive,
    isRestoringArchiveRef,
    setIsMessageInputEnabled,
  });

  // handleWsArchiveMessage moved below handleListArchives initialization
  // activeRoleFilter, expandedGroups, resetProgressHistory 已迁移到 useRoleFilterState hook
  // 专家对话框优化相关状态 - 已迁移到 useExpertState hook

  // ✅ 绘图助手相关状态 - 使用 hook
  const {
    diagramXML,
    setDiagramXML,
    diagramMessages,
    setDiagramMessages,
    isDiagramStreaming,
    setIsDiagramStreaming,
    diagramFileId,
    setDiagramFileId,
    isDrawingFullscreen,
    setIsDrawingFullscreen,
  } = useDiagramState();

  // ✅ 研讨厅相关状态 - 使用 hook
  const {
    isDiscussOpen,
    setIsDiscussOpen,
    discussSessionId,
    setDiscussSessionId,
    discussPhase,
    setDiscussPhase,
    discussPanelists,
    setDiscussPanelists,
    discussMessages,
    setDiscussMessages,
    discussTopicSummary,
    setDiscussTopicSummary,
    isDiscussWaiting,
    setIsDiscussWaiting,
    discussStatus,
    setDiscussStatus,
    discussWsRef,
    resetDiscussState,
  } = useDiscussState();

  // ✅ 群聊协作相关状态 - 使用 hook（加 gc 前缀避免与 agents/messages 等冲突）
  const {
    isGroupChatOpen,
    setIsGroupChatOpen,
    roomId: gcRoomId,
    setRoomId: setGcRoomId,
    projectName: gcProjectName,
    setProjectName: setGcProjectName,
    currentPhase: gcCurrentPhase,
    setCurrentPhase: setGcCurrentPhase,
    phaseLabel: gcPhaseLabel,
    setPhaseLabel: setGcPhaseLabel,
    phases: gcPhases,
    setPhases: setGcPhases,
    members: gcMembers,
    setMembers: setGcMembers,
    agents: gcAgents,
    setAgents: setGcAgents,
    messages: gcMessages,
    setMessages: setGcMessages,
    streamingContent: gcStreamingContent,
    setStreamingContent: setGcStreamingContent,
    roundsInPhase: gcRoundsInPhase,
    setRoundsInPhase: setGcRoundsInPhase,
    maxRounds: gcMaxRounds,
    setMaxRounds: setGcMaxRounds,
    status: groupChatStatus,
    setStatus: setGroupChatStatus,
    isWaiting: groupChatWaiting,
    setIsWaiting: setGroupChatWaiting,
    templateId,
    setTemplateId,
    myUserId: gcMyUserId,
    setMyUserId: setGcMyUserId,
    groupChatWsRef,
    resetGroupChatState,
  } = useGroupChatState();
  const [gcArchiveBreadcrumb, setGcArchiveBreadcrumb] = useState(null);
  const [portalRefreshKey, setPortalRefreshKey] = useState(0);
  // 仅课程入口「开始训练」且关卡配置了完成时限时才有值；未配置时保持 null，三个训练界面不挂载倒计时 DOM。
  const [trainingTimer, setTrainingTimer] = useState(null);
  const [portalFeatureFlags, setPortalFeatureFlags] = useState({
    tabs: {
      ccma: true,
      plaza: true,
      groupchat: true,
      discuss: true,
      youlaber: true,
      drawing: true,
    },
  });
  const portalTabs = portalFeatureFlags?.tabs || {};
  const portalTabsRef = useRef(portalTabs);
  // 从「我的课程」点「开始训练」进入的目标视图：允许进入已隐藏的顶栏 Tab
  const portalLaunchBypassRef = useRef(null);
  // 本会话从课程进入过的训练页：仅这些隐藏 Tab 会在工作区顶栏出现
  const [enteredWorkspaceTabs, setEnteredWorkspaceTabs] = useState({});
  const markWorkspaceTabEntered = useCallback((key) => {
    if (!key || key === 'portal') return;
    setEnteredWorkspaceTabs((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);
  // 作业启动时 WS 可能尚未就绪，等到 session_ready 后再 set_flow_type
  const pendingFlowSelectRef = useRef(null);
  useEffect(() => {
    portalTabsRef.current = portalTabs;
  }, [portalTabs]);

  // ============ 统一的 5-Tab 视图切换器 ============
  // activeView 派生自 activeTrainingMode + 三个浮层 boolean，保证同一时刻有且仅有一个 tab 高亮。
  // 优先级：浮层 > 互斥模式（浮层是后开的"接管态"）
  const activeView = isDrawingFullscreen
    ? 'drawing'
    : isDiscussOpen
      ? 'discuss'
      : isGroupChatOpen
        ? 'groupchat'
        : (activeTrainingMode === 'portal'
            ? 'portal'
            : (activeTrainingMode === 'plaza'
            ? 'plaza'
            : (activeTrainingMode === 'youlaber' ? 'youlaber' : 'ccma')));

  // GVA 隐藏只约束未进入的入口；从作业/关卡进入过的训练 Tab 在本会话内保持显示（含回到「我的课程」）。
  const isPortalTabVisible = (key) =>
    portalTabs[key] !== false || Boolean(enteredWorkspaceTabs[key]);

  // 综合训练头部专属控件：必须以实际入口 activeView 判定（浮层入口不会改写 activeTrainingMode）
  const isComprehensiveView = activeView === 'ccma';
  // 未开始训练时按视图按需建连，避免大厅占用 CCMA 会话；训练开始后切走其它页也保活，回来可继续对话
  const ccmaViewActive = activeView === 'ccma' || activeView === 'youlaber';
  const ccmaSessionInProgress = Boolean(hasUserSentMessage || archiveId);
  const ccmaWsEnabled = ccmaViewActive || ccmaSessionInProgress;
  const [groupChatConnStatus, setGroupChatConnStatus] = useState('idle');
  const headerConnectionStatus = activeView === 'groupchat'
    ? groupChatConnStatus
    : (ccmaViewActive ? connectionStatus : 'idle');

  // 仅当组织定制了 logoUrl（左侧主 Logo 被替换）时，面包屑首项用邮百工图替代文字
  const hasCustomOrganizationBranding = Boolean(
    currentUser?.branding && String(currentUser.branding.logoUrl || '').trim()
  );

  // 切换 view 时统一关掉所有浮层、设主模式；保留各 view 内部 state（messages/sessionId 等不动）。
  // 训练进行中 CCMA WS 保活，切走再回来直接续聊；仅未开始训练时才按视图拆连。
  const switchView = useCallback((next) => {
    // 单智能体对话是盖在内容区上的浮层；切走专项训练时必须关掉，
    // 否则顶栏已到「我的课程」但屏幕仍停在对话，点返回才会露出课程页。
    if (next !== 'plaza') {
      setFullscreenAgent(null);
      setSelectedTraining(null);
    }
    if (next === activeView) {
      setCCOverlayOpen(false);
      return;
    }
    setCCOverlayOpen(false);
    if (next !== 'groupchat') {
      setGcArchiveBreadcrumb(null);
    }
    markWorkspaceTabEntered(next);
    if (portalLaunchBypassRef.current && next !== portalLaunchBypassRef.current) {
      portalLaunchBypassRef.current = null;
    }
    if (next === 'drawing') {
      setIsDiscussOpen(false);
      setIsGroupChatOpen(false);
      setIsDrawingFullscreen(true);
    } else if (next === 'discuss') {
      setIsDrawingFullscreen(false);
      setIsGroupChatOpen(false);
      setIsDiscussOpen(true);
    } else if (next === 'groupchat') {
      setIsDrawingFullscreen(false);
      setIsDiscussOpen(false);
      setIsGroupChatOpen(true);
    } else if (next === 'portal') {
      setIsDrawingFullscreen(false);
      setIsDiscussOpen(false);
      setIsGroupChatOpen(false);
      setActiveTrainingMode('portal');
    } else if (next === 'plaza') {
      setIsDrawingFullscreen(false);
      setIsDiscussOpen(false);
      setIsGroupChatOpen(false);
      setActiveTrainingMode('plaza');
    } else if (next === 'youlaber') {
      setIsDrawingFullscreen(false);
      setIsDiscussOpen(false);
      setIsGroupChatOpen(false);
      setActiveTrainingMode('youlaber');
    } else { // 'ccma' 默认主页
      setIsDrawingFullscreen(false);
      setIsDiscussOpen(false);
      setIsGroupChatOpen(false);
      setActiveTrainingMode('ccma');
    }
  }, [activeView, markWorkspaceTabEntered, setFullscreenAgent, setSelectedTraining, setIsDrawingFullscreen, setIsDiscussOpen, setIsGroupChatOpen, setActiveTrainingMode]);

  const appBreadcrumbItems = useMemo(() => {
    const modeLabelMap = {
      portal: t('mode_course_portal'),
      ccma: t('mode_ccma'),
      plaza: t('mode_plaza'),
      discuss: t('mode_discuss'),
      groupchat: t('mode_groupchat'),
      youlaber: t('mode_youlaber'),
      drawing: t('mode_drawing'),
    };
    const getFlowDisplayName = () => resolveFlowDisplayName(FlowName);
    const stripCreator = (value) => String(value || "").trim().replace(/（创建者：.+）$/, "");
    let detail = "";
    let extraDetail = "";

    if (activeView === 'ccma') {
      detail = ccOverlayOpen
        ? (ccSelectedDevice?.device_name || "YouLabber 实时工作区")
        : (getFlowDisplayName() || projectState || "");
    } else if (activeView === 'plaza') {
      detail = selectedTraining?.name || getFlowDisplayName();
    } else if (activeView === 'groupchat') {
      if (gcArchiveBreadcrumb?.projectName) {
        detail = "历史记录";
        extraDetail = stripCreator(gcArchiveBreadcrumb.projectName);
      } else {
        detail = gcProjectName ? stripCreator(gcProjectName) : "群聊首页";
      }
    } else if (activeView === 'youlaber') {
      detail = ytSelectedDevice?.device_name || "";
    }

    const rootCrumb = hasCustomOrganizationBranding
      ? {
          type: "image",
          src: "/avatars/mozi.png",
          alt: "邮百工",
          title: "邮百工",
        }
      : "邮百工";

    return [rootCrumb, modeLabelMap[activeView] || modeLabelMap.ccma, detail, extraDetail];
  }, [
    FlowName,
    activeView,
    resolveFlowDisplayName,
    ccOverlayOpen,
    ccSelectedDevice,
    gcArchiveBreadcrumb,
    gcProjectName,
    hasCustomOrganizationBranding,
    i18n.language,
    projectState,
    selectedTraining,
    t,
    ytSelectedDevice,
  ]);

  const [guideSpotlight, setGuideSpotlight] = useState(false);
  const [guideFocusToken, setGuideFocusToken] = useState(0);
  const guideAutoCheckRef = useRef(false);
  const shouldShowFlowDialogAfterGuideRef = useRef(false);
  const guideNudgeDismissedRef = useRef(false);
  const guideViewSwitchCountRef = useRef(0);
  const lastGuideNudgeViewRef = useRef('');
  const [isGuideNudgeVisible, setIsGuideNudgeVisible] = useState(false);

  const guideIdentity = useMemo(() => {
    if (!currentUser) return { userId: '', userType: '' };
    const userId = String(
      currentUser.unified_user_id ||
      currentUser.studentID ||
      currentUser.student_id ||
      currentUser.username ||
      currentUser.employeeNumber ||
      ''
    );
    const userType = currentUser.user_type ||
      (currentUser.isSchoolUser ? 'campus' : currentUser.isCasUser ? 'bupt_cas' : currentUser.isGuestUser ? 'guest' : 'guest');
    return { userId, userType };
  }, [currentUser]);

  const guideCacheKey = useMemo(() => {
    return guideIdentity.userId ? `ybg_onboarding_${GUIDE_VERSION}_seen_${guideIdentity.userId}` : '';
  }, [guideIdentity.userId]);

  const updateGuideStatus = useCallback(async (status, lastAction = '') => {
    if (!guideIdentity.userId || !guideIdentity.userType) return false;
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/onboarding/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: guideIdentity.userId,
          user_type: guideIdentity.userType,
          guide_key: GUIDE_KEY,
          guide_version: GUIDE_VERSION,
          status,
          last_action: lastAction,
        }),
      });
      const ok = response.ok;
      if (ok && guideCacheKey && (status === 'completed' || status === 'dismissed')) {
        localStorage.setItem(guideCacheKey, status);
      }
      return ok;
    } catch (error) {
      console.error('更新导览状态失败:', error);
      return false;
    }
  }, [guideIdentity.userId, guideIdentity.userType, guideCacheKey]);

  const openGuide = useCallback(() => {
    setIsGuideNudgeVisible(false);
    switchView('portal');
    setGuideSpotlight(true);
    setGuideFocusToken((token) => token + 1);
    window.setTimeout(() => setGuideSpotlight(false), 2600);
  }, [switchView]);

  const closeGuide = useCallback((status = 'completed', action = 'finish_onboarding') => {
    shouldShowFlowDialogAfterGuideRef.current = false;
    updateGuideStatus(status, action);
    setGuideSpotlight(false);
  }, [updateGuideStatus]);

  useEffect(() => {
    if (!currentUser || !guideIdentity.userId || guideAutoCheckRef.current === guideIdentity.userId) return;
    guideAutoCheckRef.current = guideIdentity.userId;

    const checkGuideStatus = async () => {
      try {
        const params = new URLSearchParams({
          user_id: guideIdentity.userId,
          user_type: guideIdentity.userType,
          guide_key: GUIDE_KEY,
          guide_version: GUIDE_VERSION,
        });
        const response = await fetch(`${EDUGATE_API_URL}/api/onboarding/status?${params.toString()}`);
        if (!response.ok) return;
        const result = await response.json();
        const shouldShow = Boolean(result?.data?.should_show);
        // 不再全屏弹出；未完成导览时在「我的课程」右栏轻量高亮
        if (shouldShow) {
          setGuideSpotlight(true);
          window.setTimeout(() => setGuideSpotlight(false), 3200);
        } else if (guideCacheKey) {
          localStorage.setItem(guideCacheKey, result?.data?.status || 'completed');
        }
      } catch (error) {
        console.error('查询导览状态失败:', error);
        if (guideCacheKey && !localStorage.getItem(guideCacheKey)) {
          setGuideSpotlight(true);
          window.setTimeout(() => setGuideSpotlight(false), 3200);
        }
      }
    };

    checkGuideStatus();
  }, [currentUser, guideIdentity.userId, guideIdentity.userType, guideCacheKey]);

  const dismissGuideNudge = useCallback(() => {
    guideNudgeDismissedRef.current = true;
    sessionStorage.setItem('ybg_guide_nudge_dismissed', '1');
    setIsGuideNudgeVisible(false);
  }, []);

  const showGuideNudge = useCallback(() => {
    // 已在「我的课程」时右栏导览常驻，无需气泡提示
    if (!currentUser || activeView === 'portal' || isFlowTypeDialogOpen || guideNudgeDismissedRef.current) return;
    if (sessionStorage.getItem('ybg_guide_nudge_dismissed') === '1') return;
    setIsGuideNudgeVisible(true);
  }, [activeView, currentUser, isFlowTypeDialogOpen]);

  useEffect(() => {
    if (!currentUser || hasUserSentMessage || activeView === 'portal') return undefined;
    const timer = setTimeout(() => {
      if (!hasUserSentMessage) showGuideNudge();
    }, 4 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [activeView, currentUser, hasUserSentMessage, showGuideNudge]);

  useEffect(() => {
    if (!currentUser || hasUserSentMessage || activeView === 'portal') return;
    if (!lastGuideNudgeViewRef.current) {
      lastGuideNudgeViewRef.current = activeView;
      return;
    }
    if (lastGuideNudgeViewRef.current !== activeView) {
      lastGuideNudgeViewRef.current = activeView;
      guideViewSwitchCountRef.current += 1;
      if (guideViewSwitchCountRef.current >= 3) {
        showGuideNudge();
      }
    }
  }, [activeView, currentUser, hasUserSentMessage, showGuideNudge]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const keywords = ['怎么用', '为什么不回答', '教师端在哪', '不知道点哪里', '不会用', '如何使用'];
    const handleInput = (event) => {
      const target = event.target;
      const value = target && typeof target.value === 'string'
        ? target.value
        : (target && target.isContentEditable ? target.textContent || '' : '');
      if (value && keywords.some(keyword => value.includes(keyword))) {
        showGuideNudge();
      }
    };
    document.addEventListener('input', handleInput, true);
    return () => document.removeEventListener('input', handleInput, true);
  }, [currentUser, showGuideNudge]);

  const handleGuideA2UIAction = useCallback((action) => {
    const actionName = action?.name;
    if (!actionName) return false;

    const completeAndNavigate = (targetView) => {
      shouldShowFlowDialogAfterGuideRef.current = false;
      updateGuideStatus('completed', actionName);
      setGuideSpotlight(false);
      if (targetView === 'plaza') setSelectedTraining(null);
      if (targetView === 'portal') {
        switchView('portal');
        setGuideFocusToken((token) => token + 1);
        return;
      }
      switchView(targetView);
      if (targetView === 'ccma') {
        setTimeout(() => setIsFlowTypeDialogOpen(true), 200);
      }
    };

    switch (actionName) {
      case 'go_portal':
        completeAndNavigate('portal');
        return true;
      case 'go_ccma':
        completeAndNavigate('ccma');
        return true;
      case 'go_plaza':
        completeAndNavigate('plaza');
        return true;
      case 'go_discuss':
        completeAndNavigate('discuss');
        return true;
      case 'go_groupchat':
        completeAndNavigate('groupchat');
        return true;
      case 'go_youlaber':
        completeAndNavigate('youlaber');
        return true;
      case 'go_drawing':
        if (tokenBalance !== null && tokenBalance < 10) {
          showNotification('Token额度已耗尽，无法使用绘图功能', 'error');
          return true;
        }
        completeAndNavigate('drawing');
        return true;
      case 'finish_onboarding':
        closeGuide('completed', actionName);
        return true;
      case 'dismiss_onboarding':
        closeGuide('dismissed', actionName);
        return true;
      case 'postpone_onboarding':
        // 右栏常驻，稍后再说仅关闭高亮，不卸载导览
        setGuideSpotlight(false);
        return true;
      default:
        return false;
    }
  }, [closeGuide, setIsFlowTypeDialogOpen, setSelectedTraining, showNotification, switchView, tokenBalance, updateGuideStatus]);

  // 旧 'comprehensive' 静默迁移到 'ccma'（兜底）
  useEffect(() => {
    if (activeTrainingMode === 'comprehensive') {
      setActiveTrainingMode('ccma');
    }
  }, [activeTrainingMode, setActiveTrainingMode]);

  // ✅ 语音输入相关状态 - 使用 hook
  const {
    isVoiceRecording,
    setIsVoiceRecording,
    isVoiceProcessing,
    setIsVoiceProcessing,
  } = useVoiceState();
  const voiceRecognitionRef = useRef(null);

  // 🎭 数字人相关状态 - digitalHumanOpen, isSimDialogOpen 已迁移到 useUIState hook
  // simulationConfig 已迁移到 useDigitalHumanState hook
  
  // 🆕 处理数字人模拟启动
  const handleStartSimulation = (config) => {
    console.log('🎬 启动数字人模拟:', config);
    setSimulationConfig({
      id: config.avatarName.replace(/\s+/g, '_'),
      name: config.avatarName,
      avatarPath: config.avatarPath,
      voice: config.voice,
      model_name: 'cosyvoice-v1'
    });
    setCurrentSpeechText(config.text);
    setCurrentExpertName(config.avatarName);
    setDigitalHumanOpen(true);
  };
  // ✅ 数字人相关状态 - 使用 hook
  const {
    currentExpertName,
    setCurrentExpertName,
    currentSpeechText,
    setCurrentSpeechText,
    isExpertServiceAvailable,
    setIsExpertServiceAvailable,
    classMode,
    setClassMode,
    digitalHumanGlobalEnabled,
    setDigitalHumanGlobalEnabled,
    currentFileExpertName,
    setCurrentFileExpertName,
    currentFileExpertText,
    setCurrentFileExpertText,
    simulationConfig,
    setSimulationConfig,
  } = useDigitalHumanState();

  const isExpertServiceAvailableRef = useRef(false); // 用于闭包中访问最新值
  const hasCheckedExpertServiceRef = useRef(false); // 防止重复检查
  const digitalHumanGlobalEnabledRef = useRef(digitalHumanGlobalEnabled); // 用于闭包中访问最新值
  const classModeRef = useRef(classMode); // 用于闭包中访问上课模式最新值
  
  // 💡 待调用专家推荐列表 - 已迁移到 useExpertState hook

  // 🔄 监听消息流，提取A2UI中的专家推荐
  useEffect(() => {
    if (messages.length === 0) return;
    
    const lastMsg = messages[messages.length - 1];
    // 只处理 Mentor/Assistant 的文本消息（字段是 context）
    const msgContent = lastMsg.context || lastMsg.content;
    if (!msgContent || typeof msgContent !== 'string' || lastMsg.type === 'human') return;
    
    // 正则扫描 :::button action="call_expert_xxx" ... ::: \n 专家名
    // 兼容 \r\n 和 \n 换行符
    const normalizedContent = msgContent.replace(/\r\n/g, '\n');
    const buttonRegex = /:::button\s+action="call_expert_[^"]+"\s*[^\n]*\n(.*?)\n:::/g;
    
    let match;
    const newRecommendations = [];
    
    while ((match = buttonRegex.exec(normalizedContent)) !== null) {
      const expertName = match[1].trim();
      if (expertName) {
        newRecommendations.push(expertName);
      }
    }
    
    if (newRecommendations.length > 0) {
      console.log('📌 发现推荐专家:', newRecommendations);
      setPendingExperts(prev => {
        const next = [...prev];
        let changed = false;
        newRecommendations.forEach(name => {
          if (!next.includes(name)) {
            next.push(name);
            changed = true;
          }
        });
        console.log('📌 更新待调用专家列表:', next);
        return changed ? next : prev;
      });
    }
  }, [messages]);

  // 🗑️ 移除已调用的专家
  const removePendingExpert = (expertName) => {
    setPendingExperts(prev => {
      // 模糊匹配移除：如果调用了"伦理"，则移除"伦理专家"、"伦理顾问"等
      const normalizedCall = expertName.replace(/专家|顾问|咨询|调用/g, '').trim().toLowerCase();
      
      const next = prev.filter(name => {
        const normalizedPending = name.replace(/专家|顾问|咨询|调用/g, '').trim().toLowerCase();
        // 如果核心词相同，则视为已调用
        // 例如：Pending="伦理专家", Call="伦理" -> 移除
        return !normalizedPending.includes(normalizedCall) && !normalizedCall.includes(normalizedPending);
      });
      
      return next.length !== prev.length ? next : prev;
    });
  };
  
  // 🎭 持久化数字人全局开关状态
  useEffect(() => {
    digitalHumanGlobalEnabledRef.current = digitalHumanGlobalEnabled; // 同步到 ref
    localStorage.setItem('digitalHumanGlobalEnabled', digitalHumanGlobalEnabled);
    console.log('🎭 数字人全局开关:', digitalHumanGlobalEnabled ? '✅ 已开启' : '🔇 已关闭');
  }, [digitalHumanGlobalEnabled]);

  // 📚 同步上课模式到 ref
  useEffect(() => {
    classModeRef.current = classMode;
    console.log('📚 上课模式:', classMode ? '✅ 已开启' : '🔇 已关闭');
  }, [classMode]);
  
  // 🎭 文件面板专家数字人状态 - fileExpertDigitalHumanOpen 已迁移到 useUIState hook
  // currentFileExpertName, currentFileExpertText 已迁移到 useDigitalHumanState hook
  
  // 🎭 专家预初始化状态（用于提前加载数字人模型）
  const currentInitializingExpertRef = useRef(null); // 记录正在初始化的专家名称

  // conflictDialog, attachedFiles, isFileUploading 已迁移到 useAttachmentState hook
  const fileInputRef = useRef(null) // 隐藏的文件输入框引用

  // ✅ 文件上传与附件管理 Hook
  const {
    handleFileChange,
    handleRemoveAttachment,
    clearAttachments,
  } = useFileUploadHandlers({
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    isInExpertMode,
    selectedExpert,
    sessionId,
    showNotification,
  });

  // 专家对话卡片高亮（5秒）状态与定时器 - expertFlashMap 已迁移到 useExpertState hook
  const expertFlashTimersRef = useRef({}) // { [fileId]: timeoutId }


  
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault()
      const startX = e.clientX
      const startWidth = panelWidth
      const filePanel = e.target.closest(".file-panel") // 在这里获取 filePanel

      const handleMouseMove = (e) => {
        const width = Math.max(
          400,
          Math.min(800, startWidth + startX - e.clientX)
        )
        // 🚀 使用 startTransition 包装非紧急的面板宽度更新
        startTransition(() => {
          setPanelWidth(width)
        })

        // 如果找到了 filePanel，就更新它的宽度
        if (filePanel) {
          filePanel.style.width = `${width}px`
          filePanel.style.maxWidth = `${width}px`
        }
      }

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      // 🚀 使用 passive: true 提升滚动/拖拽性能
      document.addEventListener("mousemove", handleMouseMove, { passive: true })
      document.addEventListener("mouseup", handleMouseUp, { passive: true })
    },
    [panelWidth]
  )
    // 🔑 新增：添加本地聊天消息函数（不发送给后端）
    const addLocalMessage = (messageContent, messageType = "human") => {
      const localMessage = {
        type: "human",                    // ✅ 正确的人类消息类型
        role: "Human",                    // ✅ 正确的角色名  
        context: messageContent,          // ✅ 使用context字段而不是content
        timestamp: new Date().toISOString(),
        messageId: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        isLocalOnly: true,               // 标记为本地消息
        isHuman: "true"
      };
      
      setMessages((prevMessages) => [...prevMessages, localMessage]);
      console.log("📝 [本地消息] 已添加到聊天面板:", {
        类型: localMessage.type,
        角色: localMessage.role,
        内容长度: messageContent.length,
        消息ID: localMessage.messageId
      });
    };

  const handleWsActionAnswer = useWsActionAnswerHandler({
    setFiles,
    getLanguage,
    setConflictDialog,
    setActiveFile,
    isRestoringArchiveRef,
    isRestoringArchive,
    addLocalMessage,
    setProjectState,
  });

  const {
    handleConflictResolution,
    handleSendDraftToBackend,
  } = useDraftAndConflictHandlers({
    ws,
    files,
    selectedDiffLines,
    diffComments,
    setSelectedDiffLines,
    setDiffComments,
    sendSelectedContentToBackend,
    conflictDialog,
    setConflictDialog,
    setFiles,
    getLanguage,
    setActiveFile,
    addLocalMessage,
  });

  // 在组件顶部添加 useEffect
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 检查点击是否在专家咨询团队的下拉菜单外部
      const expertDropdown = event.target.closest(".expert-dropdown") // 添加一个特定的类名
      const expertDialog = event.target.closest(".expert-dialog") // 检查是否在专家对话框内部
      if (!expertDropdown && !expertDialog) {
        setIsExpertDropdownOpen(false)
      }
    }

    // 🚀 使用 passive: true 提升性能
    document.addEventListener("mousedown", handleClickOutside, { passive: true })

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {

  }, [projectState])

  // 🔧 新增：监控 isLoggedIn 状态变化（用于调试 cleanup 触发）
  useEffect(() => {
    console.log('📊 [STATE-CHANGE] isLoggedIn 状态变化:', {
      newValue: isLoggedIn,
      timestamp: new Date().toISOString(),
      // 🔍 调试：记录调用栈以追踪状态变化源
      stackTrace: new Error('isLoggedIn changed').stack?.split('\n').slice(0, 10).join('\n')
    })
  }, [isLoggedIn])

  const handleLogin = (user) => {
    console.log("登录成功，用户信息:", user);
    console.log("用户类型检查:", {
      isSchoolUser: user.isSchoolUser,
      employeeNumber: user.employeeNumber,
      name: user.name
    });
    if (ws.current) {
      ws.current.close()
    }

      // 清除之前的项目状态和历史记录
  try {
    sessionStorage.removeItem('projectStateHistory');
  } catch (error) {
    console.error('清空状态历史出错:', error);
  }
  setProjectState("");
    setIsLoggedIn(true)
    setCurrentUser(user)  // 存储完整的用户信息

    // ✅ 登录成功后，通知列表会由 useEffect 自动拉取（监听 currentUser 变化）

    // 默认落地「我的课程」面板，不再自动弹出流程选择对话框；
    // 需要时仍可通过顶部菜单或综合训练入口手动打开。
    if (user.isSchoolUser) {
      console.log("是学校用户，进入课程入口面板", {
        username: user.username,
        name: user.name,
        class_name: user.class_name,
        school: user.school,
        student_number: user.student_number
      });
      setShowHomeworkDialog(false);
    } else {
      console.log("登录成功，进入课程入口面板");
    }
    setIsFlowTypeDialogOpen(false);
    setActiveTrainingMode("portal");

    // 校园用户或CAS用户都显示自评图标和能力画像图标
    const isCasUser = user.employeeNumber && (user.class_name === '统一认证用户' || user.className === '统一认证用户');
    if (user.isSchoolUser || isCasUser) {
      console.log("检测到校园用户或CAS用户，将在3秒后检查自评状态和能力画像状态", {
        isSchoolUser: user.isSchoolUser,
        isCasUser: isCasUser,
        employeeNumber: user.employeeNumber,
        class_name: user.class_name,
        className: user.className
      });
      setTimeout(() => {
        checkSelfAssessmentStatus(user);
        checkCapabilityProfileStatus(user);
      }, 3000);
    } else {
      console.log("非校园用户且非CAS用户，不显示自评图标和能力画像图标", {
        isSchoolUser: user.isSchoolUser,
        employeeNumber: user.employeeNumber,
        class_name: user.class_name,
        className: user.className
      });
    }
    
    // 不在登录时立即获取Token余额，而是通过useEffect延迟获取
    // 避免与其他API请求冲突

    // 保存完整的用户信息到 localStorage
    localStorage.setItem(
      "userInfo",
      JSON.stringify({
        username: user.username,
        name: user.name,
        class_name: user.class_name,
        school: user.school,
        student_number: user.student_number,
        studentID: user.studentID,  // 添加studentID字段
        faculty: user.faculty,
        isSchoolUser: user.isSchoolUser,
        unified_user_id: user.unified_user_id, // ✅ 添加统一用户ID
        user_type: user.user_type, // ✅ 添加用户类型
        organizationId: user.organizationId || null,
        branding: user.branding || null,
      })
    )
  }

  // 登录后按机构定制浏览器标签标题；登出/无定制时恢复默认
  useEffect(() => {
    const brandTitle = currentUser?.branding?.title && String(currentUser.branding.title).trim();
    if (isLoggedIn && brandTitle) {
      document.title = brandTitle;
    } else {
      document.title = '邮百工——AI多智能体工程训练平台';
    }
  }, [isLoggedIn, currentUser?.branding?.title]);

  // 游戏化数据初始化 - 用户登录后获取数据（成长中心等级入口依赖此档案）
  useEffect(() => {
    const initGameData = async () => {
      if (!currentUser) return;
      const canInitGame =
        currentUser.isSchoolUser ||
        currentUser.isCasUser ||
        Boolean(currentUser.unified_user_id);
      // 访客无成长档案；其余登录用户尽量拉取，保证顶部等级入口可见
      if (!canInitGame || currentUser.isGuestUser || currentUser.user_type === 'guest') {
        return;
      }

      console.log('🎮 开始初始化游戏化数据:', currentUser);

      try {
        await fetchGameConfig();

        let studentId = currentUser.unified_user_id || currentUser.studentID || currentUser.student_id;

        if (!studentId && currentUser.isCasUser) {
          studentId = await getCasUserStudentId(
            currentUser.employeeNumber,
            currentUser.name,
            currentUser.user_type || 'bupt_cas'
          );
        }

        if (studentId) {
          console.log('🎮 使用学生ID获取游戏档案:', studentId);
          await fetchGameProfile(studentId);
          setCurrentStudentId(studentId);
        } else {
          console.warn('🎮 无法获取学生ID，跳过游戏化数据初始化');
        }
      } catch (error) {
        console.error('🎮 初始化游戏化数据失败:', error);
      }
    };

    if (currentUser) {
      setTimeout(initGameData, 1000);
    }
  }, [currentUser]);

  // 游戏化状态变化监控（调试用）
  useEffect(() => {
    console.log('🎮 [状态变化] feedbackDelta:', feedbackDelta);
  }, [feedbackDelta]);

  useEffect(() => {
    console.log('🎮 [状态变化] levelUpInfo:', levelUpInfo);
  }, [levelUpInfo]);

  useEffect(() => {
    console.log('🎮 [状态变化] unlockedAchievements:', unlockedAchievements);
  }, [unlockedAchievements]);

  const openHelpPage = () => {
    window.open("/help.html", "_blank")
  } // openHelpPage 的结束括号

  const handleRegisterSuccess = () => {
    setShowRegister(false)
    // 可以在这里添加其他注册成功后的逻辑，比如显示一个成功消息
    alert("注册成功，请登录")
  }

  // 清空项目状态和历史记录
  const clearProjectStateAndHistory = () => {
    // 先清空当前状态，确保历史记录重置时不会保留它
    setProjectState("");
    
    // 清空sessionStorage中保存的历史记录
    try {
      sessionStorage.removeItem('projectStateHistory');
      // 触发进度条组件的历史重置
      setResetProgressHistory(true);
      // 重置状态后自动还原
      setTimeout(() => setResetProgressHistory(false), 100);
    } catch (error) {
      console.error('清空状态历史出错:', error);
    }
  }

  const handleLogout = () => {
    if (ws.current) {
      ws.current.close()
    }
    // 清空项目状态和历史记录
    clearProjectStateAndHistory();
    // 清除localStorage中的用户信息
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    localStorage.removeItem('userInfo');
    setIsLoggedIn(false);
    setCurrentUser(null);
    setTrainingTimer(null);
    // 高教社用户需额外清除高教社 session
    if (userInfo.user_type === 'hep_authing') {
      window.location.href = HEP_AUTHING_LOGOUT_URL;
    }
  }
  
  const logAction = useCallback(
    (actionType, details) => {
      setLearningLog((prevLog) => [
        ...prevLog,
        {
          timestamp: new Date().toISOString(),
          actionType,
          details,
          projectState,
        },
      ])
    },
    [projectState]
  )

  const handleWsPostMessageEffects = useWsPostMessageEffects({
    logAction,
    setIsLoading,
    setIsWaitingResponse,
    setIsMessageInputEnabled,
    setIsProcessing,
    inputRef,
  });

  // 专家数据处理工具函数
  const parseExpertCategory = useCallback((expert) => {
    try {
      if (!expert.category) {
        return { base_category: "其他" };
      }
      
      if (typeof expert.category === 'string') {
        return JSON.parse(expert.category);
      }
      
      if (typeof expert.category === 'object') {
        return expert.category;
      }
      
      return { base_category: "其他" };
    } catch (error) {
      console.warn('解析专家category失败:', error);
      return { base_category: "其他" };
    }
  }, [])

  const processExpertsData = useCallback((expertsData) => {
    if (!Array.isArray(expertsData)) return [];

    return expertsData.map(expert => ({
      ...expert,
      // 统一清洗显示名称，去除首尾空格
      display_name: (expert?.display_name || '').trim(),
      // 轻度清洗描述文本
      description: typeof expert?.description === 'string' ? expert.description.trim() : expert?.description,
      parsedCategory: parseExpertCategory(expert)
    }));
  }, [parseExpertCategory])

  const getExpertCategories = useCallback((expertsData) => {
    const categories = new Set();
    expertsData.forEach(expert => {
      const category = parseExpertCategory(expert);
      if (category.base_category) {
        categories.add(category.base_category);
      }
    });
    return ['全部', ...Array.from(categories).sort()];
    }, [parseExpertCategory])

  // 动态分类颜色分配函数
  const getCategoryColor = useCallback((categoryName) => {
    if (!categoryName || categoryName === '全部' || categoryName === '其他') {
      return 'rgba(108, 117, 125, 0.8)'; // 灰色用于默认分类
    }
    
    const colors = [
      '#3498db', '#2ecc71', '#e67e22', '#9b59b6', 
      '#e74c3c', '#1abc9c', '#f39c12', '#e91e63',
      '#2980b9', '#27ae60', '#d35400', '#8e44ad',
      '#c0392b', '#16a085', '#d68910', '#ad1457'
    ];
    
    // 使用简单的哈希函数来确保同一分类总是得到相同颜色
    let hash = 0;
    for (let i = 0; i < categoryName.length; i++) {
      hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, [])

  // 专家名称转换函数组
  const getExpertCommandByDisplayName = useCallback((displayName) => {
    // 特殊处理文档分析助手
    if (displayName === "文档分析助手") {
      return "document_query";
    }
    
    if (!experts || !Array.isArray(experts)) return null;
    const target = (displayName || '').trim();
    const expert = experts.find(exp => (exp?.display_name || '').trim() === target);
    return expert ? expert.comd : null;
  }, [experts])

  const getExpertDisplayNameByCommand = useCallback((command) => {
    // 特殊处理文档分析助手
    if (command === "document_query") {
      return "文档分析助手";
    }
    
    if (!experts || !Array.isArray(experts)) return null;
    const expert = experts.find(exp => exp.comd === command);
    return expert ? (expert.display_name || '').trim() : null;
  }, [experts])

  // ✅ parseExpertInput, formatExpertForDisplay, convertExpertCommandsToDisplayNames 已迁移到 useExpertSelection hook
  // 赋值语句移到 useExpertSelection hook 调用之后

  // 当搜索词或分类变化时，更新过滤结果
  useEffect(() => {
    if (expertDialogContent && expertDialogContent.experts) {
      const filteredExperts = expertDialogContent.experts.filter(expert => {
        const matchesSearch = !expertSearchTerm || 
          expert.display_name?.toLowerCase().includes(expertSearchTerm.toLowerCase()) ||
          expert.display_name_en?.toLowerCase().includes(expertSearchTerm.toLowerCase()) ||
          expert.description?.toLowerCase().includes(expertSearchTerm.toLowerCase()) ||
          expert.description_en?.toLowerCase().includes(expertSearchTerm.toLowerCase());
        
        const category = parseExpertCategory(expert);
        const matchesCategory = selectedExpertCategory === '全部' || 
          category.base_category === selectedExpertCategory;
        
        return matchesSearch && matchesCategory;
      });
      
      // 🚀 使用 startTransition 包装非紧急的专家过滤结果更新
      startTransition(() => {
        setExpertDialogContent(prev => ({
          ...prev,
          filteredExperts
        }))
      });
    }
  }, [expertSearchTerm, selectedExpertCategory, parseExpertCategory])

  const preprocessContent = useCallback((content) => {
    return preprocessContentWithCache(content);
  }, []);

  // handleOpinionAdoption moved to useMessageHandlers
  
  const {
    handleFileLineSelection,
    copyToClipboard,
  } = useFileContentInteractions({
    activeFile,
    files,
    activeFileContent,
    setActiveFileContent,
    setSelectedFileLines,
    inputRef,
  });

  useMicroSceneBubbles({
    messages,
    parseMicroSceneData,
    setMicroSceneBubbles,
  });

  // 在其他 state 声明之后添加这个函数
  // handleDiffLineClick/handleCommentChange moved to useMessageHandlers
  // renderMessageContent moved below handleExpertCommandSelect
  // ✅ 通知处理函数已迁移到 useNotificationHandlers hook

  // 修改 getDisplayFileName 函数
  const getDisplayFileName = (fileName, removeRole = false) => {
    if (!fileName) return "无"

    // 如果需要移除角色名
    if (removeRole) {
      // 移除角色名部分 (包括冒号)
      fileName = fileName.replace(/^[^:]+:/, "")
    }

    let displayName = fileName.replace(/^(WriteCode|WriteCodeReview):/, "")
    const lastDotIndex = displayName.lastIndexOf(".")
    if (lastDotIndex !== -1) {
      const name = displayName.substring(0, lastDotIndex)
      const extension = displayName.substring(lastDotIndex)
      displayName = name.replace(/\.py$/, "") + extension
    }

    // 截断文件名并添加省略号
    if (displayName.length > 20) {
      displayName = displayName.substring(0, 19) + "..."
    }
    return displayName
  }

  // 根据角色设置文件图标
  const getFileIcon = (fileName, role = '', isExpertDialog = false) => {
    if (!fileName) {
      console.warn("Invalid file name: empty or undefined")
      return "🗃️" // 默认文件图标
    }
    
    // 如果是文件夹
    if (fileName.endsWith("/")) return "📂"

    // 专家对话文件特殊图标
    if (isExpertDialog) {
      return "💬"; // 专家对话特殊图标
    }
    
    // 🎨 Drawio 文件图标
    if (fileName.endsWith('.drawio')) {
      return "🎨";
    }
    
    // 优先考虑角色，为不同角色设置专属图标
    if (role) {
      const lowerRole = role.toLowerCase();
      
      // 产品经理
      if (lowerRole.includes('product_manager')  || lowerRole.includes('产品设计')) {
        return "📊"; // 产品经理图标
      }
      
      // 架构师
      if (lowerRole.includes('architecture_agent') || lowerRole.includes('架构设计')) {
        return "🏗️"; // 架构师图标
      }
      
      // 硬件架构师
      if (lowerRole.includes('hardware_architect') || lowerRole.includes('硬件架构设计')) {
        return "🔌"; // 硬件架构师图标
      }
      
      // 项目经理
      if (lowerRole.includes('project_manager') || lowerRole.includes('任务分配')) {
        return "📋"; // 项目经理图标
      }
      
      // 工程师
      if (lowerRole.includes('engineer') || lowerRole.includes('代码编写')) {
        return "💻"; // 更换为无背景的电脑图标
      }
      
      // QA工程师
      if (lowerRole.includes('qa_engineer') || lowerRole.includes('代码评审')) {
        return "🧪"; // QA工程师图标
      }
      
      // 硬件工程师
      if (lowerRole.includes('hardware_engineer') || lowerRole.includes('硬件模块设计')) {
        return "⚙️"; // 硬件工程师图标
      }
      
      // 硬件制造工程师
      if (lowerRole.includes('hardware_manufacture_engineer') || lowerRole.includes('硬件制造')) {
        return "🔧"; // 硬件制造工程师图标
      }
      
      // 共情阶段
      if (lowerRole.includes('empathize_agent') || lowerRole.includes('共情')) {
        return "🤝"; // 共情阶段图标
      }
      
      // 定义阶段
      if (lowerRole.includes('define_agent') || lowerRole.includes('定义')) {
        return "📝"; // 定义阶段图标
      }
      
      // 构思阶段
      if (lowerRole.includes('ideate_agent') || lowerRole.includes('构思')) {
        return "💡"; // 构思阶段图标
      }
      
      // 原型阶段
      if (lowerRole.includes('prototype_agent') || lowerRole.includes('原型')) {
        return "🎨"; // 原型阶段图标
      }
      
      // 测试阶段
      if (lowerRole.includes('test_agent') || lowerRole.includes('测试')) {
        return "🧪"; // 测试阶段图标
      }

      // 创意助手
      if (lowerRole.includes('creative_assistant') || lowerRole.includes('创意')) {
        return "💡"; // 创意助手图标
      }
    }
    
    // 如果没有匹配到任何角色或者没有角色信息，返回默认的不显眼图标
    return "🗃️"; // 使用深色文件柜图标作为默认图标
  }
  


  const handleBubbleClick = useCallback((scene) => {
    setSelectedMicroScene(scene)
    setIsDetailModalOpen(true)
  }, [])
  // shouldFlash 已迁移到 useUIState hook
  const { reconnect: reconnectCCMA } = useWebSocketLifecycle({
    isLoggedIn,
    enabled: isLoggedIn && ccmaWsEnabled,
    ws,
    wsUrl: CCMA_WS_URL, // 综合训练统一走 CCMA（旧 GoMAown 入口已下线）
    setSessionId,
    setUserId,
    setConnectionStatus,
    setIsSessionReady,
    setError,
    setTabsWidth,
    dividerRef,
    messageDispatchRef: wsMessageDispatchRef,
  });
  const chatlogExportWaiterRef = useRef(null);

  // 🔧 新增：监听 currentUser 变化，自动拉取通知列表
  useEffect(() => {
    if (currentUser) {
      console.log('👤 currentUser 已设置，开始拉取通知列表', {
        userId: currentUser.unified_user_id || currentUser.username,
        timestamp: new Date().toISOString()
      });
      fetchNotifications(true);
    }
  }, [currentUser]);

  // 🔧 标签页可见性监控：CCMA 连接保活期间检查（含训练中切到其它页），避免误报断连
  useEffect(() => {
    if (!isLoggedIn || !ccmaWsEnabled) return

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        console.log('📱 标签页进入后台')
        return
      }

      console.log('📱 标签页重新激活，延迟检查连接状态...')

      // 延迟 2 秒检查：给 WebSocket 重建连接的时间（StrictMode/正常重连均需要一段时间）
      setTimeout(() => {
        const state = ws.current?.readyState
        if (state === WebSocket.CLOSED || state === WebSocket.CLOSING || !ws.current) {
          console.warn('⚠️ 延迟检查：连接仍断开（标签页休眠），立即重连')
          setConnectionStatus('reconnecting')
          reconnectCCMA()
        } else {
          console.log('✅ 延迟检查：连接正常，readyState =', state)
        }
      }, 2000)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true })

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isLoggedIn, ccmaWsEnabled, reconnectCCMA])

  // 🔧 新增：在WebSocket连接建立后发送FlowName（确保使用最新值）
  useEffect(() => {
    // 只有在已登录、连接已建立、有sessionId的情况下才发送
    if (!isLoggedIn || connectionStatus !== 'connected' || !sessionId) {
      return;
    }

    // 检查WebSocket连接状态
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const flowToSend = pendingFlowSelectRef.current || FlowName;
    if (!flowToSend) return;
    console.log("🔄 连接建立后发送FlowName:", flowToSend);
    ws.current.send(
      JSON.stringify({
        type: "set_flow_type",
        FlowName: flowToSend,
        session_id: sessionId,
        ...getFlowResourceBinding(flowToSend)
      })
    );
  }, [isLoggedIn, connectionStatus, sessionId, FlowName, getFlowResourceBinding]);

  // 🔧 新增：会话准备超时检查（仅 CCMA/YouLaber 视图需要 CCMA 会话）
  useEffect(() => {
    if (!isLoggedIn || !ccmaWsEnabled || isSessionReady) return

    const timeout = setTimeout(() => {
      if (!isSessionReady) {
        console.warn("会话创建超时，尝试重新连接")
        setError({ message: "会话创建超时，请刷新页面重试" })
      }
    }, 30000) // 30秒超时

    return () => clearTimeout(timeout)
  }, [isLoggedIn, ccmaWsEnabled, isSessionReady])

  useMessageHistoryTracking({
    messages,
    setIsViewingHistory,
    chatPanelRef,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRoles.length < 2) {
      setTeamValidationMessage("您必须至少选中2个角色");
      setTeamDialogOpenSignal(s => s + 1);
      return;
    }

    // 检查Token余额是否小于10
    if (tokenBalance !== null && tokenBalance < 10) {
      setError({ message: '您的Token已经耗尽！' });
      return;
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      const finalFlowName = FlowName === null ? false : FlowName;
      
      // 构造完整的消息对象
      const message = {
        type: "start_project",
        params: {
          idea: params.idea || "",
          selected_role: selectedRoles.join(','),
          FlowName: finalFlowName
        }
      };
      
      // 添加更详细的日志
      console.log("准备发送完整消息:", {
        消息类型: message.type,
        创意状态: message.params.FlowName,
        选中角色: message.params.selected_role,
        创意内容: message.params.idea
      });
      
      // 确保发送完整的消息对象
      ws.current.send(JSON.stringify(message));
      
      // 记录发送后的状态
      console.log("消息已发送，当前 FlowName:", finalFlowName);
      
      setProjectState("新需求");
    } else {
      console.error("WebSocket is not connected");
    }
  };

  // 添加 WebSocket 消息监听，确认发送的内容
  useEffect(() => {
    if (!ws.current) return;
    
    const originalSend = ws.current.send;
    ws.current.send = function(data) {
      //console.log("WebSocket 实际发送的数据:", data);
      return originalSend.call(this, data);
    };
  }, [ws.current]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setParams((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? parseFloat(value)
          : value,
    }))
  }

  // 防重复发送专家总结检查请求
  const checkExpertSummaryWithDebounce = (expertCommand) => {
    const now = Date.now();
    const debounceTime = 1000; // 1秒内防重复
    
    // 使用ref进行立即检查，避免React状态更新延迟问题
    if (lastExpertSummaryCheckRef.current.command === expertCommand && 
        now - lastExpertSummaryCheckRef.current.timestamp < debounceTime) {
      console.log(`防重复：跳过专家 ${expertCommand} 的检查请求 (距离上次 ${now - lastExpertSummaryCheckRef.current.timestamp}ms)`);
      return false; // 返回false表示没有发送
    }
    
    // 发送检查请求
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      console.log(`发送专家 ${expertCommand} 的总结检查请求`);
      ws.current.send(JSON.stringify({
        type: "check_expert_summary",
        expert_command: expertCommand
      }));
      
      // 立即更新ref和state
      const newRecord = { command: expertCommand, timestamp: now };
      lastExpertSummaryCheckRef.current = newRecord;
      setLastExpertSummaryCheck(newRecord);
      return true; // 返回true表示已发送
    }
    return false;
  };

  // ✅ 专家选择逻辑 - 使用 hook
  const {
    parseExpertInput: parseExpertInputFromHook,
    formatExpertForDisplay: formatExpertForDisplayFromHook,
    convertExpertCommandsToDisplayNames: convertExpertCommandsToDisplayNamesFromHook,
    handleExpertSelect: handleExpertSelectFromHook,
    handleExpertCommandSelect: handleExpertCommandSelectFromHook,
    handleInputChange: handleInputChangeFromHook,
    createKeyDownHandler,
    getLocalizedDisplayName,
  } = useExpertSelection({
    experts,
    selectedExpert,
    setSelectedExpert,
    isInExpertMode,
    setIsInExpertMode,
    currentExpertCommand,
    setCurrentExpertCommand,
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    checkExpertSummaryWithDebounce,
    removePendingExpert,
    getExpertCommandByDisplayName,
    getExpertDisplayNameByCommand,
  });

  // 使用 hook 返回的函数（必须在 hook 调用之后）
  const parseExpertInput = parseExpertInputFromHook;
  const formatExpertForDisplay = formatExpertForDisplayFromHook;
  const convertExpertCommandsToDisplayNames = convertExpertCommandsToDisplayNamesFromHook;

  // 团队成员选择对话框：自动弹出信号（每次自增触发一次打开）+ 校验提示文案
  const [teamDialogOpenSignal, setTeamDialogOpenSignal] = useState(0);
  const [teamValidationMessage, setTeamValidationMessage] = useState('');

  const handleRoleSelect = (roleType) => {
    // 检查对话是否已经开始
    if (hasUserSentMessage) {
      showNotification('对话已开始，无法更换角色', 'warning');
      return;
    }
    
    const roleName = agents.find(agent => agent.type === roleType)?.name || roleType; // 查找对应的名称用于日志和消息发送
    const isSelected = selectedRoles.includes(roleType);
    const nextRoles = isSelected
      ? selectedRoles.filter((r) => r !== roleType)
      : [...selectedRoles, roleType];

    // 🚀 使用函数式更新，避免闭包陷阱
    setSelectedRoles(prev => {
      return isSelected
        ? prev.filter((r) => r !== roleType)
        : [...prev, roleType];
    });

    // 选够角色后自动清除"团队成员不足"提示
    if (nextRoles.length >= 2 && teamValidationMessage) {
      setTeamValidationMessage('');
    }

    // 发送选择状态到后端
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: "role_select",
        role: roleName, // 发送给后端仍然使用名称，因为后端session.go的映射是基于名称的
        selected: !isSelected,
        session_id: sessionId
      }));
      console.log(`发送角色选择: ${roleName}, 选中: ${!isSelected}`);
    }
  };

  const isInputEnabled =
    selectedRoles.length > 0 && lastMessageType === "MsgtoHuman"

  // ✅ handleExpertSelect, handleExpertCommandSelect, handleInputChange 已迁移到 useExpertSelection hook
  // 使用 hook 返回的函数
  const handleExpertSelect = handleExpertSelectFromHook;
  const handleExpertCommandSelect = handleExpertCommandSelectFromHook;
  const handleInputChange = handleInputChangeFromHook;

  // handleKeyDown 定义移到 handleHumanInputMessage 之后（避免 TDZ 错误）

  // 🏁 结束当前阶段快捷按钮处理函数
  const handleEndPhase = useCallback(() => {
    // 检查WebSocket连接
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error("WebSocket未连接，无法结束阶段");
      return;
    }
    
    // 检查是否允许输入
    if (!isMessageInputEnabled || isProcessing) {
      console.log("当前不允许操作");
      return;
    }
    
    // 设置UI状态（与handleHumanInputMessage一致）
    setIsProcessing(true);
    setHasUserSentMessage(true);
    setIsWaitingResponse(true);
    setIsMessageInputEnabled(false);
    
    // 完全复刻手工输入的消息格式
    const message = JSON.stringify({
      type: "humaninput",
      roles: selectedRoles,
      input: "@yes",
      session_id: sessionId,
      user_id: userId,
      modelType: modelType
    });
    
    ws.current.send(message);
    console.log("已发送结束阶段命令:", message);
    
    // 添加用户消息到消息列表
    setMessages((prev) => [
      ...prev,
      {
        type: "human",
        role: "Human",
        context: "@yes",
        sendto: "",
        timestamp: new Date().toISOString(),
        isNew: true
      },
    ]);
    
    // 清空输入框
    const textArea = inputRef.current || document.querySelector('textarea[name="humanInput"]');
    if (textArea) {
      textArea.value = "";
    }
    
    // 注意：不在这里重置处理状态，由ws.onmessage回调处理
  }, [isMessageInputEnabled, isProcessing, selectedRoles, sessionId, userId, modelType]);

  const handleHumanInputMessage = async (e) => {
    e.preventDefault();
    const input = e.target.elements.humanInput.value;
    const inputElement = e.target.elements.humanInput instanceof HTMLTextAreaElement 
      ? e.target.elements.humanInput 
      : document.querySelector('textarea[name="humanInput"]');

    // 设置处理状态
    setIsProcessing(true);

    // 检查 WebSocket 连接
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error("WebSocket is not connected");
      setIsProcessing(false); // 重置处理状态
      return;
    }

    // 第一次发送消息（项目启动）
    if (messages.length === 0) {
      // 检查输入是否为空
      if (!input || !input.trim()) {
        setIsProcessing(false); // 重置处理状态
        return; // 如果输入为空，直接返回，不执行后续发送操作
      }
      
      // 检查角色选择
      if (selectedRoles.length < 2) {
        setTeamValidationMessage("您必须至少选中2个角色");
        setTeamDialogOpenSignal(s => s + 1);
        setIsProcessing(false); // 重置处理状态
        return; // 不清空输入框，保留用户输入的内容
      }
      
      // 检查Token余额是否小于10
      if (tokenBalance !== null && tokenBalance < 10) {
        setError({ message: '您的Token已经耗尽！' });
        setIsProcessing(false); // 重置处理状态
        return;
      }

      // 🔧 生成新的 projectId，用于 ExpertService 专家会话隔离
      const newProjectId = "proj_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      setProjectId(newProjectId);
      console.log("生成新的 projectId:", newProjectId);

      // 使用与 handleSubmit 相同的格式发送消息
      console.log("Sending to backend:", { ...params, idea: input, FlowName });
      logAction("PROJECT_START", {
        params: { ...params, idea: input },
        selectedRoles,
        FlowName,
        projectId: newProjectId
      });

      const message = {
        type: "start_project",
        params: {
          idea: input,
          selected_role: selectedRoles.join(','),
          FlowName: FlowName === null ? false : FlowName,
          project_id: newProjectId  // 传递 projectId 给后端
        }
      };

      console.log("发送完整消息:", message);
      ws.current.send(JSON.stringify(message));

      setMessages((prev) => [
        ...prev,
        {
          type: "human",
          role: "Human",
          context: input,
          sendto: "",
          timestamp: new Date().toISOString(), // 添加时间戳
          isNew: true // 标记为新消息
        },
      ]);

      setProjectState("新需求");
      if (inputElement) {
        inputElement.value = ""; // 使用获取到的inputElement元素
      }
      setIsProcessing(false); // 重置处理状态
      // 消息成功发送后，设置用户已发送消息标志
      setHasUserSentMessage(true);
      return;
    }

    // 后续消息的处理
    if (!isMessageInputEnabled) {
      setIsProcessing(false); // 重置处理状态
      return
    }

    // 处理差异评论
    const commentsToSend = Object.entries(diffComments)
      .flatMap(([messageIndex, lineComments]) => {
        // 检查messageIndex是否是文件ID而不是消息索引
        const isFileComment = messageIndex.includes('/'); // 文件ID通常包含'/'字符
        
        if (isFileComment) {
          // 处理文件面板中的评论 - 不需要访问messages
          return Object.entries(lineComments).map(([lineIndex, comment]) => {
            // 尝试获取行内容
            const fileId = messageIndex;
            const fileName = fileId.split('/').pop(); // 从fileId中提取文件名
            
            // 获取文件内容
            let lineContent = "未找到内容";
            try {
              // 打印调试信息
              console.log('处理文件评论 - fileId:', fileId);
              console.log('window._fileContents 键:', Object.keys(window._fileContents || {}));
              
              // 尝试多种方式获取文件内容
              let fileContent = null;
              
              // 直接使用fileId
              if (window._fileContents && window._fileContents[fileId]) {
                fileContent = window._fileContents[fileId];
              } 
              // 尝试使用文件名
              else if (window._fileContents) {
                const simpleFileName = fileId.split('/').pop();
                if (window._fileContents[simpleFileName]) {
                  fileContent = window._fileContents[simpleFileName];
                  console.log('通过简化文件名找到内容:', simpleFileName);
                }
              }
              
              // 如果仍未找到，尝试从files对象获取
              if (!fileContent && files && files[fileId]) {
                fileContent = files[fileId].content;
                console.log('从files对象获取内容');
                
                // 缓存到window._fileContents
                if (!window._fileContents) window._fileContents = {};
                window._fileContents[fileId] = fileContent;
              }
              
              if (fileContent) {
                // 尝试解析为JSON，检查是否为Draft格式
                let parsedContent = fileContent;
                if (typeof fileContent === 'string') {
                  try {
                    parsedContent = JSON.parse(fileContent);
                  } catch (e) {
                    // 不是JSON，按普通文本处理
                    const lines = fileContent.split('\n');
                    lineContent = lines[lineIndex] || "未找到内容";
                  }
                }
                
                // 如果是数组且元素有type和content字段，则视为Draft格式
                if (Array.isArray(parsedContent) && parsedContent.length > 0 && 
                    'type' in parsedContent[0] && 'content' in parsedContent[0]) {
                  if (lineIndex < parsedContent.length) {
                    lineContent = `${parsedContent[lineIndex].type}: ${parsedContent[lineIndex].content}`;
                  }
                }
              }
            } catch (e) {
              console.error('获取行内容时出错:', e);
            }
            
            return `修改意见 - 行${parseInt(lineIndex) + 1}: ${comment}\n原始内容: ${lineContent}`;
          });
        } else {
          // 处理消息面板中的评论
          const message = messages[messageIndex];
          if (!message || !message.context || !Array.isArray(message.context)) {
            console.warn(`无法找到消息或消息内容格式不正确: ${messageIndex}`);
            return [];
          }
          
          return Object.entries(lineComments).map(([lineIndex, comment]) => {
            const line = message.context[lineIndex];
            if (!line) {
              console.warn(`无法找到行: ${lineIndex}`);
              return `行${parseInt(lineIndex) + 1}: ${comment}`;
            }
            return `行${parseInt(lineIndex) + 1} (${line.type}):\n当前内容: ${
              line.content
            }\n修改意见: ${comment}`;
          });
        }
      })
      .join("\n\n")

    // 处理采纳的专家意见
    const adoptedOpinions = Object.entries(adoptedExpertOpinions)
      .flatMap(([messageIndex, lineIndices]) => {
        const message = messages[messageIndex]
        return lineIndices.map((lineIndex) => {
          const lineContent = message.context.split("\n")[lineIndex]
          return `咨询意见： ${parseInt(messageIndex) + 1}.${
            parseInt(lineIndex) + 1
          }: ${lineContent}`
        })
      })
      .join("\n")
      
    // 处理选中的文件行内容
    // 查找真实的文件键名
    const findRealFileKey = (simpleFileName) => {
      // 直接匹配
      if (files[simpleFileName]) {
        return simpleFileName;
      }
      
      // 模糊匹配 - 查找包含简单文件名的键
      const possibleKeys = Object.keys(files).filter(key => 
        key.includes(simpleFileName) || 
        key.includes(simpleFileName.replace('.md', ''))
      );
      
      if (possibleKeys.length > 0) {
        return possibleKeys[0]; // 取第一个匹配项
      }
      
      return null;
    };
    
    // 通用获取文件内容的函数
    const getFileContent = (fileName) => {
      // 获取真实的文件键
      const realFileKey = findRealFileKey(fileName);
      
      // 首先尝试从activeFileContent获取
      if (activeFileContent && 
         (activeFileContent.fileName === fileName || 
          activeFileContent.fileName === realFileKey) && 
         activeFileContent.content) {
        return activeFileContent.content;
      }
      
      // 然后尝试从files对象获取
      if (realFileKey && files[realFileKey] && files[realFileKey].content) {
        if (typeof files[realFileKey].content === 'string') {
          return files[realFileKey].content;
        } else if (files[realFileKey].content && typeof files[realFileKey].content === 'object') {
          if ('content' in files[realFileKey].content) {
            return files[realFileKey].content.content || '';
          } else {
            return JSON.stringify(files[realFileKey].content, null, 2);
          }
        } else {
          return String(files[realFileKey].content || '');
        }
      }
      
      return '';
    };
    
    const selectedFileContent = Object.entries(selectedFileLines)
      .flatMap(([fileName, lineIndices]) => {
        // 如果没有选中行，跳过处理
        if (!lineIndices || lineIndices.length === 0) {
          return [];
        }
        
        // 尝试从缓存获取文件内容，确保与选择时使用同一个版本
        let content = '';
        let lines = [];
        const cachedFile = window._fileContentCache && window._fileContentCache[fileName];
        
        if (cachedFile && cachedFile.content) {
          content = cachedFile.content;
          lines = cachedFile.lines;
        } else {
          // 缓存不存在，尝试获取文件内容
          content = getFileContent(fileName);
          if (!content) {
            return [];
          }
          lines = content.split('\n');
        }
        
        // 预先过滤无效行索引，避免"行索引越界"错误
        const validLineIndices = lineIndices.filter(lineIndex => {
          const lineIndexNum = parseInt(lineIndex);
          return lineIndexNum >= 0 && lineIndexNum < lines.length;
        });
        
        // 如果存在无效行索引，记录日志但不显示错误
        if (validLineIndices.length < lineIndices.length) {
          console.warn(`文件 ${fileName} 存在无效行索引，已自动过滤`);
        }
        
        return validLineIndices.map((lineIndex) => {
          const lineIndexNum = parseInt(lineIndex);
          const lineContent = lines[lineIndexNum];
          return `咨询意见： ${fileName}:${lineIndexNum + 1}: ${lineContent}`;
        });
      })
      .filter(Boolean) // 移除任何空值
      .join("\n");
      
    // 组合所有输入
    let combinedInput = input
    if (commentsToSend) {
      combinedInput +=
        (combinedInput ? "\n\n" : "") + "修改意见：\n" + commentsToSend
    }
    if (adoptedOpinions) {
      combinedInput +=
        (combinedInput ? "\n\n" : "") + "采纳的咨询意见：\n" + adoptedOpinions
    }
    
    // 检查是否有选中的文件内容
    if (selectedFileContent && selectedFileContent.trim() !== '') {
      combinedInput +=
        (combinedInput ? "\n\n" : "") + "采纳的咨询意见：\n" + selectedFileContent
    }
    
    // 转换专家标识为命令格式并处理专家前缀
    combinedInput = parseExpertInput(combinedInput);
    
    // 添加选中专家的信息
    // 检查输入是否已经包含专家前缀（支持中文名称和命令格式）
    const expertPrefixPattern = /^@[^\s]+\s/;
    if (selectedExpert && !expertPrefixPattern.test(combinedInput)) {
      combinedInput = `@${selectedExpert.comd} ${combinedInput}`
    }


    // 检查最终组合后的输入是否为空（但如果有附件，即使输入为空也允许发送）
    if (!combinedInput || !combinedInput.trim()) {
      if (attachedFiles.length === 0) {
        setIsProcessing(false); // 重置处理状态
        return; // 如果最终输入为空且没有附件，直接返回
      }
    }

    // 额度预检查：Token余额不足时阻止所有AI调用
    if (tokenBalance !== null && tokenBalance < 10) {
      setError({ message: '您的Token已经耗尽！' });
      setIsProcessing(false);
      return;
    }

    // 确认有内容要发送后，设置UI状态
    // 用户已经成功开始对话，设置用户已发送消息标志
    setHasUserSentMessage(true);
    setIsWaitingResponse(true)
    setIsMessageInputEnabled(false)

    // 🎨 处理绘图助手模式 - 直接调用 aidrawio API，不经过 GoMAown
    if (isInExpertMode && selectedExpert && selectedExpert.comd === "diagram_draw") {
      // 额度预检查
      if (tokenBalance !== null && tokenBalance < 10) {
        setError({ message: 'Token额度已耗尽，无法使用绘图功能！' });
        setIsProcessing(false);
        setIsWaitingResponse(false);
        setIsMessageInputEnabled(true);
        return;
      }

      // 提取用户输入（去除专家前缀）
      const diagramInput = combinedInput.replace(/^@[^\s]+\s*/, '').trim();
      
      if (!diagramInput) {
        showNotification('请输入绘图需求', 'warning');
        setIsProcessing(false);
        setIsWaitingResponse(false);
        setIsMessageInputEnabled(true);
        return;
      }

      // 添加用户消息到聊天面板
      setMessages((prev) => [
        ...prev,
        {
          type: "human",
          role: "Human",
          context: combinedInput,
          sendto: "",
          timestamp: new Date().toISOString(),
          isNew: true
        },
      ]);

      // 清空输入框，保持专家前缀（本地化名称）
      if (inputElement) {
        const displayPrefix = `@${getLocalizedDisplayName(selectedExpert)} `;
        inputElement.value = displayPrefix;
        setTimeout(() => {
          inputElement.setSelectionRange?.(displayPrefix.length, displayPrefix.length);
          inputElement.focus?.();
        }, 0);
      }

      // 调用 aidrawio API
      await callAidrawioAPI(diagramInput);
      
      // 重置状态
      setIsProcessing(false);
      setIsWaitingResponse(false);
      setIsMessageInputEnabled(true);
      return; // 绘图模式不走 WebSocket
    }

    // ─────────────────────────────────────────────
    // 文档分析助手分支：完全走 ExpertService REST，不经 CCMA WS。
    // 输出对齐常规 @专家 的形态 —— 对话历史渲染在"文件面板"里（file_id = expert_document_query.final），
    // 左侧聊天面板不再追加 Human/Expert 消息，避免污染训练主对话。
    // 首轮回答末尾由后端追加"不共享上下文，请手动搬运要点"的提醒。
    // ─────────────────────────────────────────────
    if (isInExpertMode && selectedExpert && selectedExpert.comd === "document_query") {
      const expertName = getLocalizedDisplayName(selectedExpert) || '文档分析助手';
      const expertComd = 'document_query';
      const fileID = `expert_${expertComd}.final`;
      const pad = (n) => String(n).padStart(2, '0');
      // 跨天显示 MM-DD HH:MM:SS，当天内显示 HH:MM:SS
      const formatTS = () => {
        const d = new Date();
        const hhmmss = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return d.getTime() >= startOfToday.getTime()
          ? hhmmss
          : `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${hhmmss}`;
      };

      // 每轮一个稳定 sentinel id，后续按精确 marker 定位占位块进行替换，避免位置漂移
      const turnId = `t${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
      const startMarker = `<!-- doc-turn:${turnId}:start -->`;
      const endMarker = `<!-- doc-turn:${turnId}:end -->`;

      const attachedNote = attachedFiles.length > 0
        ? `\n\n> 📎 附带文档：${attachedFiles.map(f => f.name).join('、')}`
        : '';
      const askTS = formatTS();
      const startedAt = Date.now();
      // 生成占位块内容（不含 sentinel 包裹）
      const renderPlaceholder = () => {
        const waited = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
        const slowHint = waited >= 10 ? `\n\n> ⏳ 已等待 ${waited} 秒，LLM 可能较慢，请稍候…` : '';
        const dots = '.'.repeat((waited % 3) + 1);
        return `\n\n---\n\n### 🙋‍♂️ 用户提问（${askTS}）\n\n${combinedInput || '（空提问）'}${attachedNote}\n\n### 🤖 ${expertName} 正在思考${dots}${slowHint}\n\n`;
      };
      const wrap = (body) => `${startMarker}${body}${endMarker}`;

      const upsertDocAnalystFile = (fullTurnContent, { streaming } = { streaming: false }, appendIfNew = true) => {
        setFiles((prevFiles) => {
          const existing = prevFiles[fileID];
          if (existing) {
            // 若文件内已存在该 turn 的 sentinel，按精确区间替换；否则当作新一轮追加
            const s = existing.content.indexOf(startMarker);
            const e = existing.content.indexOf(endMarker);
            let newContent;
            if (s >= 0 && e > s) {
              newContent = existing.content.slice(0, s) + fullTurnContent + existing.content.slice(e + endMarker.length);
            } else if (appendIfNew) {
              newContent = existing.content + fullTurnContent;
            } else {
              return prevFiles;
            }
            return {
              ...prevFiles,
              [fileID]: {
                ...existing,
                content: newContent,
                meta: { ...existing.meta, lastUpdated: Date.now(), isStreaming: streaming },
              },
            };
          }
          const header = `# 💬 与${expertName}专家的对话\n\n> 您与**${expertName}**专家的完整对话历史  \n`;
          const fullContent = header + fullTurnContent;
          return {
            ...prevFiles,
            [fileID]: {
              content: fullContent,
              originalContent: fullContent,
              meta: {
                filename: expertName,
                role: expertName,
                originalFilename: `${expertName}_对话.final`,
                timestamp: Date.now(),
                version: 1,
                expert_cmd: expertComd,
                expert_name: expertName,
                isExpertDialog: true,
                isStreaming: streaming,
              },
            },
          };
        });
        try {
          if (expertFlashTimersRef.current[fileID]) {
            clearTimeout(expertFlashTimersRef.current[fileID]);
          }
          setExpertFlashMap((prev) => ({ ...prev, [fileID]: true }));
          expertFlashTimersRef.current[fileID] = setTimeout(() => {
            setExpertFlashMap((prev) => {
              const next = { ...prev };
              delete next[fileID];
              return next;
            });
            delete expertFlashTimersRef.current[fileID];
          }, 3000);
          // 若用户当前不在此文件上，置未读小红点（切过来时会自动清除）
          if (activeFile && activeFile !== fileID) {
            setExpertUnreadMap((prev) => ({ ...prev, [fileID]: true }));
          }
        } catch (e) {
          console.warn('文档分析助手：设置高亮失败', e);
        }
        if (!activeFile || activeFile === fileID) {
          setActiveFile(fileID);
          if (!fileContentExpanded) {
            setFileContentExpanded(true);
            setChatPanelWidth(38.2);
          }
        }
      };

      // 首次追加占位块（带 sentinel）
      upsertDocAnalystFile(wrap(renderPlaceholder()), { streaming: true }, true);

      // 启动定时器周期性刷新"正在思考 (N 秒)"动态文字
      const tickerId = setInterval(() => {
        upsertDocAnalystFile(wrap(renderPlaceholder()), { streaming: true }, false);
      }, 1000);
      const stopTicker = () => {
        try { clearInterval(tickerId); } catch (_) {}
      };
      // 用最终内容按 sentinel 精确替换占位块
      const finalizeTurn = (finalBody) => {
        stopTicker();
        upsertDocAnalystFile(finalBody, { streaming: false }, true);
      };

      // 清空输入框
      if (inputElement) {
        if (selectedExpert) {
          const displayPrefix = `@${expertName} `;
          inputElement.value = displayPrefix;
          setTimeout(() => {
            inputElement.setSelectionRange?.(displayPrefix.length, displayPrefix.length);
            inputElement.focus?.();
          }, 0);
        } else {
          inputElement.value = "";
        }
      }

      // 🚀 非阻塞：准备好请求体后立刻释放输入/CCMA 通道，
      // 真正的 fetch 在后台 IIFE 里跑；用户可以继续在 CCMA 主对话中发消息。
      // 占位块 + ticker 已经在文件面板里持续提示"正在思考"，
      // 最终结果到达时按 sentinel 精确替换，不会干扰用户当前正在做的事。
      const form = new FormData();
      form.append('user', currentUser?.username || userId || 'anonymous');
      form.append('query', combinedInput || '请基于已上传文档给我一份简要分析。');
      if (docAnalystConvIdRef.current) form.append('conversation_id', docAnalystConvIdRef.current);
      if (projectId) form.append('project_id', String(projectId));
      if (currentUser?.user_type) form.append('user_type', currentUser.user_type);
      if (currentUser?.unified_user_id) form.append('unified_user_id', String(currentUser.unified_user_id));
      const filesSnapshot = attachedFiles.slice();
      filesSnapshot.forEach(f => form.append('files', f));

      // 立即清理附件、释放输入 —— 不等 LLM
      setAttachedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsProcessing(false);
      setIsWaitingResponse(false);
      setIsMessageInputEnabled(true);

      (async () => {
        try {
          const resp = await fetch(`${EXPERT_SERVICE_URL}/v1/expert/document/chat`, {
            method: 'POST',
            body: form,
          });
          if (!resp.ok) {
            let msg = `HTTP ${resp.status}`;
            try {
              const errJson = await resp.json();
              if (errJson && errJson.message) msg = errJson.message;
            } catch (_) {}
            throw new Error(msg);
          }
          const data = await resp.json();
          if (data && data.conversation_id) {
            docAnalystConvIdRef.current = data.conversation_id;
          }
          let answer = (data && data.answer) || '';
          try {
            const seenKey = docAnalystConvIdRef.current || turnId;
            if (!docAnalystNoticeShownRef.current[seenKey]) {
              const notice = '如果以上分析对你当前的训练任务有用，请自行摘录要点，粘贴到训练主对话里告诉你的导师；文档分析助手和导师不共享上下文。';
              if (answer && answer.indexOf(notice) === -1) {
                answer += `\n\n---\n💡 ${notice}`;
              }
              docAnalystNoticeShownRef.current[seenKey] = true;
            }
          } catch (_) {}

          const replyTS = formatTS();
          const replyBlock = `\n\n---\n\n### 🙋‍♂️ 用户提问（${askTS}）\n\n${combinedInput || '（空提问）'}${attachedNote}\n\n### 🤖 ${expertName} 专家回复（${replyTS}）\n\n${answer}\n\n`;
          finalizeTurn(replyBlock);
        } catch (error) {
          console.error('文档分析助手调用失败:', error);
          const errMsg = (error && error.message) || '文档分析失败';
          const errTS = formatTS();
          const errBlock = `\n\n---\n\n### 🙋‍♂️ 用户提问（${askTS}）\n\n${combinedInput || '（空提问）'}${attachedNote}\n\n### ⚠️ ${expertName} 调用失败（${errTS}）\n\n${errMsg}\n\n`;
          finalizeTurn(errBlock);
        } finally {
          stopTicker();
        }
      })();
      return;
    }

    const message = JSON.stringify({
      type: "humaninput",
      roles: selectedRoles,
      input: combinedInput,
      session_id: sessionId,
      user_id: userId,
      modelType: modelType,
    })
    
    ws.current.send(message)
    console.log("Sending to backend:", message)

    // 添加人类消息到消息数组
    setMessages((prev) => [
      ...prev,
      {
        type: "human",
        role: "Human",
        context: combinedInput,
        sendto: "",
        timestamp: new Date().toISOString(), // 添加时间戳
        isNew: true, // 标记为新消息
      },
    ])

    // 清空输入框和相关状态
    if (inputElement) {
      if (isInExpertMode && currentExpertCommand && selectedExpert) {
        // 专家模式下保持本地化名称前缀
        const displayPrefix = `@${getLocalizedDisplayName(selectedExpert)} `;
        inputElement.value = displayPrefix;
        // 设置光标位置到前缀后面
        setTimeout(() => {
          inputElement.setSelectionRange?.(displayPrefix.length, displayPrefix.length);
          inputElement.focus?.();
        }, 0);
      } else {
        // 普通模式下完全清空
        inputElement.value = "";
      }
    }
    setDiffComments({})
    setSelectedDiffLines({})
    setAdoptedExpertOpinions({})
    setSelectedFileLines({}) // 清空选中的文件行
    setActiveFileContent(null) // 清空活跃文件内容
    // 清空附件
    setAttachedFiles([])
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // 在专家模式下不清空selectedExpert，保持专家选中状态
    if (!isInExpertMode) {
      setSelectedExpert(null)
    }
    
    // 注意：这里不需要重置处理状态，因为在ws.onmessage回调中会处理
  }

  // 处理键盘事件，使用 hook 创建的处理器（必须在 handleHumanInputMessage 之后定义）
  const handleKeyDown = createKeyDownHandler(handleHumanInputMessage, isMessageInputEnabled, isProcessing);

  const handleSlide = (direction) => {
    if (direction === "left") {
      setCurrentAgentIndex((prev) => Math.max(0, prev - 1))
    } else {
      setCurrentAgentIndex((prev) => Math.min(agents.length - 4, prev + 1))
    }
  }


  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null);
    // 可能这里有其他逻辑导致打开了存档列表
  }
  // 恢复最近的存档

  const handleBackToPreviousPhase = async (str = null) => {
    // 🔧 新增：支持增强版回退功能的选择
    const useEnhancedReset = true; // 可以改为false来使用原始逻辑
    
    let previousPhase = ""
    console.log(str)
    if (str == null) {
      previousPhase = prompt("请输入要回滚到的阶段名称：")
    } else {
      const confirmResult = window.confirm(`是否要回滚到${str}阶段？\n\n将执行完全系统重置以确保稳定性。`)
      if (confirmResult) {
        previousPhase = str
      } else {
        return
      }
    }

    if (previousPhase && previousPhase.trim() !== "") {
      // 关闭菜单
      handleMenuClose()
      
      if (useEnhancedReset) {
        // 🔧 使用新的增强版回退逻辑
        console.log('使用增强版回退功能...');
        const success = await performEnhancedBackToPreviousPhase(previousPhase);
        if (!success) {
          console.error('增强版回退失败，回退操作未完成');
        }
      } else {
        // 保留原始回退逻辑作为备选
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          // 重置所有关键状态
          setMessages([])
          setFiles({})
          setActiveFile(null)
          clearProjectStateAndHistory()
          setSelectedRoles([])
          setMicroSceneBubbles([])
          setLearningLog([])

          // 重置文件相关状态
          setFileNameCounts({})

          // 重置diff和评论相关状态
          setSelectedDiffLines({})
          setCurrentDiffMessageIndex(null)
          setDiffComments({})

          // 重置专家和意见相关状态
          setAdoptedExpertOpinions({})
          setSelectedExpert(null)
          setIsInExpertMode(false)
          setCurrentExpertCommand('')

          // 重置项目和交互状态
          setIsLoading(false)
          setIsWaitingResponse(false)
          setIsMessageInputEnabled(true)

          // 重置进度条相关状态
          setShouldFlash(false)

          // 向后端发送回滚请求
          ws.current.send(
            JSON.stringify({
              type: "back_to_previous_phase",
              previous_phase: previousPhase.trim(),
            })
          )

          // 记录用户行为
          logAction("BACK_TO_PREVIOUS_PHASE", {
            previousPhase: previousPhase.trim(),
          })
        } else {
          console.error("WebSocket is not connected")
          alert("无法连接到服务器，请检查网络")
        }
      }
    }
  }

  const handleRecoverArchive = async () => {
    // 检查用户是否已发送消息
    if (hasUserSentMessage && messages.length > 0) {
      showNotification('对话已开始，无法恢复存档', 'warning');
      handleMenuClose();
      return;
    }
    
    // 检查Token余额是否小于10
    if (tokenBalance !== null && tokenBalance < 10) {
      setError({ message: '您的Token已经耗尽！' });
      handleMenuClose();
      return;
    }
    
    // 🔧 新增：支持增强版重置功能的选择
    const useEnhancedReset = false; // 恢复存档暂时保持原逻辑，因为它已经很稳定
    
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // 关闭菜单
      handleMenuClose();
      
      if (useEnhancedReset) {
        // 🔧 使用新的增强版重置逻辑
        console.log('恢复存档：使用增强版重置功能...');
        const resetSuccess = await performCompleteSystemReset();
        if (!resetSuccess) {
          showNotification('系统重置失败，无法恢复存档', 'error');
          return;
        }
        
        // 设置用户已发送消息标志，禁止更改流程
        setHasUserSentMessage(true); 
        console.log("恢复存档开始，设置 hasUserSentMessage 为 true");
        
        // 发送恢复存档请求
        ws.current.send(
          JSON.stringify({
            type: "recover_archive",
            session_id: sessionId,
          })
        );
        
        // 记录用户行为
        logAction("ENHANCED_RECOVER_ARCHIVE", { sessionId });
        showNotification('正在恢复存档，请稍候...', 'info');
        
      } else {
        // 保留原始恢复存档逻辑（已经很稳定）
        // 显示加载状态
        setIsLoading(true);
        setIsWaitingResponse(true);
        
        console.log("正在恢复存档，请稍候...");

        // 发送恢复存档请求
        ws.current.send(
          JSON.stringify({
            type: "recover_archive",
            session_id: sessionId,
          })
        )

        // 记录用户行为
        logAction("RECOVER_ARCHIVE", { sessionId })
      }
    } else {
      console.error("WebSocket is not connected")
      alert("无法连接到服务器，请检查网络")
    }
  }

  const buildArchiveIdentityHeaders = () => {
    const headers = {};
    const studentId = currentUser?.studentID || currentUser?.student_id;
    if (studentId) {
      headers['X-Student-ID'] = String(studentId);
    }
    if (currentUser?.employeeNumber) {
      headers['X-Cas-User-ID'] = encodeURIComponent(currentUser.employeeNumber);
      headers['X-User-Name'] = encodeURIComponent(currentUser.name || currentUser.username || '');
      headers['X-User-Type'] = currentUser.user_type || 'bupt_cas';
    }
    return headers;
  };

  // 请求存档列表：菜单与课程页均走 EduGate HTTP，避免 portal 关闭 CCMA WS 后无法查询。
  const handleRequestArchives = async (filter = 'all', openModal = true) => {
    if (openModal) {
      setIsListArchivesFromMenu(true);
    }
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/archives/summary?filter=${encodeURIComponent(filter)}`,
        { headers: buildArchiveIdentityHeaders() },
      );
      const data = await response.json();
      if (!response.ok || data.code !== 0) {
        throw new Error(data.message || '存档加载失败');
      }
      setArchivesList(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error('获取训练存档错误:', error);
      if (openModal) {
        setIsListArchivesFromMenu(false);
      }
      alert(`存档加载失败：${error.message || '请稍后重试'}`);
    }
  };

  // 从菜单打开存档列表时调用
  const handleListArchives = () => {
    handleRequestArchives('all');
    handleMenuClose();
  };

  // 存档元数据操作与训练会话无关，统一走 EduGate HTTP。
  const handleDeleteArchive = async (archive) => {
    const archiveId = archive?.archiveId;
    if (!archiveId) return;
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/archives/${encodeURIComponent(archiveId)}`, {
        method: 'DELETE',
        headers: buildArchiveIdentityHeaders(),
      });
      const data = await response.json();
      if (!response.ok || data.code !== 0) {
        throw new Error(data.message || '删除存档失败');
      }
      await handleRequestArchives('all', false);
      showNotification('存档已删除', 'success');
    } catch (error) {
      console.error('删除训练存档错误:', error);
      showNotification(`删除存档失败：${error.message || '请稍后重试'}`, 'error');
    }
  };

  // 发送Mermaid修复请求（REST 调 ExpertService，不再走 GoMAown/Dify）
  const requestMermaidFix = async (chartCode, errorMessage, requestId) => {
    console.log("发送Mermaid修复请求:", { chartCode, errorMessage, requestId });
    try {
      const resp = await fetch(`${EXPERT_SERVICE_URL}/v1/expert/mermaid/fix`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: chartCode,
          error: errorMessage || "",
          // 透传用户身份，供 ExpertService 准确记录 token 用量
          user: currentUser?.username || userId || "",
          user_type: currentUser?.user_type || "",
          unified_user_id: currentUser?.unified_user_id ? String(currentUser.unified_user_id) : "",
          session_id: sessionId || "",
        }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status} ${text}`);
      }
      const data = await resp.json();
      const fixed = (data && data.fixed_code) || "";
      if (!fixed) {
        throw new Error("修复服务返回为空");
      }
      window.dispatchEvent(new CustomEvent("mermaidFixSuccess", {
        detail: {
          fixedCode: fixed,
          originalCode: chartCode,
          message: "修复成功",
          requestId,
        },
      }));
    } catch (e) {
      console.error("Mermaid 修复失败:", e);
      window.dispatchEvent(new CustomEvent("mermaidFixError", {
        detail: {
          message: (e && e.message) || "修复失败",
          requestId,
        },
      }));
    }
  };

  const handleWsArchiveMessage = useWsArchiveHandlers({
    handleListArchives,
    setProjectState,
    setResetProgressHistory,
    managedSetTimeout,
    setMessages,
    setFiles,
    setActiveFile,
    clearProjectStateAndHistory,
    setSelectedRoles,
    setMicroSceneBubbles,
    setLearningLog,
    setFileNameCounts,
    setSelectedDiffLines,
    setCurrentDiffMessageIndex,
    setDiffComments,
    setAdoptedExpertOpinions,
    setSelectedExpert,
    setIsInExpertMode,
    setCurrentExpertCommand,
    setIsMessageInputEnabled,
    setShouldFlash,
    setHasUserSentMessage,
    setFlowName,
    ws,
    sessionId,
    isRestoringArchiveRef,
    logAction,
    setIsLoading,
    setIsWaitingResponse,
    setIsRestoringArchive,
    setArchivesList,
    setProjectId,  // 新增：用于恢复存档时设置 projectId
  });

  // 将函数通过window全局暴露给其他组件使用
  window.requestMermaidFix = requestMermaidFix;

  // 发送内容编辑请求
  const requestContentEdit = (selectedText, editInstruction, fileId, selectionRange, requestId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // 从files中获取当前文件的工程上下文信息
      const currentFile = files[fileId];
      const phase = currentFile?.meta?.phase || "";
      const roleContext = currentFile?.meta?.roleContext || "";
      const taskFilename = currentFile?.meta?.taskFilename || "";
      
      console.log("发送内容编辑请求:", { 
        selectedText, editInstruction, fileId, requestId,
        phase, roleContext, taskFilename 
      });
      
      ws.current.send(JSON.stringify({
        type: "edit_content",
        selected_text: selectedText,
        edit_instruction: editInstruction,
        file_id: fileId,
        selection_range: selectionRange,
        request_id: requestId,
        session_id: sessionId,
        // 新增：发送工程上下文信息
        phase: phase,
        role: roleContext,
        task_filename: taskFilename
      }));
    } else {
      console.error("WebSocket连接不可用");
      alert("无法连接到服务器，请检查网络连接");
    }
  };

  // 将函数通过window全局暴露给其他组件使用
  window.requestContentEdit = requestContentEdit;
  
  // 🔑 新增：获取文件元数据的全局函数
  window.getFileMeta = (fileId) => {
    const currentFile = files[fileId];
    return {
      // 文件基本信息
      filename: currentFile?.meta?.filename || "",           // FileMeta.Filename
      role: currentFile?.meta?.roleContext || "",            // 🔑 使用roleContext（后端的实际role标识符）
      // 任务上下文信息
      phase: currentFile?.meta?.phase || "",                 // FileMeta.task.Phase
      roleContext: currentFile?.meta?.roleContext || "",     // FileMeta.task.Role  
      taskFilename: currentFile?.meta?.taskFilename || ""    // FileMeta.task.TaskFilename
    };
  };

  // 与 EduGate Rename 一致：按 Unicode 字符计，最多 100；旧逻辑用 length<=20 拒绝更新，
  // 导致已超长的英文名删字后仍 >20，输入框完全冻住（短中文名不易触发）。
  const MAX_PROJECT_NAME_CHARS = 100;
  const countProjectNameChars = (value) => Array.from(value || '').length;
  const clipProjectName = (value, maxChars = MAX_PROJECT_NAME_CHARS) =>
    Array.from(value || '').slice(0, maxChars).join('');

  const handleEditProjectName = (archive) => {
   setEditingArchive(archive);  // 保存正在编辑的存档
   setNewProjectName(archive.projectName || '');  // 设置当前的项目名称
   setIsEditNameModalOpen(true);  // 打开编辑模态框
  };

  // 修改保存按钮的处理函数
  const handleSaveProjectName = async (e) => {
    e.preventDefault();
    if (!editingArchive) return;

    const projectName = (newProjectName || "未命名项目").trim();
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/archives/${encodeURIComponent(editingArchive.archiveId)}`,
        {
          method: 'PATCH',
          headers: {
            ...buildArchiveIdentityHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectName }),
        },
      );
      const data = await response.json();
      if (!response.ok || data.code !== 0) {
        throw new Error(data.message || '修改存档名称失败');
      }
      setArchivesList((prevList) =>
        prevList.map((archive) =>
          archive.archiveId === editingArchive.archiveId
            ? { ...archive, projectName }
            : archive,
        ),
      );
      setIsEditNameModalOpen(false);
      setEditingArchive(null);
      showNotification('存档名称已更新', 'success');
    } catch (error) {
      console.error('修改训练存档名称错误:', error);
      showNotification(`修改存档名称失败：${error.message || '请稍后重试'}`, 'error');
    }
  };

  // 存档列表始终由 EduGate HTTP 提供；不要在门户视图遗留 CCMA WS 查询。
  useEffect(() => {
    if (isLoggedIn) {
      handleRequestArchives('all', false);
    }
  // 登录身份变更时刷新；handleRequestArchives 会读取最新 currentUser。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentUser?.studentID, currentUser?.employeeNumber]);
  const startSpecificArchiveRecovery = (archive) => {
    const archiveId = archive?.archiveId;
    const flowName = archive?.workflowType || archive?.workflowName;
    if (
      activeView !== 'ccma' ||
      !archiveId ||
      !flowName ||
      !isSessionReady ||
      !ws.current ||
      ws.current.readyState !== WebSocket.OPEN
    ) {
      return false;
    }

    setIsLoading(true);
    setIsWaitingResponse(true);
    setProjectState("");
    setResetProgressHistory(true);
    managedSetTimeout(() => setResetProgressHistory(false), 100);
    setMessages([]);
    setFiles({});
    setActiveFile(null);
    clearProjectStateAndHistory();
    setSelectedRoles([]);
    setMicroSceneBubbles([]);
    setLearningLog([]);
    setFileNameCounts({});
    setSelectedDiffLines({});
    setCurrentDiffMessageIndex(null);
    setDiffComments({});
    setAdoptedExpertOpinions({});
    setSelectedExpert(null);
    setIsMessageInputEnabled(false);
    setShouldFlash(false);
    setHasUserSentMessage(true);
    setIsRestoringArchive(true);
    isRestoringArchiveRef.current = true;

    ws.current.send(JSON.stringify({
      type: "recover_archive_by_id",
      archive_id: archiveId,
      // CCMA 在尚未加载剧本的新会话中需要流程 ID；服务端还会以存档数据复核。
      FlowName: flowName,
      session_id: sessionId,
    }));
    sessionStorage.removeItem(PENDING_ARCHIVE_RECOVERY_KEY);
    logAction("RECOVER_SPECIFIC_ARCHIVE", { archiveId });
    setIsArchivesModalOpen(false);
    return true;
  };

  const handleRecoverSpecificArchive = async (archive) => {
    const archiveId = archive?.archiveId;
    if (!archiveId) return;
    if (hasUserSentMessage && messages.length > 0) {
      showNotification('对话已开始，无法恢复存档', 'warning');
      setIsArchivesModalOpen(false);
      return;
    }
    if (tokenBalance !== null && tokenBalance < 10) {
      setError({ message: '您的Token已经耗尽！' });
      setIsArchivesModalOpen(false);
      return;
    }

    // 页面切换不能依赖后续网络请求的成功。课程恢复属于受控入口，即使综合训练
    // 顶栏被隐藏，也立即进入该视图，再等待恢复目标校验完成。
    if (activeView !== 'ccma') {
      portalLaunchBypassRef.current = 'ccma';
      setIsArchivesModalOpen(false);
      switchView('ccma');
      showNotification('正在打开综合训练并准备恢复存档…', 'info');
    }

    try {
      // 无论入口来自列表还是手工输入，都由 EduGate 复核归属并给出正确流程。
      const response = await fetch(
        `${EDUGATE_API_URL}/api/archives/${encodeURIComponent(archiveId)}/recovery-target`,
        { headers: buildArchiveIdentityHeaders() },
      );
      const data = await response.json();
      if (!response.ok || data.code !== 0 || !data.data?.workflowType) {
        throw new Error(data.message || '无法读取存档恢复信息');
      }
      const recoveryTarget = data.data;
      sessionStorage.setItem(PENDING_ARCHIVE_RECOVERY_KEY, JSON.stringify({
        ...recoveryTarget,
        username: currentUser?.username || '',
      }));
      // 即使 WS 已先完成 session_ready，也要重新触发 effect 读取新的待恢复目标。
      setPendingArchiveRecoveryVersion((version) => version + 1);
    } catch (error) {
      console.error('准备恢复训练存档错误:', error);
      showNotification(`恢复存档失败：${error.message || '请稍后重试'}`, 'error');
    }
  };

  useEffect(() => {
    if (activeView !== 'ccma' || !isSessionReady) return;
    const raw = sessionStorage.getItem(PENDING_ARCHIVE_RECOVERY_KEY);
    if (!raw) return;
    try {
      const pending = JSON.parse(raw);
      if (pending.username && pending.username !== (currentUser?.username || '')) {
        sessionStorage.removeItem(PENDING_ARCHIVE_RECOVERY_KEY);
        return;
      }
      startSpecificArchiveRecovery(pending);
    } catch (error) {
      console.error('读取待恢复存档失败:', error);
      sessionStorage.removeItem(PENDING_ARCHIVE_RECOVERY_KEY);
    }
  // startSpecificArchiveRecovery 使用当前会话状态；仅在会话就绪或视图切换后执行。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, isSessionReady, currentUser?.username, pendingArchiveRecoveryVersion]);

  const handleDeleteAllArchive = () => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // 发送删除所有存档请求
      ws.current.send(
        JSON.stringify({
          type: "delete_all_archive",
          session_id: sessionId,
        })
      )

      // 记录用户行为
      logAction("DELETE_ALL_ARCHIVE", { sessionId })

      // 关闭菜单
      handleMenuClose()

      // 可选：添加用户提示
      alert("已发送删除所有存档的请求")
    } else {
      console.error("WebSocket is not connected")
      alert("无法连接到服务器，请检查网络")
    }
  }

  const handleLogoDoubleClick = () => {
    setIsBubbleSidebarVisible((prev) => !prev)
  }
  


 const handleCloseEditModal = () => {
  setIsEditNameModalOpen(false);
  setEditingArchive(null);
  setNewProjectName('');
 };



  // 综合训练流程仍同步给 WebSocket；专项能力广场智能体已改为按全部已加入课程聚合。
  useEffect(() => {
    console.log(`[训练广场] 已加入课程关联智能体:`, plazaAgents.length, '个', plazaAgents.map(a => a.name));
    
    // 当WebSocket连接已建立且FlowName发生变化时，发送到后端
    if (ws.current && ws.current.readyState === WebSocket.OPEN && sessionId) {
      const flowToSend = pendingFlowSelectRef.current || FlowName;
      if (!flowToSend) return;
      console.log("向后端发送FlowName更新:", flowToSend);
      ws.current.send(JSON.stringify({
        type: "set_flow_type",
        FlowName: flowToSend,
        session_id: sessionId,
        ...getFlowResourceBinding(flowToSend)
      }));
    }
  }, [FlowName, plazaAgents, sessionId, getFlowResourceBinding]);




  useEffect(() => {
    console.log('FlowName 已更新为:', FlowName);
  }, [FlowName]);

  useEffect(() => {
    console.log('FlowName 状态更新:', {
      newValue: FlowName,
      timestamp: new Date().toISOString(),
      source: 'App.js useEffect'
    });
  }, [FlowName]);

  // 添加 useEffect 来监听 archivesList 的变化
  useEffect(() => {
    if (isListArchivesFromMenu && archivesList !== null) {
      setIsArchivesModalOpen(true)
      setIsListArchivesFromMenu(false)  // 重置标记
    }
  }, [archivesList, isListArchivesFromMenu])

  // 在组件加载时检查URL参数
  useEffect(() => {
    captureYbgLocalLogin();
    const params = new URLSearchParams(window.location.search);
    const casLogin = params.get('cas_login');
    
    if (casLogin === 'success') {
      // 从URL获取用户信息
      const username = params.get('username');
      const name = params.get('name');
      const employeeNumber = params.get('employeeNumber');
      const token = params.get('token');
      
      if (username && token) {
        if (getPendingLocalLogin()) {
          completeLocalLogin('bupt_cas', token).then((url) => {
            window.location.replace(url);
          }).catch((error) => {
            console.error(error);
            window.alert(error.message || '本机登录失败');
          });
          return;
        }
        // 🔧 修复：让北邮统一认证用户也走完整的登录流程
        
        // 先关闭现有的WebSocket连接
        if (ws.current) {
          ws.current.close()
        }

        // 清除之前的项目状态和历史记录
        try {
          sessionStorage.removeItem('projectStateHistory');
        } catch (error) {
          console.error('清空状态历史出错:', error);
        }
        setProjectState("");
        
        // 定义异步处理函数
        const handleCasLogin = async () => {
          // 获取CAS用户的student_id
          let studentId = null;
          try {
            studentId = await getCasUserStudentId(
              employeeNumber, 
              name || username,
              'bupt_cas'
            );
          } catch (error) {
            console.error('获取CAS用户student_id失败:', error);
          }
          
          const brandInfo = await fetchStudentBranding(studentId);

          // 保存用户信息
          const userData = {
            username: username,
            name: name || username,
            employeeNumber: employeeNumber,
            class_name: "统一认证用户",
            isSchoolUser: false,
            isCasUser: true,
            unified_user_id: studentId, // ✅ 北邮认证用户直接使用student_id，确保不为 null
            user_type: "bupt_cas", // ✅ 添加用户类型
            organizationId: brandInfo.organizationId,
            branding: brandInfo.branding,
          };
          
          localStorage.setItem('userInfo', JSON.stringify(userData));
          setCurrentUser(userData);
          setIsLoggedIn(true);
          
          // 注意：CAS登录日志已经在EduGate后端自动记录，无需前端额外调用
          
          // 北邮统一认证用户登录成功后进入课程入口面板
           console.log("北邮统一认证用户登录成功，进入课程入口面板");
           setIsFlowTypeDialogOpen(false);
           setActiveTrainingMode("portal");

          // ✅ CAS登录成功后，通知列表会由 useEffect 自动拉取（监听 currentUser 变化）

          // CAS用户登录成功后，延迟3秒获取自评状态和能力画像状态
          console.log("检测到CAS用户，将在3秒后检查自评状态和能力画像状态");
          setTimeout(() => {
            checkSelfAssessmentStatus(userData);
            checkCapabilityProfileStatus(userData);
          }, 3000);

          // 清除URL参数
          window.history.replaceState({}, document.title, '/');
        };
        
        // 调用异步处理函数
        handleCasLogin();
      }
    }

    // 🆕 处理教育智联网CAS登录回调
    const edunetCasLogin = params.get('edunet_cas_login');

    if (edunetCasLogin === 'success') {
      // 从URL获取用户信息
      const username = params.get('username');
      const name = params.get('name');
      const employeeNumber = params.get('employeeNumber');
      const token = params.get('token');
      
      if (username && token) {
        if (getPendingLocalLogin()) {
          completeLocalLogin('edunet_cas', token).then((url) => {
            window.location.replace(url);
          }).catch((error) => {
            console.error(error);
            window.alert(error.message || '本机登录失败');
          });
          return;
        }
        // 先关闭现有的WebSocket连接
        if (ws.current) {
          ws.current.close()
        }

        // 清除之前的项目状态和历史记录
        try {
          sessionStorage.removeItem('projectStateHistory');
        } catch (error) {
          console.error('清空状态历史出错:', error);
        }
        setProjectState("");
        
        // 定义异步处理函数
        const handleEduNetCasLogin = async () => {
          // ✅ 验证必需参数
          if (!employeeNumber || employeeNumber === 'undefined') {
            console.error('❌ employeeNumber 参数无效:', employeeNumber);
            console.log('URL参数:', { username, name, employeeNumber, token });
            alert('登录失败：缺少工号信息');
            return;
          }
          
          // 获取教育智联网CAS用户的student_id
          let studentId = null;
          try {
            studentId = await getCasUserStudentId(
              employeeNumber, 
              name || username, 
              'edunet_cas' // 🆕 用户类型
            );
            console.log('✅ 获取到教育智联网CAS用户student_id:', studentId);
          } catch (error) {
            console.error('获取教育智联网CAS用户student_id失败:', error);
            // 不中止登录，允许用户进入系统，后续功能会再次尝试获取
          }
          
          const brandInfo = await fetchStudentBranding(studentId);

          // 保存用户信息
          const userData = {
            username: username,
            name: name || username, // 真实姓名作为显示名称
            employeeNumber: employeeNumber,
            class_name: "教育智联网统一认证用户",
            isSchoolUser: false,
            isCasUser: true,
            unified_user_id: studentId, // ✅ 确保 studentId 不为 null
            user_type: "edunet_cas", // 🆕 用户类型
            organizationId: brandInfo.organizationId,
            branding: brandInfo.branding,
          };
          
          localStorage.setItem('userInfo', JSON.stringify(userData));
          setCurrentUser(userData);
          setIsLoggedIn(true);
          
          // 注意：登录日志已经在EduGate后端自动记录
          
          // 教育智联网用户登录成功后进入课程入口面板
          console.log("教育智联网统一认证用户登录成功，进入课程入口面板");
          setIsFlowTypeDialogOpen(false);
          setActiveTrainingMode("portal");

          // 延迟3秒获取自评状态和能力画像状态
          console.log("检测到教育智联网CAS用户，将在3秒后检查自评状态和能力画像状态");
          setTimeout(() => {
            checkSelfAssessmentStatus(userData);
            checkCapabilityProfileStatus(userData);
          }, 3000);

          // 清除URL参数
          window.history.replaceState({}, document.title, '/');
        };
        
        // 调用异步处理函数
        handleEduNetCasLogin();
      }
    }

    // 🆕 处理高教社 HEP Authing SSO 登录回调
    const hepAuthingLogin = params.get('hep_authing_login');

    if (hepAuthingLogin === 'success') {
      const sub = params.get('sub');
      const name = params.get('name');
      const school = params.get('school');
      const token = params.get('token');
      const hepIdToken = params.get('id_token') || '';

      if (sub && token) {
        if (getPendingLocalLogin()) {
          completeLocalLogin('hep_authing', token).then((url) => {
            window.location.replace(url);
          }).catch((error) => {
            console.error(error);
            window.alert(error.message || '本机登录失败');
          });
          return;
        }
        if (ws.current) {
          ws.current.close();
        }
        try {
          sessionStorage.removeItem('projectStateHistory');
        } catch (error) {
          console.error('清空状态历史出错:', error);
        }
        setProjectState("");

        const handleHepAuthingLogin = async () => {
          // 获取 student_id（用 sub 作为 employeeNumber，hep_authing 作为 userType）
          let studentId = null;
          try {
            studentId = await getCasUserStudentId(
              sub,
              name || sub,
              'hep_authing'
            );
          } catch (error) {
            console.error('获取 HEP Authing 用户 student_id 失败:', error);
          }

          const brandInfo = await fetchStudentBranding(studentId);

          const userData = {
            username: sub,
            name: name || sub,
            employeeNumber: sub,
            class_name: school || "高教社统一认证用户",
            isSchoolUser: false,
            isCasUser: true,
            unified_user_id: studentId,
            user_type: "hep_authing",
            hep_id_token: hepIdToken,   // 🆕 存储 id_token 供 CCMA 埋点上报使用
            organizationId: brandInfo.organizationId,
            branding: brandInfo.branding,
          };

          localStorage.setItem('userInfo', JSON.stringify(userData));
          setCurrentUser(userData);
          setIsLoggedIn(true);

          console.log("HEP Authing 用户登录成功，进入课程入口面板");
          setIsFlowTypeDialogOpen(false);
          setActiveTrainingMode("portal");

          setTimeout(() => {
            checkSelfAssessmentStatus(userData);
            checkCapabilityProfileStatus(userData);
          }, 3000);

          // 清除URL参数
          window.history.replaceState({}, document.title, '/');
        };

        handleHepAuthingLogin();
      }
    }
  }, []);

  // selectedTraining, fullscreenAgent 已迁移到 useAgentState hook
  // isDrawingFullscreen 已迁移到 useDiagramState hook

  const studentIdCacheRef = useRef({});  // 格式: { [employeeNumber]: { id: xxx, promise: xxx } }

  // CAS用户获取student_id的工具函数（支持用户类型，带并发控制）
  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas', phone = '') => {
    // 1. 如果已经有缓存的结果，直接返回
    if (studentIdCacheRef.current[employeeNumber]?.id) {
      console.log('📦 使用缓存的student_id:', studentIdCacheRef.current[employeeNumber].id);
      return studentIdCacheRef.current[employeeNumber].id;
    }
    
    // 2. 如果已经有正在进行的请求，等待该请求完成
    if (studentIdCacheRef.current[employeeNumber]?.promise) {
      console.log('⏳ 等待正在进行的student_id请求...');
      return studentIdCacheRef.current[employeeNumber].promise;
    }
    
    // 3. 创建新的请求
    const promise = (async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          'X-Cas-User-ID': encodeURIComponent(employeeNumber),
          'X-User-Name': encodeURIComponent(userName),
          'X-User-Type': userType // 🆕 添加用户类型
        };
        
        console.log(`🔍 调用get-student-id API，参数: employeeNumber=${employeeNumber}, userName=${userName}, userType=${userType}`);
        
        const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
          method: 'POST',
          headers: headers
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.code === 0) {
            const studentId = result.data.student_id;
            console.log(`✅ 获取student_id成功: ${studentId}`);
            
            // 4. 缓存结果
            studentIdCacheRef.current[employeeNumber] = { 
              id: studentId, 
              promise: null 
            };
            
            return studentId;
          }
        }
        return null;
      } catch (error) {
        console.error('获取CAS用户student_id失败:', error);
        return null;
      } finally {
        // 5. 请求完成后清除 promise
        if (studentIdCacheRef.current[employeeNumber]) {
          studentIdCacheRef.current[employeeNumber].promise = null;
        }
      }
    })();
    
    // 6. 保存 promise 以供其他并发调用使用
    if (!studentIdCacheRef.current[employeeNumber]) {
      studentIdCacheRef.current[employeeNumber] = {};
    }
    studentIdCacheRef.current[employeeNumber].promise = promise;
    console.log('🚀 创建新的student_id请求');
    
    return promise;
  };

  // 检查自评状态的函数
  const checkSelfAssessmentStatus = async (user) => {
    try {
      console.log('开始检查自评状态:', user);
      
      let studentId;
      
      // 1. 获取学生ID
      if (user.isSchoolUser && user.studentID) {
        // 校园用户直接使用studentID
        studentId = user.studentID;
        console.log('校园用户，直接使用studentID:', studentId);
      } else if (user.employeeNumber) {
        // CAS用户通过API获取
        console.log('CAS用户，通过API获取studentID');
        studentId = await getCasUserStudentId(
          user.employeeNumber, 
          user.name,
          user.user_type || 'bupt_cas'
        );
        console.log('CAS用户获取到学生ID:', studentId);
      }
      
      if (!studentId) {
        console.log('获取学生ID失败，不显示自评图标');
        return;
      }
      
      // 2. 查询自评状态
      const response = await fetch(`${EDUGATE_API_URL}/api/self-assessment/status?student_id=${studentId}`);
      if (!response.ok) {
        console.error('查询自评状态失败:', response.status);
        return;
      }
      
      const result = await response.json();
      if (result.code !== 0) {
        console.error('自评状态查询返回错误:', result.message);
        return;
      }
      
      const status = result.data;
      console.log('自评状态:', status);
      
      // 3. 设置状态
      setSelfAssessmentStatus(status);
      setShowSelfAssessmentIcon(true);
      
      // 4. 判断是否需要闪烁提醒
      // 闪烁条件：
      // 1. 从未提交过自评 (!status.has_assessment)
      // 2. 距离上一次自评超过14天 (status.days_since_last_assessment > 14)
      let shouldBlink = false;
      
      if (!status.has_assessment) {
        // 没有自评数据时闪烁
        shouldBlink = true;
        console.log('没有自评数据，开始闪烁');
      } else if (status.days_since_last_assessment && status.days_since_last_assessment > 14) {
        // 距离上一次自评超过14天时闪烁
        shouldBlink = true;
        console.log('距离上一次自评超过14天，开始闪烁', {
          days_since_last_assessment: status.days_since_last_assessment
        });
      } else {
        console.log('无需提醒，停止闪烁', {
          has_assessment: status.has_assessment,
          days_since_last_assessment: status.days_since_last_assessment
        });
      }
      
      setShouldBlinkIcon(shouldBlink);
      
         } catch (error) {
       console.error('检查自评状态时发生错误:', error);
     }
   };

  // 处理自评提交成功
  const handleSelfAssessmentSubmitSuccess = () => {
    console.log('自评提交成功，重新检查状态');
    showNotification('自评提交成功！', 'success');
    
    // 重新检查自评状态
    if (currentUser && currentUser.employeeNumber) {
      setTimeout(() => {
        checkSelfAssessmentStatus(currentUser);
      }, 1000);
    }
  };

  // 检查能力画像状态的函数
  const checkCapabilityProfileStatus = async (user) => {
    try {
      console.log('开始检查能力画像状态:', user);
      
      let studentId;
      
      // 1. 获取学生ID
      if (user.isSchoolUser && user.studentID) {
        // 校园用户直接使用studentID
        studentId = user.studentID;
        console.log('校园用户，直接使用studentID:', studentId);
      } else if (user.employeeNumber) {
        // CAS用户通过API获取
        console.log('CAS用户，通过API获取studentID');
        studentId = await getCasUserStudentId(
          user.employeeNumber, 
          user.name,
          user.user_type || 'bupt_cas'
        );
        console.log('CAS用户获取到学生ID:', studentId);
      }
      
      if (!studentId) {
        console.log('获取学生ID失败，不显示能力画像图标');
        return;
      }
      
      // 2. 查询能力画像状态
      const response = await fetch(`${EDUGATE_API_URL}/api/capability-profile/status?student_id=${studentId}`);
      if (!response.ok) {
        console.error('查询能力画像状态失败:', response.status);
        return;
      }
      
      const result = await response.json();
      if (result.code !== 0) {
        console.error('能力画像状态查询返回错误:', result.message);
        return;
      }
      
      const hasProfile = result.data.has_profile;
      const level = result.data.level || 'unknown';
      const levelScore = result.data.level_score || 0;
      const levelDetails = result.data.level_details || null;
      
      console.log('能力画像状态:', hasProfile, '等级:', level, '得分:', levelScore);
      
      // 3. 设置状态
      // 无论是否有画像数据，都显示图标
      setShowCapabilityProfileIcon(true);
      setCurrentStudentId(studentId);
      
      if (hasProfile) {
        setCapabilityLevel(level);
        setCapabilityLevelDetails(levelDetails);
        
        // 4. 判断是否需要闪烁提醒
        const isViewed = result.data.is_viewed;
        if (!isViewed) {
          console.log('学生尚未查看过画像数据，开始闪烁提醒');
          setShouldBlinkCapabilityIcon(true);
        } else {
          console.log('学生已查看过画像数据，不需要闪烁');
          setShouldBlinkCapabilityIcon(false);
        }
        
        console.log('显示能力画像图标，studentId:', studentId, '等级:', level, '是否已查看:', isViewed);
      } else {
        setCapabilityLevel('recruit'); // 设置为新兵状态
        setCapabilityLevelDetails(null);
        setShouldBlinkCapabilityIcon(true); // 没有画像数据时也闪烁
        console.log('学生暂无能力画像数据，显示新兵图标并闪烁提醒');
      }
      
    } catch (error) {
      console.error('检查能力画像状态时发生错误:', error);
    }
  };

  // 获取能力画像图标的函数
  const getCapabilityIcon = (level) => {
    switch(level) {
      case 'diamond': return '💎';
      case 'gold': return '🏆';
      case 'silver': return '🥈';
      case 'bronze': return '🥉';
      case 'recruit': return '🎖️';
      default: return '📊';
    }
  };

  // 获取能力画像图标标题的函数
  const getCapabilityIconTitle = (level, levelDetails) => {
    const levelNames = {
      'diamond': '钻石级',
      'gold': '黄金级', 
      'silver': '白银级',
      'bronze': '青铜级',
      'recruit': '工程新兵',
      'unknown': '未评定'
    };
    
    const levelName = levelNames[level] || '未知';
    
    if (level === 'recruit') {
      return '点击了解如何获得工程能力画像';
    }
    
    let title = t('capability_profile_title', { level: levelName });
    
    if (levelDetails && levelDetails.valid_dimensions_count > 0) {
      title += t('capability_profile_dimensions', { count: levelDetails.valid_dimensions_count });
    }
    
    return title;
  };

  // 标记画像为已查看
  const markCapabilityProfileAsViewed = async (studentId) => {
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/capability-profile/mark-viewed?student_id=${studentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      if (result.code === 0) {
        console.log('画像已标记为已查看');
        setShouldBlinkCapabilityIcon(false); // 停止闪烁
        return true;
      } else {
        console.error('标记画像为已查看失败:', result.message);
        return false;
      }
    } catch (error) {
      console.error('标记画像为已查看时发生错误:', error);
      return false;
    }
  };

  // 游戏化API调用函数
  const fetchGameConfig = async () => {
    try {
      console.log('🎮 获取游戏配置...');
      const response = await fetch(`${EDUGATE_API_URL}/api/game/config`);
      const result = await response.json();
      
      if (result.code === 0) {
        console.log('🎮 游戏配置获取成功:', result.data);
        setGameConfig(result.data);
        return result.data;
      } else {
        console.error('🎮 获取游戏配置失败:', result.message);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取游戏配置时发生错误:', error);
      return null;
    }
  };

  // 计算用户当前实际等级
  const getCurrentLevel = () => {
    if (!gameProfile || !gameConfig?.levelThresholds) return 1;
    
    const thresholds = gameConfig.levelThresholds;
    let actualLevel = 1;
    for (let i = 0; i < thresholds.length; i++) {
      if (gameProfile.totalXp >= thresholds[i]) {
        actualLevel = i + 2; // 因为阈值数组是从2级开始的
      } else {
        break;
      }
    }
    return actualLevel;
  };

  const fetchGameProfile = async (studentId) => {
    try {
      console.log('🎮 获取学生游戏档案:', studentId);
      const response = await fetch(`${EDUGATE_API_URL}/api/game/profiles/${studentId}`);
      const result = await response.json();
      
      if (result.code === 0) {
        console.log('🎮 游戏档案获取成功:', result.data);
        setGameProfile(result.data);
        return result.data;
      } else {
        console.error('🎮 获取游戏档案失败:', result.message);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取游戏档案时发生错误:', error);
      return null;
    }
  };

  const fetchGameLeaderboard = async (studentId) => {
    try {
      console.log('🎮 获取游戏排行榜:', studentId);
      const response = await fetch(`${EDUGATE_API_URL}/api/game/leaderboard/${studentId}?limit=10&neighbors=2`);
      const result = await response.json();
      
      if (result.code === 0) {
        console.log('🎮 排行榜获取成功:', result.data);
        setLeaderboardData(result.data);
        return result.data;
      } else {
        console.error('🎮 获取排行榜失败:', result.message);
        setLeaderboardData(null);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取排行榜时发生错误:', error);
      setLeaderboardData(null);
      return null;
    }
  };

  const fetchAchievementWall = async (studentId) => {
    try {
      console.log('🎮 获取成就墙数据:', studentId);
      const url = `${EDUGATE_API_URL}/api/game/achievements/${studentId}`;
      console.log('🎮 请求URL:', url);
      
      const response = await fetch(url);
      console.log('🎮 响应状态:', response.status, response.statusText);
      
      const result = await response.json();
      console.log('🎮 响应数据:', result);
      
      if (result.code === 0) {
        console.log('🎮 成就墙数据获取成功:', result.data);
        setAchievementWallData(result.data);
        return result.data;
      } else {
        console.error('🎮 获取成就墙数据失败:', result.message);
        setAchievementWallData(null);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取成就墙数据时发生错误:', error);
      setAchievementWallData(null);
      return null;
    }
  };

  const fetchProfileHistory = async (studentId) => {
    try {
      console.log('🎮 获取成长历史数据:', studentId);
      const url = `${EDUGATE_API_URL}/api/game/profiles/${studentId}/history`;
      console.log('🎮 请求URL:', url);
      
      const response = await fetch(url);
      console.log('🎮 响应状态:', response.status, response.statusText);
      
      const result = await response.json();
      console.log('🎮 响应数据:', result);
      
      if (result.code === 0) {
        console.log('🎮 成长历史数据获取成功:', result.data);
        setProfileHistoryData(result.data);
        return result.data;
      } else {
        console.error('🎮 获取成长历史数据失败:', result.message);
        setProfileHistoryData(null);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取成长历史数据时发生错误:', error);
      setProfileHistoryData(null);
      return null;
    }
  };

  const fetchSkillHistory = async (studentId, skills = [], days = 30) => {
    try {
      console.log('🎮 获取能力历史数据:', studentId, '能力:', skills, '天数:', days);
      const params = new URLSearchParams();
      if (skills.length > 0) {
        params.append('skills', skills.join(','));
      }
      params.append('days', days.toString());
      const url = `${EDUGATE_API_URL}/api/game/profiles/${studentId}/history?${params.toString()}`;
      console.log('🎮 请求URL:', url);
      
      const response = await fetch(url);
      console.log('🎮 响应状态:', response.status, response.statusText);
      
      const result = await response.json();
      console.log('🎮 响应数据:', result);
      
      if (result.code === 0) {
        console.log('🎮 能力历史数据获取成功:', result.data);
        setSkillHistoryData(result.data);
        return result.data;
      } else {
        console.error('🎮 获取能力历史数据失败:', result.message);
        setSkillHistoryData(null);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取能力历史数据时发生错误:', error);
      setSkillHistoryData(null);
      return null;
    }
  };

  // 获取成长地图数据
  const fetchGrowthMap = async (studentId) => {
    try {
      console.log('🎮 获取成长地图数据:', studentId);
      const response = await fetch(`${EDUGATE_API_URL}/api/game/growth-map/${studentId}`);
      console.log('🎮 响应状态:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('🎮 响应数据:', result);
      
      if (result.code === 0) {
        console.log('🎮 成长地图数据获取成功:', result.data);
        setGrowthMapData(result.data);
        return result.data;
      } else {
        console.error('🎮 成长地图数据获取失败:', result.message);
        setGrowthMapData(null);
        return null;
      }
    } catch (error) {
      console.error('🎮 获取成长地图数据时发生错误:', error);
      setGrowthMapData(null);
      return null;
    }
  };

  // 修复点击处理功能，确保正确调用状态更新
  // 在 App 组件中的状态声明之后添加一个日志函数
  const handleAgentClick = (agent) => {
    console.log("点击智能体:", agent.name);
    setFullscreenAgent(agent);
  };

// 在 App 组件内部，靠近其他事件处理函数的地方，添加这个新函数：
const handleAgentIdentifierClick = (agentName) => {
  console.log("===== App.js: handleAgentIdentifierClick 被调用 =====");
  console.log("传入的 agentName:", agentName);
  console.log("当前 inputRef 状态:", inputRef.current ? "已设置" : "未设置");
  
  // 检查 agentName 是否有效 (例如，非空、非 "Human")，以及 inputRef 是否已准备好
  if (!agentName || agentName === 'Human' || !inputRef.current) {
    console.log("无效的 agentName 或 inputRef 未准备好:", agentName);
    return;
  }

  const agentPrefix = `&${agentName} `; // 使用 & 前缀，并确保末尾有空格
  let currentValue = inputRef.current.value; // 直接从 DOM 获取当前值，以应对非控或混合模式
  console.log("当前输入框值:", currentValue);

  // 如果当前输入框已有 @ 或 & 开头的内容，先移除它们
  currentValue = currentValue.replace(/^[&@][^\s]+ /, '');
  console.log("清理后的输入框值:", currentValue);

  // 构造新值，将前缀添加到当前内容之前
  const newValue = agentPrefix + currentValue;

  // --- 更新状态和 DOM ---
  try {
    // 1. 更新 React 状态 humanInput
    setHumanInput(newValue);
    console.log("React 状态已更新");

    // 2. 显式更新 DOM 的 value 属性。
    //    即使 textarea 的 value 绑定到 humanInput state，
    //    这样做可以确保在存在其他可能直接操作 DOM 的逻辑时，
    //    界面能立即、可靠地反映出变化。
    inputRef.current.value = newValue;
    console.log("DOM 已直接更新");

    // 3. 将焦点设置回输入框，并将光标移到插入的前缀之后
    inputRef.current.focus();
    try {
      const caretPosition = agentPrefix.length;
      inputRef.current.setSelectionRange(caretPosition, caretPosition);
      console.log("光标位置已设置到", caretPosition);
    } catch (e) {
      console.error("设置光标位置失败:", e);
      // 即使设置光标失败，也要确保焦点设置成功
      inputRef.current.focus();
    }

    console.log(`单击成功: ${agentName}, 输入框内容更新为: ${newValue}`);
  } catch (error) {
    console.error("处理智能体点击时出错:", error);
  }
};
// --- 函数定义结束 ---

  // ✅ 密码处理逻辑 - 使用 hook
  const {
    handlePasswordChange,
    handlePasswordSubmit,
    handleModelTypeChange,
    handleAdvancedPasswordSubmit,
    handleAdvancedPasswordCancel,
  } = usePasswordHandlers({
    currentUser,
    passwordForm,
    setPasswordForm,
    setPasswordDialogOpen,
    modelType,
    setModelType,
    isAdvancedUnlocked,
    setIsAdvancedUnlocked,
    advancedPassword,
    setAdvancedPassword,
    setShowAdvancedPasswordDialog,
    ADVANCED_MODE_CONFIG,
  });

  // 确保访客用户不能使用DeepSeek
  useEffect(() => {
    if (currentUser && currentUser.user_type === "guest" && modelType === "deepseek") {
      console.log("访客用户重置为Basic");
      setModelType("noReasoning");
    }
  }, [currentUser, modelType]);

  // 删除重复的滚动逻辑，保留上面那个更合理的实现
// 修改 isNew 标记更新逻辑，避免不必要的数组重建
useEffect(() => {
  if (messages.length > 0) {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg.isNew) return; // 如果最后一条消息已经不是新消息，不需要设置定时器

    const timer = setTimeout(() => {
      setMessages(prevMessages => {
        // 检查是否需要更新
        const hasNewMessages = prevMessages.some(msg => msg.isNew);
        if (!hasNewMessages) return prevMessages; // 如果没有新消息，返回原数组，避免触发重渲染
        
        return prevMessages.map(msg => ({
          ...msg,
          isNew: false
        }));
      });
    }, 5000);
    
    return () => clearTimeout(timer);
  }
}, [messages]);

  // 文档分析助手专用：清空当前对话文件内容、重置 conversation_id，开启新一段分析会话
  const resetDocAnalystSession = useCallback((fileId) => {
    try {
      const ok = window.confirm('新建一段文档分析会话？\n\n当前对话历史将从面板清空，ExpertService 也会开启一个新的 conversation_id。');
      if (!ok) return;
    } catch (_) {}
    docAnalystConvIdRef.current = null;
    docAnalystNoticeShownRef.current = {};
    setFiles((prevFiles) => {
      const existing = prevFiles[fileId];
      if (!existing) return prevFiles;
      const expertName = existing.meta?.expert_name || '文档分析助手';
      const header = `# 💬 与${expertName}专家的对话\n\n> 您与**${expertName}**专家的完整对话历史  \n`;
      return {
        ...prevFiles,
        [fileId]: {
          ...existing,
          content: header,
          originalContent: header,
          meta: { ...existing.meta, lastUpdated: Date.now(), isStreaming: false },
        },
      };
    });
  }, [setFiles]);

  const getCCMAExpertCmd = useCallback(() => {
    const fileData = filesRef.current[ccOverlayFileId] || filesRef.current[activeFile];
    return fileData?.meta?.expert_cmd || "cc_default";
  }, [activeFile, ccOverlayFileId]);

  const handleCCMASendMessage = useCallback((text) => {
    setCCMessages((prev) => [...prev, {
      id: `cc-user-${Date.now()}`,
      type: "user_input",
      content: text,
      timestamp: Date.now(),
    }]);
    ccStreamingTextRef.current = "";
    setCCStreamingText("");
    setCCThinkingText("");
    setCCSessionState("running");
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: "cc_message",
        source: "ccma",
        content: text,
        expert_cmd: getCCMAExpertCmd(),
      }));
    }
  }, [ccStreamingTextRef, getCCMAExpertCmd, setCCMessages, setCCSessionState, setCCStreamingText, setCCThinkingText]);

  const handleCCMAInterrupt = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: "cc_control", source: "ccma", payload: {
        type: "control_request",
        request_id: `interrupt-${Date.now()}`,
        request: { subtype: "interrupt" },
      }}));
    }
  }, []);

  const handleCCMAQuestionRespond = useCallback((toolUseId, answers) => {
    setCCPendingQuestion(null);
    setCCSessionState("running");
    const summary = Array.isArray(answers)
      ? answers.map(a => a.answer).join(" / ")
      : answers;
    setCCMessages((prev) => [...prev, {
      id: `cc-user-${Date.now()}`,
      type: "user_input",
      content: `[选择] ${summary}`,
      timestamp: Date.now(),
    }]);
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: "cc_control",
        source: "ccma",
        payload: {
          type: "ask_user_question_response",
          tool_use_id: toolUseId,
          answers: Array.isArray(answers) ? answers : [{ question: "", answer: answers }],
        },
      }));
    }
  }, [setCCMessages, setCCPendingQuestion, setCCSessionState]);

  const handleCCMASendControl = useCallback((payload) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: "cc_control", source: "ccma", payload }));
    }
  }, []);

  const handleCCMACompact = useCallback(() => {
    if (ccSessionState !== "running") {
      setCCMessages((prev) => [...prev, {
        id: `cc-user-${Date.now()}`,
        type: "user_input",
        content: "/compact",
        timestamp: Date.now(),
      }]);
      setCCSessionState("running");
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({
          type: "cc_message",
          source: "ccma",
          content: "/compact",
          expert_cmd: getCCMAExpertCmd(),
        }));
      }
    }
  }, [ccSessionState, getCCMAExpertCmd, setCCMessages, setCCSessionState]);

  const handleCCMAClearHistory = useCallback(() => {
    if (ccSessionState === "running") return;
    if (!window.confirm("对话将归档到本机 chatlogs 目录，导出时可选包含。确定清空当前面板并重置学生机模型上下文吗？")) return;
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: "cc_clear_history", source: "ccma" }));
    }
  }, [ccSessionState]);

  const waitYouLaberExportLinks = useCallback((ms = 30000) => {
    return new Promise((resolve) => {
      const found = [];
      const timer = setTimeout(() => {
        chatlogExportWaiterRef.current = null;
        resolve(found);
      }, ms);
      chatlogExportWaiterRef.current = (cc) => {
        const path = String(cc.path || "").replace(/\\/g, "/");
        if (!/chatlog\.md$/i.test(path)) return;
        if (found.some((x) => String(x.path || "").replace(/\\/g, "/") === path)) return;
        found.push({ ...cc, path });
        if (/\.md$/i.test(path)) {
          clearTimeout(timer);
          chatlogExportWaiterRef.current = null;
          resolve(found);
        }
      };
    });
  }, []);

  const buildYouLaberChatlogMeta = useCallback(() => ({
    student_id: currentUser?.username || currentUser?.unified_user_id || "",
    device_type: ytSelectedDevice?.device_type || "",
    device_id: ytSelectedDevice?.device_id || "",
    device_name: ytSelectedDevice?.device_name || "",
    cwd: "",
  }), [currentUser, ytSelectedDevice]);

  const downloadWorkspaceFile = useCallback(async (path, filename) => {
    const deviceId = ytSelectedDevice?.device_id;
    if (!deviceId || !path) return null;
    // 与编辑器同一条 workspace 代理，勿用 /api/claw/files（生产 CCMA 前缀下会 404）
    const rel = String(path).replace(/\\/g, "/");
    const url = `${String(CCMA_API_URL).replace(/\/$/, "")}/api/claw/workspace/${encodeURIComponent(deviceId)}/file?path=${encodeURIComponent(rel)}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `下载失败 HTTP ${res.status}`);
    const text = typeof data.content === "string" ? data.content : "";
    const blob = new Blob([text], { type: "application/octet-stream" });
    const fileUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = filename || path.split("/").pop();
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(fileUrl);
    return blob;
  }, [ytSelectedDevice]);

  const handleYouLaberChatlogExport = useCallback(async () => {
    if (ytSessionState === "running") return;
    const includeArchives = window.confirm("是否包含历次清空后的归档对话？\n确定=含归档，取消=仅当前段");
    let links = [];
    let usedBridge = false;
    if (ws.current?.readyState === WebSocket.OPEN && ytSelectedDevice) {
      const pending = waitYouLaberExportLinks(30000);
      ws.current.send(JSON.stringify({
        type: "cc_chatlog_export",
        source: "youlaber",
        include_archives: includeArchives,
      }));
      links = await pending;
      usedBridge = links.some((l) => /chatlog\.md$/i.test(String(l.path || "").replace(/\\/g, "/")));
    }
    if (usedBridge) {
      try {
        for (const link of links) {
          const rel = String(link.path || "").replace(/\\/g, "/");
          if (!/chatlog\.md$/i.test(rel)) continue;
          await downloadWorkspaceFile(rel, link.name || "chatlog.md");
        }
        return;
      } catch (err) {
        console.warn("bridge 导出下载失败，改用内存版", err);
      }
    }
    if (ytSelectedDevice) {
      window.alert("本机 YouLabber 未返回导出文件（可能需要升级客户端），已改用当前面板内存版。");
    }
    downloadText("chatlog.md", toMarkdown(ytMessages, buildYouLaberChatlogMeta()), "text/markdown");
  }, [
    ytSessionState,
    ytSelectedDevice,
    ytMessages,
    waitYouLaberExportLinks,
    buildYouLaberChatlogMeta,
    downloadWorkspaceFile,
  ]);

  const handleCCDialogActivated = useCallback((fileId) => {
    if (!fileId) return;
    setActiveFile(fileId);
    if (!fileContentExpanded) {
      setFileContentExpanded(true);
      setChatPanelWidth(38.2);
    }
    setIsDrawingFullscreen(false);
    setIsDiscussOpen(false);
    setIsGroupChatOpen(false);
    setActiveTrainingMode('ccma');
    setCCOverlayFileId(fileId);
    setCCOverlayHasUnread(false);
    setCCOverlayOpen(true);
  }, [
    fileContentExpanded,
    setActiveFile,
    setActiveTrainingMode,
    setChatPanelWidth,
    setFileContentExpanded,
    setIsDiscussOpen,
    setIsDrawingFullscreen,
    setIsGroupChatOpen,
  ]);

  // 修改renderFileCard函数，添加一个简化参数
  const renderFileCard = (fileId, simplified = false) => {
    // 🔑 关键修复：使用 filesRef.current 获取最新的 files 状态
    const fileData = filesRef.current[fileId];
    if (!fileData) {
      console.warn(`File data not found for: ${fileId}`);
      return null;
    }
    
    // 从元数据获取角色、原始文件名和版本号
    const role = fileData?.meta?.role || '';
    const originalFilename = fileData?.meta?.filename || fileId;
    const version = fileData?.meta?.version || 1;
    const isExpertDialog = fileData?.meta?.isExpertDialog;
    const isDiagram = fileData?.meta?.isDiagram; // 🎨 获取绘图标记
    const isFinal = fileData?.meta?.isFinal || false; // 🔑 获取定稿标记
    
    // 对于专家对话文件：优先显示专家名（区分多个专家 tab），为空时再回退到通用"对话"标签
    let displayRole = isExpertDialog
      ? (fileData?.meta?.expert_name || t('expert_dialog_label'))
      : role;
    // 🎨 如果是绘图文件且没有角色，显示"绘图助手"
    if (isDiagram && !role) {
      displayRole = '绘图助手';
    }
    
    const displayFilename = isExpertDialog ? originalFilename : originalFilename;
    
    // 根据角色选择合适的样式类
    let roleTagClass = fileStyles.roleTag;
    if (isExpertDialog) {
      // 专家对话文件特殊样式
      roleTagClass = `${fileStyles.roleTag} ${fileStyles.expertDialogTag}`;
    } else if (isDiagram) {
      // 🎨 绘图助手特殊样式
      roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagEngineer}`; // 复用工程师样式，或者定义新的
    } else if (role) {
      const lowerRole = role.toLowerCase();
      
      // 工程师/开发者
      if (lowerRole.includes('代码') || lowerRole.includes('编写') || lowerRole.includes('开发') || 
          lowerRole.includes('工程师') || lowerRole.includes('engineer') || lowerRole.includes('code')) {
        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagEngineer}`;
      } 
      // 测试/QA
      else if (lowerRole.includes('测试') || lowerRole.includes('test') || lowerRole.includes('qa')) {
        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagQA}`;
      } 
      // 硬件相关
      else if (lowerRole.includes('硬件') || lowerRole.includes('hardware')) {
        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagHardware}`;
      }
    }
    
    // 获取文件类型
    const fileExt = originalFilename.split('.').pop().toLowerCase();
    let fileTypeClass = fileStyles.fileCard;
    
    // 🔑 添加定稿文件样式
    if (isFinal) {
      fileTypeClass += ` ${fileStyles.fileFinal}`;
    }

    // 🔧 专家对话文件：高亮5秒后恢复低调
    if (isExpertDialog) {
      const isFlashing = !!expertFlashMap[fileId];
      fileTypeClass += ` ${fileStyles.fileExpert}`;
      if (isFlashing) {
        fileTypeClass += ` ${fileStyles.fileExpertFlash}`;
      }
    }
    
    if (fileExt === 'js' || fileExt === 'jsx') {
      fileTypeClass += ` ${fileStyles.fileTypeJs}`;
    } else if (fileExt === 'json') {
      fileTypeClass += ` ${fileStyles.fileTypeJson}`;
    } else if (fileExt === 'md') {
      fileTypeClass += ` ${fileStyles.fileTypeMd}`;
    } else if (fileExt === 'html' || fileExt === 'htm') {
      fileTypeClass += ` ${fileStyles.fileTypeHtml}`;
    } else if (fileExt === 'css') {
      fileTypeClass += ` ${fileStyles.fileTypeCss}`;
    }
    
    // 计算创建时间
    const timestamp = fileData?.meta?.timestamp || Date.now();
    const date = new Date(timestamp);
    const now = new Date();
    let timeStr = '';
    
    // 格式化时间显示
    if (date.toDateString() === now.toDateString()) {
      // 今天 - 显示时间
      timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      // 其他日期 - 显示月日
      timeStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    
    // 用于标题的完整信息
    const fileTitle = `${role ? role + ': ' : ''}${originalFilename} (版本 ${version}, ${date.toLocaleString()})${isFinal ? ' [已定稿]' : ''}`;
    
    // YouLaber 文件：渲染两个入口（实验记录 + 实时对话）
    const isClawDialog = fileData?.meta?.isClawDialog;
    if (isClawDialog) {
      const expertName = fileData?.meta?.expert_name || 'YouLabber 实验员';
      const isActive = activeFile === fileId;
      const isLiveOpen = ccOverlayOpen && ccOverlayFileId === fileId;

      const handleOpenArchive = () => {
        setFiles(prev => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            meta: { ...prev[fileId].meta, isCCDevice: false },
          },
        }));
        handleFileCardClick(fileId);
      };

      const handleOpenLive = () => {
        setFiles(prev => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            meta: { ...prev[fileId].meta, isCCDevice: false },
          },
        }));
        handleFileCardClick(fileId);
        handleCCDialogActivated(fileId);
      };

      const baseCardStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: '100%',
        padding: '5px 8px',
        background: 'none',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        textAlign: 'left',
        color: '#c9d1d9',
      };
      const activeStyle = { background: 'rgba(88,166,255,0.12)', color: '#58a6ff' };

      return (
        <div key={fileId} style={{ display: 'flex', flexDirection: 'column', gap: '2px', margin: '2px 0' }}>
          {/* 方块1：实验记录 */}
          <button
            style={{ ...baseCardStyle, ...(isActive && !isLiveOpen ? activeStyle : {}) }}
            onClick={handleOpenArchive}
            title={`${expertName} · 实验记录`}
          >
            <span style={{ fontSize: '13px', flexShrink: 0 }}>🔬</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {expertName}
            </span>
            <span style={{ fontSize: '10px', color: '#8b949e', flexShrink: 0 }}>记录</span>
          </button>
          {/* 方块2：实时对话（CCPanel） */}
          <button
            style={{ ...baseCardStyle, ...(isLiveOpen ? activeStyle : {}) }}
            onClick={handleOpenLive}
            title={`${expertName} · 实时对话`}
          >
            <span style={{ fontSize: '13px', flexShrink: 0 }}>💻</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {expertName}
            </span>
            {ccOverlayHasUnread && ccOverlayFileId === fileId && (
              <span
                title="实时对话有新消息"
                style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#f85149', flexShrink: 0 }}
              />
            )}
            {ccSessionState === "running" && (
              <span
                title="YouLabber 正在运行"
                style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3fb950', flexShrink: 0 }}
              />
            )}
            <span style={{ fontSize: '10px', color: '#8b949e', flexShrink: 0 }}>实时</span>
          </button>
        </div>
      );
    }

    // 如果是简化版本（用于展开的文件组中），只显示角色和版本信息
    if (simplified) {
      return (
        <button
          key={fileId}
          className={`${fileStyles.fileCardSimplified} ${activeFile === fileId ? fileStyles.fileCardActive : ''} ${isFinal ? fileStyles.fileFinal : ''} ${isExpertDialog ? (fileStyles.fileExpert + (expertFlashMap[fileId] ? ' ' + fileStyles.fileExpertFlash : '')) : ''}`}
          onClick={() => handleFileCardClick(fileId)}
          onDoubleClick={isExpertDialog ? () => handleExpertFileDoubleClick(fileId) : undefined}
          title={isExpertDialog ? `${fileTitle}\n\n${t('double_click_hint', { name: fileData?.meta?.expert_name || t('expert') })}` : fileTitle}
        >
          <div className={fileStyles.fileTypeIndicator}></div>
          <div className={fileStyles.fileCardContent}>
            <span className={fileStyles.fileTypeIcon}>
              {getFileIcon(originalFilename, role, isExpertDialog)}
            </span>
            <span className={fileStyles.roleTag + ' ' + roleTagClass}>
              {displayRole}
              {isFinal && <span className={fileStyles.finalBadge} title={t('finalized_badge')}>📋</span>}
            </span>
            {isExpertDialog && expertUnreadMap[fileId] && <span className={fileStyles.unreadDot} title={t('has_new_message') || '有新消息'} />}
            {isExpertDialog && fileData?.meta?.expert_cmd === 'document_query' && (
              <span
                role="button"
                tabIndex={0}
                className={fileStyles.newSessionBtn}
                title="新建文档分析会话（清空当前对话并重置上下文）"
                onClick={(ev) => { ev.stopPropagation(); resetDocAnalystSession(fileId); }}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); resetDocAnalystSession(fileId); } }}
              >
                🔄
              </span>
            )}
            <span className={fileStyles.versionTag}>v{version}</span>
            <span className={fileStyles.timeTag}>{timeStr}</span>
          </div>
        </button>
      );
    }
    
    // 完整版本（用于单个文件或文件组标题）
    return (
      <button
        key={fileId}
        className={`${fileTypeClass} ${activeFile === fileId ? fileStyles.fileCardActive : ''}`}
        onClick={() => handleFileCardClick(fileId)}
        onDoubleClick={isExpertDialog ? () => handleExpertFileDoubleClick(fileId) : undefined}
        title={isExpertDialog ? `${fileTitle}\n\n💡 双击可快速填入"@${fileData?.meta?.expert_name || '专家'}"到输入框` : fileTitle}
      >
        <div className={fileStyles.fileTypeIndicator}></div>
        <div className={fileStyles.fileCardHeader}>
          {displayRole && <span className={roleTagClass}>{displayRole}</span>}
          {isExpertDialog && expertUnreadMap[fileId] && <span className={fileStyles.unreadDot} title={t('has_new_message') || '有新消息'} />}
          {isExpertDialog && fileData?.meta?.expert_cmd === 'document_query' && (
            <span
              role="button"
              tabIndex={0}
              className={fileStyles.newSessionBtn}
              title="新建文档分析会话（清空当前对话并重置上下文）"
              onClick={(ev) => { ev.stopPropagation(); resetDocAnalystSession(fileId); }}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); resetDocAnalystSession(fileId); } }}
            >
              🔄
            </span>
          )}
          <span className={fileStyles.versionTag}>v{version}</span>
        </div>
        <div className={fileStyles.fileCardContent}>
          <span className={fileStyles.fileTypeIcon}>
            {getFileIcon(originalFilename, role, isExpertDialog)}
          </span>
          <span className={fileStyles.fileName}>
            {displayFilename}
            {isFinal && <span className={fileStyles.finalBadge} title={t('finalized_badge')}>📋</span>}
          </span>
          <span className={fileStyles.versionTag}>{timeStr}</span>
        </div>
      </button>
    );
  };
  
  // 切换文件组展开状态
  const toggleGroupExpanded = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleResizeDoubleClick = () => {
    // 获取文件列表中最长的文件名长度
    const maxFileNameLength = Object.keys(files).reduce((max, fileName) => {
      const displayName = files[fileName]?.meta?.filename || fileName;
      return Math.max(max, displayName.length);
    }, 0);

    // 计算最佳宽度（每个字符约 8px + 边距和图标空间）
    const optimalWidth = Math.min(
      Math.max(maxFileNameLength * 8 + 80, 220), // 最小 220px，每个字符 8px + 80px 边距和图标
      window.innerWidth * 0.4 // 最大为窗口宽度的 40%
    );

    // 应用新宽度，带有平滑动画
    const fileTabsContainer = document.querySelector(`.${fileStyles.fileTabsContainer}`);
    if (fileTabsContainer) {
      fileTabsContainer.style.width = `${optimalWidth}px`;
      fileTabsContainer.style.transition = 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // 动画结束后移除过渡效果
      setTimeout(() => {
        fileTabsContainer.style.transition = '';
      }, 300);
    }
  };

  // 在现有的拖拽相关代码中添加双击处理
  const initResize = () => {
    // 确保容器元素存在
    const container = document.querySelector(`.${fileStyles.fileTabsContainer}`);
    if (!container) {
      console.warn('File tabs container not found');
      return () => {}; // 返回空清理函数
    }

    const resizeHandle = document.createElement('div');
    resizeHandle.className = `${fileStyles.resizeHandle} ${fileStyles.dblClickable}`;
    
    let isResizing = false;
    let startX;
    let startWidth;
    
    const onMouseDown = (e) => {
      isResizing = true;
      startX = e.clientX;
      startWidth = container.offsetWidth;
      
      document.body.classList.add(fileStyles.dragging);
      resizeHandle.classList.add(fileStyles.resizeHandleActive);
    };
    
    const onMouseMove = (e) => {
      if (!isResizing) return;
      
      const width = startWidth + (e.clientX - startX);
      const minWidth = 160; // 最小宽度
      const maxWidth = window.innerWidth * 0.6; // 最大宽度为窗口的 60%
      
      const newWidth = Math.min(Math.max(width, minWidth), maxWidth);
      container.style.width = `${newWidth}px`;
    };
    
    const onMouseUp = () => {
      isResizing = false;
      document.body.classList.remove(fileStyles.dragging);
      resizeHandle.classList.remove(fileStyles.resizeHandleActive);
    };
    
    // 添加双击事件处理
    // 🚀 使用 passive: true 提升拖拽性能
    resizeHandle.addEventListener('dblclick', handleResizeDoubleClick, { passive: true });
    
    resizeHandle.addEventListener('mousedown', onMouseDown, { passive: true });
    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseup', onMouseUp, { passive: true });
    
    // 添加到 DOM
    container.appendChild(resizeHandle);
    
    // 清理函数
    return () => {
      if (resizeHandle && resizeHandle.parentNode) {
        resizeHandle.removeEventListener('mousedown', onMouseDown);
        resizeHandle.removeEventListener('dblclick', handleResizeDoubleClick);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        resizeHandle.remove();
      }
    };
  };

  // 在组件挂载时初始化，并在文件面板可见时重新初始化
  useEffect(() => {
    let cleanup;
    // 使用 requestAnimationFrame 确保 DOM 已经渲染
    const initTimer = requestAnimationFrame(() => {
      cleanup = initResize();
    });

    return () => {
      cancelAnimationFrame(initTimer);
      if (cleanup) {
        cleanup();
      }
    };
  }, []); // 仅在组件挂载时执行

  // fileContentExpanded 已迁移到 useFileManagementState hook

  // handleWsExpertDialogUpdate moved below setChatPanelWidth initialization

  // chatPanelWidth 已迁移到 useUIState hook

  const handleWsExpertDialogUpdate = useWsExpertDialogHandler({
    setFiles,
    setExpertFlashMap,
    setExpertUnreadMap,
    expertFlashTimersRef,
    isRestoringArchiveRef,
    activeFile,
    setActiveFile,
    fileContentExpanded,
    setFileContentExpanded,
    setChatPanelWidth,
    digitalHumanGlobalEnabledRef,
    isExpertServiceAvailableRef,
    currentInitializingExpertRef,
    classModeRef,
    setCurrentFileExpertText,
    setCurrentFileExpertName,
    setFileExpertDigitalHumanOpen,
    onCCDialogActivated: handleCCDialogActivated,
  });

  const handleWsCCMessageCCMA = useWsCCHandler({
    setCCMessages,
    setCCSessionState,
    setCCStatus,
    setCCPendingPermission,
    setCCPendingQuestion,
    setCCTaskInfo,
    setCCInitInfo,
    setCCTokenUsage,
    setCCStreamingText,
    ccStreamingTextRef,
    setCCThinkingText,
    setCCFileTree,
    setCCRoster,
  });

  // YouLaber tab 的独立 CC 消息消费者（绑到 yt* setters）
  const handleWsCCMessageYL = useWsCCHandler({
    setCCMessages: setYTMessages,
    setCCSessionState: setYTSessionState,
    setCCStatus: setYTStatus,
    setCCPendingPermission: setYTPendingPermission,
    setCCPendingQuestion: setYTPendingQuestion,
    setCCTaskInfo: setYTTaskInfo,
    setCCInitInfo: setYTInitInfo,
    setCCTokenUsage: setYTTokenUsage,
    setCCStreamingText: setYTStreamingText,
    ccStreamingTextRef: ytStreamingTextRef,
    setCCThinkingText: setYTThinkingText,
    setCCFileTree: setYTFileTree,
    setCCRoster: setYTRoster,
  });

  // 按 source 路由 cc_ndjson_stream：
  //   data.source === "youlaber" → YouLaber tab 实例
  //   其它（"ccma"/缺省）         → 综合训练实例
  const handleWsCCMessage = useCallback((data) => {
    if (!["cc_ndjson_stream", "cc_history_loaded", "cc_history_cleared"].includes(data?.type)) return false;
    if (data.type === "cc_ndjson_stream" && data.ndjson && chatlogExportWaiterRef.current) {
      try {
        const cc = JSON.parse(data.ndjson);
        if (cc.type === "workspace_file_link") {
          chatlogExportWaiterRef.current(cc);
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (data.source === "youlaber") return handleWsCCMessageYL(data);
    return handleWsCCMessageCCMA(data);
  }, [handleWsCCMessageCCMA, handleWsCCMessageYL]);

  useEffect(() => {
    if (!ccSelectedDevice || ws.current?.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify({ type: "cc_history_request", source: "ccma" }));
  }, [ccSelectedDevice]);

  useEffect(() => {
    if (!ytSelectedDevice || ws.current?.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify({ type: "cc_history_request", source: "youlaber" }));
  }, [ytSelectedDevice]);

  useEffect(() => {
    if (connectionStatus !== "connected" || ws.current?.readyState !== WebSocket.OPEN) return;
    if (ccSelectedDevice) {
      ws.current.send(JSON.stringify({ type: "cc_history_request", source: "ccma" }));
    }
    if (ytSelectedDevice) {
      ws.current.send(JSON.stringify({ type: "cc_history_request", source: "youlaber" }));
    }
  }, [connectionStatus, ccSelectedDevice, ytSelectedDevice]);

  // isMainDragging, isChatPanelVisible 已迁移到 useUIState hook

  const {
    handleCloseFile,
    handleExpandFileContent,
    handleFileCardClick,
    handleExpertFileDoubleClick,
    toggleChatPanel,
    handleMainDragStart,
  } = useFilePanelHandlers({
    fileContentExpanded,
    setFileContentExpanded,
    setActiveFile,
    setActiveFileContent,
    setChatPanelWidth,
    filesRef,
    inputRef,
    setSelectedExpert,
    setIsInExpertMode,
    setCurrentExpertCommand,
    isMainDragging,
    setIsMainDragging,
    setIsChatPanelVisible,
    fileStyles,
  });

  // welcomeMessage 已迁移到 useMiscState hook

  const handleWsMessageHandlers = useWsMessageHandlers({
    messages,
    setMessages,
    setError,
    sessionId,
    setIsMessageInputEnabled,
    setIsWaitingResponse,
    setIsLoading,
    showNotification,
    setIsProcessing,
    setExperts,
    processExpertsData,
    expertSearchTerm,
    selectedExpertCategory,
    parseExpertCategory,
    getExpertCategories,
    setExpertDialogContent,
    setIsExpertDialogReady,
    setAgents,
    setWelcomeMessage,
    parseMicroSceneData,
    setMicroSceneBubbles,
    isRestoringArchiveRef,
    digitalHumanGlobalEnabledRef,
    isExpertServiceAvailableRef,
    classModeRef,
    setCurrentExpertName,
    setCurrentSpeechText,
    setDigitalHumanOpen,
    currentInitializingExpertRef,
    setCurrentFileExpertName,
    setCurrentFileExpertText,
    setFileExpertDigitalHumanOpen,
    setLastMessageType,
    setProjectState,
    logAction,
    handleNewNotification,
    inputRef,
  });

  const handleWsMessageDispatch = useWsMessageDispatcher({
    handleWsCommonMessage,
    handleWsTaskSelection,
    handleWsExpertDialogUpdate,
    handleWsCCMessage,
    handleWsArchiveMessage,
    handleWsEditorEvents,
    handleWsRoleStatus,
    handleWsMessageHandlers,
    handleWsGameProfileUpdate,
    handleWsTodayYouCard,
    handleWsActionAnswer,
    handleWsPostMessageEffects,
    setShouldFlash,
  });
  wsMessageDispatchRef.current = handleWsMessageDispatch;

  // 渲染消息内容（放在 showNotification 之后，避免提前引用）
  const renderMessageContent = useCallback((msg, index, options = EMPTY_OPTIONS) => {
    const renderCopyButton = (content) => (
      <MessageCopyButton
        content={content}
        msg={msg}
        t={t}
        copyToClipboard={copyToClipboard}
      />
    );

    return (
      <MessageRenderer
        msg={msg}
        index={index}
        options={options}
        renderCopyButton={renderCopyButton}
        shouldCollapseByValue={shouldCollapseByValue}
        styles={styles}
        convertExpertCommandsToDisplayNames={convertExpertCommandsToDisplayNames}
        adoptedExpertOpinions={adoptedExpertOpinions}
        handleOpinionAdoption={handleOpinionAdoption}
        selectedDiffLines={selectedDiffLines}
        handleDiffLineClick={handleDiffLineClick}
        diffComments={diffComments}
        handleCommentChange={handleCommentChange}
        handleExpertCommandSelect={handleExpertCommandSelect}
        websocket={ws.current}
        preprocessContent={preprocessContent}
        expertDialogContent={expertDialogContent}
        experts={experts}
        handleExpertSelect={handleExpertSelect}
        showNotification={showNotification}
        inputRef={inputRef}
        setHumanInput={setHumanInput}
      />
    );
  }, [
    adoptedExpertOpinions,
    convertExpertCommandsToDisplayNames,
    copyToClipboard,
    diffComments,
    expertDialogContent,
    experts,
    handleCommentChange,
    handleDiffLineClick,
    handleExpertCommandSelect,
    handleExpertSelect,
    handleOpinionAdoption,
    inputRef,
    preprocessContent,
    selectedDiffLines,
    setHumanInput,
    showNotification,
    styles
  ]);

  // 添加处理流程类型选择的函数
  const handleFlowTypeSelect = (flowType) => {
    if (!flowType) return;
    if (hasUserSentMessage) {
      setIsFlowTypeDialogOpen(false);
      return;
    }
    rememberLaunchedFlow(flowType);
    setFlowName(flowType);
    pendingFlowSelectRef.current = flowType;
    if (ws.current && ws.current.readyState === WebSocket.OPEN && sessionId) {
      console.log(`设置流程类型为: ${flowType}`);
      pendingFlowSelectRef.current = null;
      ws.current.send(JSON.stringify({
        type: "set_flow_type",
        FlowName: flowType,
        session_id: sessionId,
        ...getFlowResourceBinding(flowType)
      }));
      
      // 清空已选择的角色
      setSelectedRoles([]);
      // 触发进度条历史重置
      setResetProgressHistory(true);
      managedSetTimeout(() => setResetProgressHistory(false), 100);
      
      // 显示通知提醒用户需要重新选择角色
      showNotification(t('flow_type_changed_reselect_role'), 'info');
      
      setIsFlowTypeDialogOpen(false); // 关闭对话框
    } else {
      setIsFlowTypeDialogOpen(false);
    }
  };

  // 作业入口等到 CCMA 会话就绪后再绑定流程，避免固定 150ms 时 WS 尚未 OPEN
  useEffect(() => {
    if (activeView !== 'ccma' || !isSessionReady) return;
    if (hasUserSentMessage) {
      pendingFlowSelectRef.current = null;
      return;
    }
    const flowType = pendingFlowSelectRef.current;
    if (!flowType) return;
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    pendingFlowSelectRef.current = null;
    handleFlowTypeSelect(flowType);
  // handleFlowTypeSelect 每轮渲染都会重建；用 ref 保证只执行一次。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, isSessionReady, connectionStatus, hasUserSentMessage]);

  const resumeOrLaunchCcma = (flowName, displayName) => {
    portalLaunchBypassRef.current = 'ccma';
    switchView('ccma');
    if (hasUserSentMessage) {
      if (flowName && FlowName && flowName !== FlowName) {
        showNotification('当前综合训练进行中，已返回正在进行的对话。', 'info');
      }
      return;
    }
    if (!flowName) return;
    rememberLaunchedFlow(flowName, displayName);
    setFlowName(flowName);
    pendingFlowSelectRef.current = flowName;
  };
  
  // 添加打开流程类型对话框的函数
  const openFlowTypeDialog = () => {
    // 如果用户已发送消息，显示提示，不打开对话框
    if (hasUserSentMessage) {
      showNotification(t('flow_type_cannot_change_after_chat'), 'warning');
      return;
    }
    setIsFlowTypeDialogOpen(true);
  };

  // 🎨 绘图助手按钮点击处理
  const handleDiagramClick = () => {
    // 检查 archiveId 是否存在
    if (!archiveId) {
      showNotification('请先开始训练后再使用绘图功能', 'warning');
      return;
    }

    // 检查是否已经在绘图助手专家模式下
    if (isInExpertMode && selectedExpert && selectedExpert.comd === "diagram_draw") {
      // 已经在绘图助手模式下，不做任何操作
      return;
    }
    
    // 创建虚拟的绘图助手专家对象
    const diagramAssistantExpert = {
      comd: "diagram_draw",
      display_name: "绘图助手",
      description: "AI驱动的图表创建助手，支持流程图、架构图、思维导图等"
    };
    
    // 复用专家选择逻辑
    handleExpertSelect(diagramAssistantExpert);

    // 创建绘图文件卡片（如果还没有）
    if (!diagramFileId) {
      const newFileId = `diagram_${Date.now()}`;
      setDiagramFileId(newFileId);
      
      // 添加绘图文件到文件列表
      setFiles(prev => ({
        ...prev,
        [newFileId]: {
          id: newFileId,
          name: '绘图画布.drawio',
          content: '',
          meta: {
            isDiagram: true,
            senderInfo: 'diagram_draw',
            filename: '绘图画布.drawio',  // 🎨 添加文件名以便正确显示图标
            timestamp: Date.now(),         // 🎨 添加时间戳
            version: 1                     // 🎨 添加版本号
          }
        }
      }));
      
      // 切换到绘图文件
      setActiveFile(newFileId);
    } else {
      // 已有绘图文件，直接切换
      setActiveFile(diagramFileId);
    }
  };

  // 🎤 初始化 FunASR 语音识别（自建服务）
  useEffect(() => {
    const recognition = new FunASRClient();

    recognition.onstart = () => {
      setIsVoiceRecording(true);
      setIsVoiceProcessing(false);
      console.log('🎤 FunASR 语音识别开始');
    };

    recognition.onend = () => {
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
      console.log('🎤 FunASR 语音识别结束');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      console.log('📝 FunASR 识别结果:', transcript);
      setIsVoiceProcessing(false);
      
      // 追加到输入框（不覆盖已有内容）
      const textArea = document.querySelector('textarea[name="humanInput"]');
      if (textArea) {
        const currentValue = textArea.value || '';
        // 在已有内容后追加，用空格分隔
        const newValue = currentValue ? `${currentValue} ${transcript}` : transcript;
        
        // 使用原生 setter 设置值
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLTextAreaElement.prototype, "value"
        ).set;
        nativeInputValueSetter.call(textArea, newValue);
        
        // 触发 input 事件让 React 状态同步
        const inputEvent = new Event('input', { bubbles: true });
        textArea.dispatchEvent(inputEvent);
        
        // 聚焦输入框
        textArea.focus();
      }
    };

    recognition.onerror = (event) => {
      console.error('❌ FunASR 语音识别错误:', event.error);
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
    };

    voiceRecognitionRef.current = recognition;
  }, []);

  // 🎤 麦克风按钮点击处理
  const handleVoiceClick = () => {
    if (!voiceRecognitionRef.current) {
      alert('抱歉，您的浏览器不支持语音识别功能');
      return;
    }

    if (isVoiceRecording) {
      // 停止录音
      voiceRecognitionRef.current.stop();
      setIsVoiceProcessing(true);
    } else {
      // 开始录音
      try {
        voiceRecognitionRef.current.start();
      } catch (error) {
        console.error('启动录音失败:', error);
      }
    }
  };

  // 🎨 调用 aidrawio API 生成图表
  const callAidrawioAPI = async (userInput) => {
    if (!archiveId) {
      showNotification('请先开始训练后再使用绘图功能', 'warning');
      return;
    }

    // 额度预检查：Token余额不足时阻止调用
    if (tokenBalance !== null && tokenBalance < 10) {
      showNotification('Token额度已耗尽，无法使用绘图功能', 'error');
      return;
    }

    setIsDiagramStreaming(true);
    
    // 先添加用户消息到历史
    setDiagramMessages(prev => [
      ...prev,
      { id: `user_${Date.now()}`, role: 'user', content: userInput }
    ]);

    // 🎨 获取用户信息（用于 token 使用量记录）
    let userId = 'guest';
    let userType = 'guest';
    
    if (currentUser) {
      if (currentUser.isSchoolUser) {
        userId = currentUser.unified_user_id || currentUser.username;
        userType = 'campus';
      } else if (currentUser.isCasUser) {
        userId = currentUser.unified_user_id || currentUser.employeeNumber;
        userType = 'bupt_cas';
      } else {
        userId = currentUser.unified_user_id || `guest_${currentUser.username}`;
        userType = 'guest';
      }
    }

    try {
      const response = await fetch(`${AIDRAWIO_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [
            ...diagramMessages.map(msg => ({
              id: msg.id || `msg_${Date.now()}`,
              role: msg.role,
              parts: [{ type: 'text', text: msg.content }]
            })),
            {
              id: `msg_${Date.now()}`,
              role: 'user',
              parts: [{ type: 'text', text: userInput }]
            }
          ],
          xml: diagramXML || '',
          archiveId: archiveId,
          sessionId: sessionId,           // ✅ 传入会话ID
          unifiedUserId: userId,          // ✅ 传入用户ID
          userType: userType              // ✅ 传入用户类型
        })
      });

      if (!response.ok) {
        throw new Error(`API 请求失败: ${response.status}`);
      }

      // 处理流式响应 - Vercel AI SDK UI Message Stream 格式
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fullResponse += decoder.decode(value, { stream: true });
      }

      console.log('🎨 完整响应长度:', fullResponse.length);
      console.log('🎨 响应预览:', fullResponse.substring(0, 500));

      // 解析响应，提取文字消息和工具调用
      let extractedXml = '';
      let textContent = '';
      let hasToolCall = false;
      let toolName = '';
      let edits = null; // 用于 edit_diagram 的编辑操作
      
      // 解析流式响应中的各个事件
      const lines = fullResponse.split('\n');
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const jsonStr = line.substring(6);
        if (jsonStr === '[DONE]') continue;
        
        try {
          const event = JSON.parse(jsonStr);
          
          // 提取文字消息
          if (event.type === 'text-delta' && event.delta) {
            textContent += event.delta;
          }
          
          // 检测工具调用开始
          if (event.type === 'tool-input-start' && 
              (event.toolName === 'display_diagram' || event.toolName === 'edit_diagram')) {
            hasToolCall = true;
            toolName = event.toolName;
          }
          
          // 提取工具调用的完整输入
          if (event.type === 'tool-input-available') {
            toolName = event.toolName;
            if (event.toolName === 'display_diagram' && event.input?.xml) {
              extractedXml = event.input.xml;
              console.log('🎨 从 tool-input-available 提取 display_diagram XML');
            } else if (event.toolName === 'edit_diagram' && event.input?.edits) {
              edits = event.input.edits;
              console.log('🎨 从 tool-input-available 提取 edit_diagram edits:', edits.length, '个编辑');
            }
          }
        } catch (e) {
          // 忽略解析错误
        }
      }
      
      // 如果没有从 tool-input-available 提取到，尝试从原始响应提取
      if (!extractedXml && !edits) {
        // 方法1: 尝试从 JSON 格式中提取 XML
        const xmlMatch = fullResponse.match(/"xml"\s*:\s*"((?:[^"\\]|\\.)*)"/);
        if (xmlMatch) {
          // 解码转义字符
          extractedXml = xmlMatch[1]
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, '\\')
            .replace(/\\u003c/g, '<')
            .replace(/\\u003e/g, '>');
          console.log('🎨 从 JSON 正则提取 XML, 长度:', extractedXml.length);
        }
        
        // 方法2: 尝试提取 edits 数组
        if (!extractedXml) {
          const editsMatch = fullResponse.match(/"edits"\s*:\s*(\[[\s\S]*?\])\s*\}/);
          if (editsMatch) {
            try {
              // 解码转义字符后解析
              const editsStr = editsMatch[1]
                .replace(/\\u003c/g, '<')
                .replace(/\\u003e/g, '>');
              edits = JSON.parse(editsStr);
              toolName = 'edit_diagram';
              console.log('🎨 从 JSON 正则提取 edits:', edits.length, '个编辑');
            } catch (e) {
              console.error('🎨 解析 edits 失败:', e);
            }
          }
        }
      }
      
      // 🎨 处理 edit_diagram 工具调用 - 应用编辑到当前 XML
      if (toolName === 'edit_diagram' && edits && edits.length > 0 && diagramXML) {
        console.log('🎨 处理 edit_diagram，当前 XML 长度:', diagramXML.length);
        try {
          let editedXml = diagramXML;
          
          // 应用每个编辑操作（简单的字符串替换）
          for (const edit of edits) {
            if (edit.search && edit.replace) {
              // 尝试直接替换
              if (editedXml.includes(edit.search)) {
                editedXml = editedXml.replace(edit.search, edit.replace);
                console.log('🎨 成功应用编辑');
              } else {
                // 尝试去除空格后匹配
                const searchTrimmed = edit.search.trim();
                const replaceTrimmed = edit.replace.trim();
                if (editedXml.includes(searchTrimmed)) {
                  editedXml = editedXml.replace(searchTrimmed, replaceTrimmed);
                  console.log('🎨 使用 trimmed 匹配成功应用编辑');
                } else {
                  console.warn('🎨 未找到匹配的搜索内容:', edit.search.substring(0, 100));
                }
              }
            }
          }
          
          extractedXml = editedXml;
          console.log('🎨 edit_diagram 完成，新 XML 长度:', extractedXml.length);
        } catch (error) {
          console.error('🎨 edit_diagram 处理失败:', error);
          showNotification('图表编辑失败: ' + error.message, 'error');
        }
      }

      // 构建 AI 回复消息
      // 确定消息内容：优先使用文本内容，否则根据工具类型显示默认消息
      let messageContent = '';
      if (textContent) {
        messageContent = textContent;
      } else if (extractedXml) {
        messageContent = (toolName === 'edit_diagram') ? '图表已更新' : '图表已生成';
      }
      
      const assistantMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: messageContent,
        xml: null,
        hasToolCall: hasToolCall,
        toolName: toolName
      };

      // 更新图表（如果有 XML）
      if (extractedXml && extractedXml.includes('<mxCell')) {
        // 🎨 借鉴 aidrawio 的 convertToLegalXml 函数 - 只保留完整的 mxCell 标签
        const convertToLegalXml = (xmlString) => {
          const regex = /<mxCell\b[^>]*(?:\/>|>([\s\S]*?)<\/mxCell>)/g;
          let match;
          let result = "<root>\n";
          while ((match = regex.exec(xmlString)) !== null) {
            const formatted = match[0].split('\n').map(line => "    " + line.trim()).join('\n');
            result += formatted + "\n";
          }
          result += "</root>";
          return result;
        };

        // 🎨 借鉴 aidrawio 的 validateMxCellStructure 函数 - 验证 XML 结构
        const validateMxCellStructure = (xml) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xml, "text/xml");
          
          // 检查 XML 解析错误
          const parseError = doc.querySelector('parsererror');
          if (parseError) {
            return `Invalid XML: 语法错误，请检查特殊字符是否正确转义`;
          }
          
          const allCells = doc.querySelectorAll('mxCell');
          const cellIds = new Set();
          const duplicateIds = [];
          const nestedCells = [];
          
          allCells.forEach(cell => {
            const id = cell.getAttribute('id');
            
            // 检查重复 ID
            if (id) {
              if (cellIds.has(id)) {
                duplicateIds.push(id);
              } else {
                cellIds.add(id);
              }
            }
            
            // 检查嵌套的 mxCell
            if (cell.parentElement?.tagName === 'mxCell') {
              nestedCells.push(id || 'unknown');
            }
          });
          
          if (nestedCells.length > 0) {
            return `Invalid XML: 发现嵌套的 mxCell 元素 (IDs: ${nestedCells.slice(0, 3).join(', ')})`;
          }
          
          if (duplicateIds.length > 0) {
            return `Invalid XML: 发现重复的 cell ID (${duplicateIds.slice(0, 3).join(', ')})`;
          }
          
          return null; // 验证通过
        };

        // 🎨 使用 DOMParser 进行健壮的 XML 处理（借鉴 aidrawio 的 replaceNodes 逻辑）
        let finalXml = '';
        
        try {
          // 1️⃣ 先用 convertToLegalXml 清理不完整的 XML
          const cleanedXml = convertToLegalXml(extractedXml);
          console.log('🎨 清理后的 XML:', cleanedXml.substring(0, 200));
          
          const parser = new DOMParser();
          const serializer = new XMLSerializer();
          
          // 解析清理后的 XML（已经包含 <root> 标签）
          const doc = parser.parseFromString(cleanedXml, "text/xml");
          
          // 检查解析错误
          const parseError = doc.querySelector('parsererror');
          if (parseError) {
            console.error('🎨 XML 解析错误:', parseError.textContent);
            throw new Error('XML 解析失败');
          }
          
          // 查找 root 元素（可能在不同层级）
          let rootElement = doc.querySelector('root') || 
                           doc.querySelector('mxGraphModel > root') ||
                           doc.querySelector('diagram > mxGraphModel > root');
          
          // 如果没有 root，尝试从 mxGraphModel 或直接获取 mxCell
          if (!rootElement) {
            const mxGraphModel = doc.querySelector('mxGraphModel');
            if (mxGraphModel) {
              rootElement = mxGraphModel.querySelector('root');
            }
          }
          
          // 收集所有 mxCell 元素
          let allCells = [];
          if (rootElement) {
            allCells = Array.from(rootElement.querySelectorAll('mxCell'));
          } else {
            // 直接从文档中获取所有 mxCell
            allCells = Array.from(doc.querySelectorAll('mxCell'));
          }
          
          // 检查是否已有 id="0" 和 id="1"
          const hasCell0 = allCells.some(cell => cell.getAttribute('id') === '0');
          const hasCell1 = allCells.some(cell => cell.getAttribute('id') === '1');
          
          // 构建新的 XML 结构
          const newDoc = parser.parseFromString(
            '<mxfile host="Embed"><diagram name="Page-1" id="page-1"><mxGraphModel><root></root></mxGraphModel></diagram></mxfile>',
            "text/xml"
          );
          const newRoot = newDoc.querySelector('root');
          
          // 添加基础 cell（如果不存在）
          if (!hasCell0) {
            const cell0 = newDoc.createElement('mxCell');
            cell0.setAttribute('id', '0');
            newRoot.appendChild(cell0);
          }
          
          if (!hasCell1) {
            const cell1 = newDoc.createElement('mxCell');
            cell1.setAttribute('id', '1');
            cell1.setAttribute('parent', '0');
            newRoot.appendChild(cell1);
          }
          
          // 复制所有 mxCell 到新文档（排除可能重复的 id="0" 和 id="1"，如果我们已经添加了）
          allCells.forEach(cell => {
            const id = cell.getAttribute('id');
            // 如果我们已经添加了 id="0" 或 id="1"，跳过原始的
            if ((id === '0' && !hasCell0) || (id === '1' && !hasCell1)) {
              return; // 跳过，因为我们已经添加了
            }
            const importedCell = newDoc.importNode(cell, true);
            newRoot.appendChild(importedCell);
          });
          
          finalXml = serializer.serializeToString(newDoc);
          
          // 3️⃣ 验证最终的 XML 结构
          const validationError = validateMxCellStructure(finalXml);
          if (validationError) {
            console.error('🎨 XML 验证失败:', validationError);
            // 继续使用，但记录警告
          }
          
        } catch (parseError) {
          console.error('🎨 XML 处理失败，使用备用方案:', parseError);
          
          // 备用方案：简单字符串处理
          let innerContent = extractedXml;
          
          // 移除外层标签
          if (innerContent.includes('<mxGraphModel')) {
            const match = innerContent.match(/<mxGraphModel[^>]*>([\s\S]*)<\/mxGraphModel>/);
            if (match) innerContent = match[1];
          }
          if (innerContent.includes('<root>')) {
            const match = innerContent.match(/<root>([\s\S]*)<\/root>/);
            if (match) innerContent = match[1];
          }
          
          // 清理标签
          innerContent = innerContent.replace(/<\/?root>/g, '')
                             .replace(/<\/?mxGraphModel[^>]*>/g, '')
                             .replace(/<\/?mxfile[^>]*>/g, '')
                             .replace(/<\/?diagram[^>]*>/g, '');
          
          // 移除已有的基础 cell
          innerContent = innerContent.replace(/<mxCell\s+id="0"[^>]*\/>/g, '');
          innerContent = innerContent.replace(/<mxCell\s+id="1"[^>]*\/>/g, '');
          
          finalXml = `<mxfile host="Embed"><diagram name="Page-1" id="page-1"><mxGraphModel><root><mxCell id="0"/><mxCell id="1" parent="0"/>${innerContent}</root></mxGraphModel></diagram></mxfile>`;
        }
        
        console.log('🎨 最终 XML 长度:', finalXml.length);
        console.log('🎨 XML 预览:', finalXml.substring(0, 300));
        
        setDiagramXML(finalXml);
        assistantMessage.xml = finalXml;
        
        // 更新文件内容（包含 XML 和消息历史）
        if (diagramFileId) {
          setFiles(prev => ({
            ...prev,
            [diagramFileId]: {
              ...prev[diagramFileId],
              content: finalXml,
              messages: [...(prev[diagramFileId]?.messages || []), assistantMessage]
            }
          }));
        }
        
        showNotification(toolName === 'edit_diagram' ? '图表更新成功' : '图表生成成功', 'success');
      } else if (textContent) {
        // 只有文字消息，没有图表
        console.log('🎨 AI 文字回复:', textContent);
        
        // 更新文件的消息历史
        if (diagramFileId) {
          setFiles(prev => ({
            ...prev,
            [diagramFileId]: {
              ...prev[diagramFileId],
              messages: [...(prev[diagramFileId]?.messages || []), assistantMessage]
            }
          }));
        }
      } else {
        console.warn('🎨 未能从响应中提取有效内容');
        console.log('🎨 响应内容:', fullResponse);
      }
      
      // 添加 AI 回复到消息历史
      setDiagramMessages(prev => [...prev, assistantMessage]);
      
    } catch (error) {
      console.error('调用 aidrawio API 失败:', error);
      showNotification(`绘图失败: ${error.message}`, 'error');
      
      // 添加错误消息
      setDiagramMessages(prev => [
        ...prev,
        { id: `error_${Date.now()}`, role: 'assistant', content: `绘图失败: ${error.message}`, isError: true }
      ]);
    } finally {
      setIsDiagramStreaming(false);
    }
  };

  // 文档导入相关函数
  const handlePaperclipClick = () => {
    // 检查是否已经在文档分析助手专家模式下
    if (isInExpertMode && selectedExpert && selectedExpert.comd === "document_query") {
      // 已经在文档分析助手模式下，直接打开文件选择对话框
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    // 从其他模式进入"文档分析助手"：开启新的一段文档会话，重置 ExpertService 侧的 conversation_id
    docAnalystConvIdRef.current = null;
    docAnalystNoticeShownRef.current = {};

    // 创建虚拟的文档分析助手专家对象
    const documentAnalysisExpert = {
      comd: "document_query",
      display_name: "文档分析助手",
      description: "专业的文档分析和内容理解助手"
    };
    
    // 复用专家选择逻辑
    handleExpertSelect(documentAnalysisExpert);
    
    // 然后打开文件选择对话框
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ✅ handleFileChange, handleRemoveAttachment, uploadFilesToBackend 已迁移到 useFileUploadHandlers hook

  const fileContentRef = useRef(null); // 1. 创建ref
  const fileListRef = useRef(null); // 文件列表容器ref

  // 自动滚动到文件内容底部（专家对话或流式生成中的文件）
  // 智能策略：仅在用户当前视口"贴近底部"（<80px）时才自动跟随，否则保留滚动位置，
  // 避免用户往回翻看历史时被新 chunk 踢回底部。切换文件时照旧滚到底。
  const prevActiveFileRef = useRef(activeFile);
  useEffect(() => {
    const el = fileContentRef.current;
    if (!el) return;
    const fileMeta = files[activeFile]?.meta;
    if (!(fileMeta?.isExpertDialog || fileMeta?.isStreaming)) return;

    const isFileSwitch = prevActiveFileRef.current !== activeFile;
    prevActiveFileRef.current = activeFile;

    const { scrollHeight, clientHeight, scrollTop } = el;
    const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
    // 文件切换时强制滚到底；内容增量更新时仅在接近底部时跟随
    if (isFileSwitch || distanceFromBottom <= 80) {
      el.scrollTop = scrollHeight - clientHeight;
    }
  }, [files[activeFile]?.content, activeFile]);

  // 切到某专家对话文件时清掉它的未读红点
  useEffect(() => {
    if (!activeFile) return;
    if (expertUnreadMap[activeFile]) {
      setExpertUnreadMap((prev) => {
        if (!prev[activeFile]) return prev;
        const next = { ...prev };
        delete next[activeFile];
        return next;
      });
    }
  }, [activeFile, expertUnreadMap, setExpertUnreadMap]);

  // 自动滚动文件列表到底部，当有新文件添加时
  useEffect(() => {
    if (fileListRef.current) {
      // 使用managedSetTimeout确保DOM已更新
      managedSetTimeout(() => {
        const { scrollHeight, clientHeight } = fileListRef.current;
        if (scrollHeight > clientHeight) {
          fileListRef.current.scrollTop = scrollHeight - clientHeight;
          console.log("文件列表滚动到底部，显示最新文件");
        }
      }, 100); // 短暂延迟确保DOM更新完成
    }
  }, [Object.keys(files).length]); // 依赖项：当文件数量变化时触发（只在新增文件时滚动）



  // 获取用户Token余额
  const fetchTokenBalance = async (user) => {
    if (!user) {
      console.log("无法获取Token余额：缺少用户信息");
      return;
    }
    
    // 根据用户类型确定统一用户ID
    let userId;
    let userType;
    
    if (user.isSchoolUser) {
      // 校园用户
      userId = user.unified_user_id || user.username;
      userType = 'campus';
      console.log('校园用户，使用ID:', userId);
    } else if (user.isCasUser) {
      // CAS用户 - 重要：使用unified_user_id而不是employeeNumber
      // CAS用户的unified_user_id在登录时已经通过getCasUserStudentId获取并设置
      userId = user.unified_user_id || user.employeeNumber;
      // ✅ 修正：从用户数据中读取 user_type，而不是硬编码
      userType = user.user_type || 'bupt_cas'; // 如果没有设置，默认为 bupt_cas（向后兼容）
      console.log('CAS用户，使用ID:', userId, '用户类型:', userType);
    } else {
      // 访客用户
      userId = user.unified_user_id;
      userType = 'guest';
      console.log('访客用户，使用ID:', userId);
    }
    
    if (!userId) {
      console.log("无法获取Token余额：缺少有效用户ID");
      return;
    }

    try {
      //console.log("正在获取Token余额，用户ID:", userId, "用户类型:", userType);
      
      // 使用绝对URL确保请求正确发送
      const response = await fetch(`${EDUGATE_API_URL}/api/user/token-usage`, {
        headers: {
          'X-User-ID': userId,
          'X-User-Type': userType
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Token余额信息:", data);

      if (data.code === 0 && data.data) {
        setTokenBalance(data.data.tokenBalance);
        setTokenBalanceK(data.data.tokenBalanceK);
        setTotalTokens(data.data.totalTokens);
        setTokenGrant(data.data.tokenGrant);
        setPlanName(data.data.planName);
        setAdminAdjustedBalance(data.data.adminAdjustedBalance || 0);
        console.log("Token余额已更新:", data.data.tokenBalance, "K单位:", data.data.tokenBalanceK, "已用:", data.data.totalTokens, "赠送:", data.data.tokenGrant,
          "套餐:", data.data.planName, "管理员调整:", data.data.adminAdjustedBalance);
      }
    } catch (error) {
      console.error("获取Token余额失败:", error);
      // 不再设置默认值，让界面显示真实情况
    }
  };

  // 定期更新Token余额
  useEffect(() => {
    if (!currentUser) return;
    
    // 登录后延迟3秒获取一次，确保用户信息已完全加载（尤其是CAS用户的studentId）
    const initialFetchTimeout = setTimeout(() => {
      console.log("初始获取token余额，当前用户信息:", {
        username: currentUser.username,
        unified_user_id: currentUser.unified_user_id,
        isSchoolUser: currentUser.isSchoolUser,
        isCasUser: currentUser.isCasUser,
        userType: currentUser.user_type
      });
      fetchTokenBalance(currentUser);
      // 登录后立即发一次心跳
      const uid = currentUser.unified_user_id || currentUser.username || currentUser.employeeNumber;
      if (uid) {
        fetch(`${GVA_API_URL}/LL/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: String(uid) })
        }).catch(() => {});
      }
    }, 3000);
    
    // 每2分钟更新一次token余额
    const tokenUpdateInterval = setInterval(() => {
      fetchTokenBalance(currentUser);
      // 顺带上报心跳（去重在线人数统计）
      if (currentUser) {
        const uid = currentUser.unified_user_id || currentUser.username || currentUser.employeeNumber;
        if (uid) {
          fetch(`${GVA_API_URL}/LL/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: String(uid) })
          }).catch(() => {});
        }
      }
    }, 2 * 60 * 1000);
    
    // 组件卸载时清除定时器
    return () => {
      clearTimeout(initialFetchTimeout);
      clearInterval(tokenUpdateInterval);
    };
  }, [currentUser]);

  // 打开「我的用量」对话框时刷新 token 数据，确保「已使用」显示当前用户最新用量
  useEffect(() => {
    if (isTokenUsageLogsOpen && currentUser) {
      fetchTokenBalance(currentUser);
    }
  }, [isTokenUsageLogsOpen, currentUser]);

  // 获取能力图标

  // 在顶部导入TokenUsageLogs组件


  // 添加状态变量

  // 在Token余额图标部分添加点击事件，打开Token使用日志对话框
  const renderTokenBalanceIcon = () => {
    if (tokenBalance !== null) {
      return (
        <div 
          className="token-balance-icon"
          title={t('token_tooltip', { available: Math.round(tokenBalance/1000), used: Math.round(totalTokens/1000) })}
          onClick={() => setIsTokenUsageLogsOpen(true)}
          style={{ cursor: 'pointer' }}
        >
          💰 {tokenBalanceK || 0}k
        </div>
      );
    }
    return null;
  };

  return (
    <div className="App">
      {!isLoggedIn ? (
        <AuthPage onLogin={handleLogin} onRegisterSuccess={handleRegisterSuccess} />
      ) : (
        <>
          {/* 系统重置状态指示器 */}
          {isSystemResetting && (
            <div style={{
              position: 'fixed',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              color: 'white',
              fontSize: '18px',
              fontWeight: '500'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '30px',
                backgroundColor: 'rgba(44, 62, 80, 0.9)',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                <div style={{ marginBottom: '20px' }}>{t('system_resetting')}</div>
                <div style={{ fontSize: '14px', opacity: '0.8' }}>
                  {t('system_resetting_hint')}
                </div>
              </div>
            </div>
          )}

          {/* 通知组件 */}
          {notification.show && (
            <div className={`notification notification-${notification.type}`} style={{
              position: 'fixed',
              top: '72px',
              left: 'auto',
              right: '24px',
              transform: 'none',
              animation: 'none',
              maxWidth: '420px',
              padding: '10px 20px',
              borderRadius: '4px',
              backgroundColor: notification.type === 'warning' ? '#2c3e50' : '#2c3e50', // 改为深色背景
              color: 'white',
              zIndex: 9999,
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              borderLeft: notification.type === 'warning' ? '4px solid #e74c3c' : '4px solid #2ecc71', // 添加左侧边框
              fontWeight: '500'
            }}>
              {notification.message}
            </div>
          )}
          
          {/* Header */}
          <TopHeader>
              {/* 重构为三部分布局 */}
              
              {/* 第一部分：左侧区域 - 图标、标题和按钮 */}
              <div className="header-left-new">
                {/* Logo：机构定制优先，否则默认墨子 */}
                <img
                  src={resolveBrandLogoUrl(currentUser?.branding?.logoUrl)}
                  alt="Logo"
                  className="logo-new"
                  onDoubleClick={handleLogoDoubleClick}
                  style={{ cursor: "pointer" }}
                />
                
                {/* 标题和按钮容器 */}
                <div className="header-content-new">
                  {/* 标题和连接状态 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h1 className="title-new">
                      {(currentUser?.branding?.title && String(currentUser.branding.title).trim())
                        ? currentUser.branding.title
                        : t('app_title')}
                    </h1>
                    <Breadcrumb
                      items={appBreadcrumbItems}
                      className="app-breadcrumb"
                    />
                    
                    <div className="connection-status-group">
                      {/* 🔧 按视图显示连接状态：群聊用 GroupChatHub；CCMA/YouLaber 用 CCMA；大厅不显示 */}
                      <div className="connection-indicator-inline">
                        {headerConnectionStatus === 'connected' && (
                          <div className="status-badge online">
                            <span className="status-dot"></span>
                            {t('status_connected')}
                          </div>
                        )}
                        {headerConnectionStatus === 'disconnected' && (
                          <div className="status-badge offline">
                            <span className="status-dot"></span>
                            {t('status_disconnected')}
                          </div>
                        )}
                        {headerConnectionStatus === 'connecting' && (
                          <div className="status-badge connecting">
                            <span className="status-dot"></span>
                            {t('status_connecting')}
                          </div>
                        )}
                        {headerConnectionStatus === 'reconnecting' && (
                          <div className="status-badge connecting">
                            <span className="status-dot"></span>
                            重连中
                          </div>
                        )}
                      </div>
                      
                      {isComprehensiveView && (
                        <>
                          {/* 🎭 数字人全局开关（紧凑版） */}
                          <label className="digital-human-toggle-compact" title={t('digital_human_toggle')}>
                            <input
                              type="checkbox"
                              className="digital-human-toggle-checkbox"
                              checked={digitalHumanGlobalEnabled}
                              onChange={(e) => setDigitalHumanGlobalEnabled(e.target.checked)}
                            />
                            <span className="digital-human-toggle-switch">
                              <span className="digital-human-toggle-icon">🎭</span>
                            </span>
                          </label>
                          {/* 📚 上课模式开关 */}
                          <label className="digital-human-toggle-compact" title="上课模式：数字人朗读全文（去除图表和表格）">
                            <input
                              type="checkbox"
                              className="digital-human-toggle-checkbox"
                              checked={classMode}
                              onChange={(e) => setClassMode(e.target.checked)}
                            />
                            <span className="digital-human-toggle-switch">
                              <span className="digital-human-toggle-icon">📚</span>
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* 入口切换栏：组织名称（若有）+ 六入口；邮百工平台签名仅保留在面包屑首项 */}
                  <div className="mode-selector-row">
                    {currentUser?.branding?.schoolName &&
                      String(currentUser.branding.schoolName).trim() && (
                      <div className="mode-brand-prefix">
                        {currentUser.branding.schoolNameType === 'image' ? (
                          <img
                            className="brand-school-name brand-school-name-image"
                            src={resolveBrandMediaUrl(currentUser.branding.schoolName)}
                            alt="学校名称"
                          />
                        ) : (
                          <span
                            className="brand-school-name"
                            title={currentUser.branding.schoolName}
                          >
                            {currentUser.branding.schoolName}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mode-selector-new" role="tablist">
                      <button
                        role="tab"
                        aria-selected={activeView === 'portal'}
                        className={`mode-button-new ${activeView === 'portal' ? 'active-new' : ''}`}
                        onClick={() => switchView('portal')}
                      >
                        {t('mode_course_portal')}
                      </button>
                      {isPortalTabVisible('ccma') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'ccma'}
                          className={`mode-button-new ${activeView === 'ccma' ? 'active-new' : ''}`}
                          onClick={() => switchView('ccma')}
                        >
                          {t('mode_ccma')}
                        </button>
                      )}
                      {isPortalTabVisible('plaza') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'plaza'}
                          className={`mode-button-new ${activeView === 'plaza' ? 'active-new' : ''}`}
                          onClick={() => switchView('plaza')}
                        >
                          {t('mode_plaza')}
                        </button>
                      )}
                      {isPortalTabVisible('discuss') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'discuss'}
                          className={`mode-button-new ${activeView === 'discuss' ? 'active-new' : ''}`}
                          onClick={() => switchView('discuss')}
                        >
                          {t('mode_discuss')}
                        </button>
                      )}
                      {isPortalTabVisible('groupchat') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'groupchat'}
                          className={`mode-button-new ${activeView === 'groupchat' ? 'active-new' : ''}`}
                          onClick={() => switchView('groupchat')}
                        >
                          {t('mode_groupchat')}
                        </button>
                      )}
                      {isPortalTabVisible('youlaber') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'youlaber'}
                          className={`mode-button-new ${activeView === 'youlaber' ? 'active-new' : ''}`}
                          onClick={() => switchView('youlaber')}
                        >
                          {t('mode_youlaber')}
                        </button>
                      )}
                      {isPortalTabVisible('drawing') && (
                        <button
                          role="tab"
                          aria-selected={activeView === 'drawing'}
                          className={`mode-button-new ${activeView === 'drawing' ? 'active-new' : ''}`}
                          onClick={() => {
                            if (tokenBalance !== null && tokenBalance < 10) {
                              showNotification('Token额度已耗尽，无法使用绘图功能', 'error');
                              return;
                            }
                            switchView('drawing');
                          }}
                        >
                          {t('mode_drawing')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 第二部分：中间区域 - 角色选择和状态 */}
              <div className="header-middle-new">
                {/* 中间左侧 - 角色选择区域，确保从顶部开始 */}
                <div className="header-middle-left-new">
                  {isComprehensiveView && (
                    <div className="role-selection-area">
                      {/* 使用水平排列的角色选择器行 */}
                      <div className="role-selection-container">
                      <div className="role-selectors-row">
                        <RoleSelector
                          selectedRoles={selectedRoles}
                          onRoleSelect={handleRoleSelect}
                          agents={agents}
                          openSignal={teamDialogOpenSignal}
                          validationMessage={teamValidationMessage}
                          onDialogClose={() => setTeamValidationMessage('')}
                          accentColor={currentUser?.branding?.themeColor}
                        />
                        <div className="role-selector">
                          <span className="role-label">{t('expert_team')}</span>
                          <div className="custom-dropdown expert-dropdown">
                            {/* 保留专家选择的原有代码 */}
                            <div
                              className="dropdown-trigger"
                              onClick={() =>
                                setIsExpertDropdownOpen(!isExpertDropdownOpen)
                              }
                            >
                              {selectedExpert ? (
                                <span className="selected-roles">
                                  {getLocalizedDisplayName(selectedExpert)}
                                </span>
                              ) : (
                                <span className="placeholder">{t('select_expert')}</span>
                              )}
                              <span
                                className={`dropdown-arrow ${
                                  isExpertDropdownOpen ? "open" : ""
                                }`}
                              >
                                ▼
                              </span>
                            </div>
                            <ExpertSelector
                              open={isExpertDropdownOpen && isExpertDialogReady}
                              onClose={() => {
                                setIsExpertDropdownOpen(false)
                                setExpertSearchTerm('')
                                setSelectedExpertCategory('全部')
                              }}
                              experts={expertDialogContent?.filteredExperts || []}
                              selectedExpert={selectedExpert}
                              onExpertSelect={(expert) => {
                                handleExpertSelect(expert)
                                setIsExpertDropdownOpen(false)
                                setExpertSearchTerm('')
                                setSelectedExpertCategory('全部')
                              }}
                              searchTerm={expertSearchTerm}
                              onSearchChange={setExpertSearchTerm}
                              selectedCategory={selectedExpertCategory}
                              onCategorySelect={setSelectedExpertCategory}
                              categories={expertDialogContent?.categories || []}
                              accentColor={currentUser?.branding?.themeColor}
                            />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                

              </div>
              
             {/* 第三部分：右侧区域 - 工具栏式布局 */}
             <div className="header-user-menu-container">
                {/* 左侧组合：流程名称 + 用户信息 */}
                <div className="left-group">
                  {/* 流程名称区域 - 仅综合训练显示 */}
                  {isComprehensiveView && (
                    <>
                      <div className="flow-name-section" style={{ marginRight: '0px' }}>
                        <div 
                          className="current-flow-display" 
                          onClick={openFlowTypeDialog} 
                          style={{ 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0px 6px',
                            backgroundColor: 'rgba(38, 79, 120, 0.2)', // 幽灵深蓝色
                            border: '1px solid rgba(38, 79, 120, 0.4)',
                            borderRadius: '12px',
                            transition: 'all 0.2s ease',
                            height: '22px'
                          }} 
                          title={t('flow_type_change')}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(38, 79, 120, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(38, 79, 120, 0.2)'}
                        >
                          {/* 移除手动添加的菱形图标，因为流程名称本身已经包含了符号 */}
                          <span className="current-flow-name" style={{ color: '#9cdcfe', fontWeight: 500, fontSize: '12px', lineHeight: 1 }}>
                            {resolveFlowDisplayName(FlowName)}
                          </span>
                        </div>
                      </div>
                  
                      {/* 垂直分割线 */}
                      <div style={{ width: '1px', height: '12px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '0 3px' }}></div>
                    </>
                  )}

                  {/* 🧭 邮百工导览员入口 */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <div
                      className="header-icon-btn guide-btn"
                      onClick={openGuide}
                      title="邮百工怎么用 / 入场导览"
                      aria-label="邮百工入场导览"
                      style={{
                        padding: '2px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#8fd3ff',
                        cursor: 'pointer',
                        marginRight: '2px'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(143, 211, 255, 0.12)'; e.currentTarget.style.color = '#d7f2ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#8fd3ff'; }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </div>
                    {isGuideNudgeVisible && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '26px',
                          right: '-8px',
                          zIndex: 1300,
                          width: '210px',
                          padding: '10px',
                          borderRadius: '12px',
                          border: '1px solid rgba(143, 211, 255, 0.28)',
                          background: 'rgba(10, 24, 38, 0.96)',
                          boxShadow: '0 14px 36px rgba(0,0,0,0.32)',
                          color: '#dff6ff',
                          fontSize: '12px',
                          lineHeight: 1.55
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>不知道从哪里开始？可在「我的课程」右侧询问邮百工导览员。</div>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={openGuide}
                            style={{
                              border: 'none',
                              borderRadius: '8px',
                              padding: '4px 8px',
                              background: '#2f8cff',
                              color: '#fff',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            前往我的课程
                          </button>
                          <button
                            type="button"
                            onClick={dismissGuideNudge}
                            style={{
                              border: '1px solid rgba(143, 211, 255, 0.24)',
                              borderRadius: '8px',
                              padding: '4px 8px',
                              background: 'transparent',
                              color: '#aee8ff',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            不再提示
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 🔔 通知铃铛 - Material Icon */}
                  <div 
                    className="header-icon-btn notification-btn"
                    onClick={() => {
                      setNotificationCenterOpen(true);
                      fetchNotifications(); // 打开时刷新通知列表
                    }}
                    title={unreadNotificationCount > 0 ? t('unread_notif', { count: unreadNotificationCount }) : t('notif_center')}
                    style={{ 
                      padding: '2px', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: '#a0a0a0', 
                      cursor: 'pointer',
                      marginRight: '2px' // 覆盖默认的8px间距，大幅拉近与右侧图标距离
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = '#e0e0e0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#a0a0a0'; }}
                  >
                    <NotificationsNoneIcon sx={{ fontSize: 16 }} />
                    {unreadNotificationCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          background: '#e74c3c',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          border: '1.5px solid #1e1e1e' // 增加描边使其更清晰
                        }}
                      >
                      </span>
                    )}
                  </div>

                  {/* 🔬 研究中心图标 - Material Icon */}
                  <div 
                    className="header-icon-btn research-btn"
                    onClick={() => {
                      setResearchCenterOpen(true);
                    }}
                    title={unreadResearchCount > 0 ? t('research_new', { count: unreadResearchCount }) : t('research')}
                    style={{ 
                      padding: '2px', 
                      borderRadius: '50%', 
                      display: 'none', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: '#a0a0a0', 
                      marginLeft: '0px', 
                      marginRight: '0px', // 覆盖默认的8px间距
                      cursor: 'pointer' 
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = '#e0e0e0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#a0a0a0'; }}
                  >
                    <ScienceIcon sx={{ fontSize: 16 }} />
                    {unreadResearchCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          background: '#e74c3c',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          border: '1.5px solid #1e1e1e'
                        }}
                      >
                      </span>
                    )}
                  </div>
                  
                  {/* 垂直分割线 */}
                  <div style={{ width: '1px', height: '12px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '0 3px' }}></div>
                  
                  {/* 用户信息区域 */}
                  <div className="user-info-section" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {currentUser && (
                    <div className="user-info-display" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {/* 用户名 - 纯文字无图标，更简洁；下方附 AI 生成免责小字 */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {currentUser.isSchoolUser ? (
                            <SchoolIcon sx={{ fontSize: 14, color: '#a0a0a0' }} />
                          ) : (
                            <AccountCircleIcon sx={{ fontSize: 14, color: '#a0a0a0' }} />
                          )}
                          <span 
                            title={t('class_hint', { class: currentUser.class_name || t('unknown'), num: currentUser.student_number || t('unknown') })}
                            style={{ fontSize: '12px', fontWeight: 500, color: '#e0e0e0' }}
                          >
                            {currentUser.name || currentUser.username}
                          </span>
                        </div>
                        <span
                          className="ai-disclaimer-text-compact"
                          title="本平台所有内容均由 AI 生成，仅供工程训练参考，不代表官方立场"
                        >
                          AI 生成 · 仅供训练参考
                        </span>
                      </div>

                      {/* 我的成长中心：用户名右侧显示等级（非文字按钮），点击打开成长中心 */}
                      {(currentUser.isSchoolUser || currentUser.isCasUser || currentUser.unified_user_id) &&
                        !currentUser.isGuestUser &&
                        currentUser.user_type !== 'guest' && (
                        <div className="growth-center-entry" title={t('game_center_title')}>
                          <FloatingXPBar
                            gameProfile={gameProfile || { totalXp: 0 }}
                            gameConfig={gameConfig}
                            onClick={() => setShowGameCenter(true)}
                            hasUnreadTodayYou={hasUnreadTodayYou}
                          />
                        </div>
                      )}
                      
                      {/* Token余额图标 - 四阶段状态颜色 */}
                      {tokenBalance !== null && (() => {
                        const usagePercent = tokenGrant > 0 ? ((tokenGrant - tokenBalance) / tokenGrant) * 100 : 0;
                        const isBlocked = tokenBalance <= 0;
                        const isWarning = usagePercent >= 90;
                        const isOverage = usagePercent >= 70;
                        // 四阶段颜色：正常(金) → 注意(橙) → 预警(红) → 封停(灰)
                        const statusColor = isBlocked ? '#999' : isWarning ? '#f56c6c' : isOverage ? '#e6a23c' : '#ffd700';
                        const bgColor = isBlocked ? 'rgba(153,153,153,0.1)' : isWarning ? 'rgba(245,108,108,0.1)' : isOverage ? 'rgba(230,162,60,0.1)' : 'rgba(255,215,0,0.1)';
                        const borderColor = isBlocked ? 'rgba(153,153,153,0.3)' : isWarning ? 'rgba(245,108,108,0.3)' : isOverage ? 'rgba(230,162,60,0.3)' : 'rgba(255,215,0,0.3)';
                        const planLabel = planName === '散户试用' ? '试用额度(个人)' : planName === '未归属组织' ? '默认额度' : (planName || '未知套餐');
                        const tooltipText = `${planLabel} | ${Math.round(tokenBalance/1000)}k可用 / ${Math.round((tokenGrant||0)/1000)}k总额 | 已用${Math.round((totalTokens||0)/1000)}k`;
                        return (
                        <div 
                          className="token-balance-icon"
                          title={tooltipText}
                          onClick={() => setIsTokenUsageLogsOpen(true)}
                          style={{ 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0px 6px',
                            backgroundColor: bgColor,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '12px',
                            gap: '2px',
                            transition: 'all 0.2s ease',
                            height: '18px',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          <TokenIcon sx={{ fontSize: 12, color: statusColor }} />
                          <span style={{ fontSize: '11px', fontFamily: 'Consolas, monospace', color: statusColor, lineHeight: 1 }}>
                            {isBlocked ? '0' : (tokenBalanceK || 0) + 'k'}
                          </span>
                        </div>
                        );
                      })()}
                    </div>
                  )}
                  </div>
                </div>
                
                {/* 垂直分割线 - 分隔用户区和菜单区 */}
                <div style={{ width: '1px', height: '12px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '0 2px 0 4px' }}></div>
                
                {/* 菜单按钮区域 - 固定在右侧 - 仅图标 */}
                <div className="menu-section">
                  <IconButton
                    onClick={handleMenuOpen}
                    color="inherit"
                    aria-label={t('aria_menu')}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      backgroundColor: "transparent",
                      border: "1px solid transparent", // 预留透明边框以防布局跳动
                      color: "#a0a0a0",
                      borderRadius: "4px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        color: "#e0e0e0"
                      }
                    }}
                    title={t('menu')}
                  >
                    <MoreVertIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </div>
              </div>
              
              {/* 保留菜单组件不变 */}
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: "#252525",
                    color: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
                    minWidth: "200px",
                  },
                  "& .MuiMenuItem-root": {
                    padding: "10px 16px",
                    borderRadius: "4px",
                    margin: "2px 4px",
                    fontSize: "14px",
                  },
                  "& .MuiDivider-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    margin: "4px 8px",
                  }
                }}
              >
                {/* 主菜单项在各入口保持一致，不随训练模式切换 */}
                {[
                      // 常用功能和帮助项
                      <MenuItem
                        key="ai-secretary"
                        onClick={() => {
                          setAISecretaryVisible(true)
                          handleMenuClose()
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#3a3a3a",
                          },
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <SmartToyIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                        {t('menu_ai_secretary')}
                      </MenuItem>,
                      /* 🎭 数字人模拟菜单项 - 暂时隐藏，需要时恢复 */
                      /* 
                      <MenuItem
                        key="digital-human-sim"
                        onClick={() => {
                          setIsSimDialogOpen(true)
                          handleMenuClose()
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#3a3a3a",
                          },
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <PersonIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                        数字人模拟
                      </MenuItem>,
                      */
                      <MenuItem
                        key="help"
                        onClick={() => {
                          openHelpPage()
                          handleMenuClose()
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#3a3a3a",
                          },
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <HelpIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                        {t('menu_help')}
                      </MenuItem>,
                      // “我的课程”只能从课程任务进入训练，菜单不提供通用流程选择入口。
                      activeView !== 'portal' && (
                        <React.Fragment key="flow-type-section">
                          <Divider />
                          <MenuItem
                            key="flow-type"
                            onClick={() => {
                              openFlowTypeDialog()
                              handleMenuClose()
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#3a3a3a",
                              },
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <SettingsIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                            {t('menu_flow_type')}
                          </MenuItem>
                          <Divider />
                        </React.Fragment>
                      ),
                      // 用户特定功能
                      // 课程管理 - 访客用户和北邮用户可见，校园用户不可见
                      currentUser && (currentUser.isCasUser) && (
                        <MenuItem
                          key="course-management"
                          onClick={() => {
                            setIsCourseManagementDialogOpen(true)
                            handleMenuClose()
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#3a3a3a",
                            },
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SchoolIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                          {t('menu_course_mgmt')}
                        </MenuItem>
                      ),
                      currentUser && (currentUser.isCasUser || currentUser.isSchoolUser) && (
                        <MenuItem
                          key="school-task"
                          onClick={() => {
                            setFocusHomeworkTaskId(null)
                            setFocusHomeworkMode(null)
                            setFocusHomeworkCourseId(null)
                            setShowHomeworkDialog(true)
                            setIsFromMenu(true)
                            handleMenuClose()
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#3a3a3a",
                            },
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SchoolIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                          {t('menu_school_task')}
                        </MenuItem>
                      ),
                      currentUser && (currentUser.isSchoolUser || currentUser.isCasUser) && (
                        <MenuItem
                          key="yuyan-project"
                          onClick={() => {
                            setIsYuyanProjectDialogOpen(true)
                            handleMenuClose()
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#3a3a3a",
                            },
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FlightTakeoffIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                          {t('menu_yuyan_project')}
                        </MenuItem>
                      ),
                      currentUser &&
                        currentUser.isSchoolUser && (
                          <MenuItem
                            key="change-password"
                            onClick={() => {
                              setPasswordDialogOpen(true)
                              handleMenuClose()
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#3a3a3a",
                              },
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <KeyIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                            {t('menu_change_password')}
                          </MenuItem>
                        ),
                      currentUser?.isSchoolUser && <Divider key="divider-3" />,
                      // 进度和存档管理
                      <Divider key="divider-4" />,
                      <MenuItem
                        key="list-archives"
                        onClick={handleListArchives}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#3a3a3a",
                          },
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <ArchiveIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                        {t('menu_archives')}
                      </MenuItem>,
                    ].filter(Boolean)}

                <Divider />

                {/* 🌐 语言切换 - 点击展开语言选择面板 */}
                <MenuItem
                  onClick={(e) => {
                    setLangMenuAnchor(e.currentTarget);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#3a3a3a",
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <TranslateIcon sx={{ mr: 1.5, fontSize: "18px" }} />
                    {getCurrentLanguageLabel()}
                  </span>
                  <ArrowForwardIosIcon sx={{ fontSize: 12, ml: 1, opacity: 0.5 }} />
                </MenuItem>

                {/* 语言选择子菜单 */}
                <Menu
                  anchorEl={langMenuAnchor}
                  open={Boolean(langMenuAnchor)}
                  onClose={() => setLangMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{
                    "& .MuiPaper-root": {
                      backgroundColor: "#252525",
                      color: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
                      maxHeight: "400px",
                      minWidth: "180px",
                    },
                    "& .MuiMenuItem-root": {
                      padding: "6px 16px",
                      fontSize: "13px",
                    },
                  }}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => {
                    const currentLang = getPreferredLanguage();
                    const isSelected = currentLang === lang.code;
                    return (
                      <MenuItem
                        key={lang.code}
                        onClick={() => {
                          switchLanguage(lang.code, i18n);
                          setLangMenuAnchor(null);
                          handleMenuClose();
                          // 如果 WebSocket 已连接，发送语言变更通知
                          if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                            ws.current.send(JSON.stringify({
                              type: "language_change",
                              language: lang.code,
                            }));
                          }
                        }}
                        sx={{
                          backgroundColor: isSelected ? "rgba(100, 181, 246, 0.15)" : "transparent",
                          "&:hover": { backgroundColor: "#3a3a3a" },
                        }}
                      >
                        <span style={{ marginRight: 8, minWidth: 20, textAlign: 'center' }}>
                          {isSelected ? '✓' : ''}
                        </span>
                        {lang.label}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Menu>
          </TopHeader>

          {/* 条件渲染不同的训练内容 */}
          {activeTrainingMode === "portal" ? (
            <CoursePortal
              key={portalRefreshKey}
              currentUser={currentUser}
              onOpenCourseManagement={() => setIsCourseManagementDialogOpen(true)}
              onOpenHomework={(item) => {
                const taskId = item?.taskId != null && Number(item.taskId) > 0
                  ? Number(item.taskId)
                  : null;
                const courseId = item?.courseId != null && Number(item.courseId) > 0
                  ? Number(item.courseId)
                  : null;
                setFocusHomeworkTaskId(taskId);
                setFocusHomeworkMode(taskId ? 'homework' : null);
                setFocusHomeworkCourseId(courseId);
                setIsFromMenu(true);
                setShowHomeworkDialog(true);
              }}
              onOpenEvaluation={(item) => {
                const taskId = item?.taskId != null && Number(item.taskId) > 0
                  ? Number(item.taskId)
                  : null;
                const courseId = item?.courseId != null && Number(item.courseId) > 0
                  ? Number(item.courseId)
                  : null;
                setFocusHomeworkTaskId(taskId);
                setFocusHomeworkMode(taskId ? 'evaluation' : null);
                setFocusHomeworkCourseId(courseId);
                setIsFromMenu(true);
                setShowHomeworkDialog(true);
              }}
              onOpenGrowth={(item) => {
                const courseId = item?.courseId != null && Number(item.courseId) > 0
                  ? Number(item.courseId)
                  : null;
                if (!courseId) return;
                setFocusHomeworkTaskId(null);
                setFocusHomeworkCourseId(courseId);
                setFocusHomeworkMode('growth');
                setIsFromMenu(true);
                setShowHomeworkDialog(true);
              }}
              onFeatureFlagsChange={setPortalFeatureFlags}
              guideAgent={GUIDE_AGENT}
              guideWelcomeContent={GUIDE_WELCOME_CONTENT}
              guideDocLinks={GUIDE_DOC_LINKS}
              guideExpertServiceUrl={EXPERT_SERVICE_URL}
              onGuideA2UIAction={handleGuideA2UIAction}
              guideSpotlight={guideSpotlight}
              guideFocusToken={guideFocusToken}
              onLoadFatal={() => {
                // tabs 经 ref 读取，避免把最新值塞进依赖导致面板反复全量刷新闪烁
                const tabs = portalTabsRef.current || {};
                if (tabs.ccma !== false) {
                  setActiveTrainingMode('ccma');
                  switchView('ccma');
                  window.setTimeout(() => setIsFlowTypeDialogOpen(true), 200);
                  return;
                }
                if (tabs.plaza !== false) {
                  setActiveTrainingMode('plaza');
                  switchView('plaza');
                  return;
                }
                if (tabs.groupchat !== false) {
                  setActiveTrainingMode('groupchat');
                  switchView('groupchat');
                  return;
                }
                setActiveTrainingMode('portal');
                switchView('portal');
              }}
              onLaunch={(item) => {
                // 顶栏关闭只隐藏入口；课程内「开始训练」仍可进入对应训练面板。
                // 多候选资源时，浮层会把所选资源字段合并进 item（launchKind/flowName/templateId/agentCommand）。
                const minutes = Number(item?.timeLimitMinutes) || 0;
                const taskId = Number(item?.taskId) || Number(String(item?.key || '').replace(/^task:/i, '')) || 0;
                const beginTrainingTimer = (kind) => {
                  if (minutes <= 0 || taskId <= 0) {
                    setTrainingTimer(null);
                    return;
                  }
                  const userKey = currentUser?.unified_user_id || currentUser?.studentID || currentUser?.username || 'anon';
                  const startedAt = startTrainingTimerClock({ userKey, taskId, kind });
                  setTrainingTimer({ taskId, minutes, kind, startedAt });
                };
                if (item.launchKind === 'ccma' && item.flowName) {
                  beginTrainingTimer('ccma');
                  resumeOrLaunchCcma(item.flowName, item.selectedResourceTitle);
                } else if (item.launchKind === 'groupchat' && item.templateId) {
                  beginTrainingTimer('groupchat');
                  portalLaunchBypassRef.current = 'groupchat';
                  setTemplateId(item.templateId);
                  switchView('groupchat');
                } else if (item.launchKind === 'plaza' && item.agentCommand) {
                  beginTrainingTimer('plaza');
                  // 与专项广场卡片点击一致：复用 FullscreenAgentView，不进入带「可用智能体」侧栏的 selectedTraining 布局
                  portalLaunchBypassRef.current = 'plaza';
                  switchView('plaza');
                  const matched = (plazaAgents || []).find(
                    (agent) => String(agent.id) === String(item.agentCommand)
                      || String(agent.command) === String(item.agentCommand)
                  );
                  handleAgentClick(matched ? {
                    ...matched,
                    name: matched.name || item.selectedResourceTitle || item.title,
                  } : {
                    id: item.agentCommand,
                    name: item.selectedResourceTitle || item.title,
                    description: item.description || '',
                    expert_group: 'plaza',
                    icon: 'SmartToy',
                    iconColor: 'analysis',
                    attributions: [],
                  });
                }
              }}
            />
          ) : activeTrainingMode === "ccma" ? (
            // 综合训练（CCMA 先进综合）—— 旧 GoMAown 'comprehensive' 入口已下线
            <>
              {/* 优化的进度条，移除多余容器 */}
                  <ProjectProgressBar
                    currentState={projectState}
                    shouldFlash={shouldFlash}
                    handleBackToPreviousPhase={handleBackToPreviousPhase}
                    flowName={FlowName}
                    resetHistory={resetProgressHistory}
                    feedbackDelta={feedbackDelta}
                    onFeedbackComplete={() => setFeedbackDelta(null)}
                  />

              <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <h2>{t('advanced_settings')}</h2>
                <div className="params-grid">
                  {Object.entries(params).map(([key, value]) => {
                    if (key === "idea") return null
                    return (
                      <div key={key} className="input-group">
                        <label htmlFor={key}>
                          {key.replace(/_/g, " ")}:
                          <span className="param-description">
                            {paramDescriptions[key]}
                          </span>
                        </label>
                        {typeof value === "boolean" ? (
                          <input
                            type="checkbox"
                            id={key}
                            name={key}
                            checked={value}
                            onChange={handleChange}
                          />
                        ) : (
                          <input
                            type={typeof value === "number" ? "number" : "text"}
                            id={key}
                            name={key}
                            value={value}
                            onChange={handleChange}
                            step={typeof value === "number" ? "any" : undefined}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </Modal>

              {/* Main Content */}
              <div
                className="main-content chatAndFileContainer ccma-main-content"
                style={buildAccentThemeStyle(currentUser?.branding, 'ccma')}
              >
                {isBubbleSidebarVisible && (
                  <div className="ms-bubble-sidebar">
                    <MicroSceneBubbleList
                      bubbles={microSceneBubbles}
                      onBubbleClick={handleBubbleClick}
                    />
                  </div>
                )}
                {/* Chat Panel */}
                {isChatPanelVisible && (
                  <div
                    className={`${styles.chatPanel}`}
                    ref={messagePanelRef}
                    style={{
                      width: `${chatPanelWidth}%`,
                      transition: "width 0.3s ease",
                    }}
                  >
                  {/* 🎭 数字人弹窗 - 悬浮在右上角 */}
                  <Suspense fallback={null}>
                    <DigitalHumanDialog
                      isOpen={digitalHumanOpen && (digitalHumanGlobalEnabled || !!simulationConfig)}
                      expertName={currentExpertName}
                      text={currentSpeechText}
                      onClose={() => {
                        setDigitalHumanOpen(false);
                        setSimulationConfig(null); // 🆕 关闭时清除模拟配置
                      }}
                      messageType="MsgtoHuman"
                      workflowName={FlowName}
                      currentUser={currentUser}
                      customConfig={simulationConfig} // 🆕 传入模拟配置
                      initialMaximized={!!simulationConfig} // 🆕 如果是模拟模式，初始即大屏
                      classMode={classMode}
                    />
                  </Suspense>
                  
                  <div className={styles.messagesContainer} ref={chatPanelRef} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* 欢迎消息 - 已移至VirtualMessageList中统一处理，实现整体虚拟滚动 */}
                    
                    <VirtualMessageList
                      messages={messages}
                      welcomeMessage={welcomeMessage}
                      renderMessageContent={renderMessageContent}
                      chatPanelRef={chatPanelRef}
                      styles={styles}
                      isAutoScrollEnabled={true}
                      isViewingHistory={isViewingHistory}
                      onAgentClick={handleAgentIdentifierClick}
                      apiUrl={CCMA_API_URL}
                    />
                  </div>
                  {/* 阶段切换后的"镜子里的你"拉式入口（不打扰、不弹窗、不进聊天流） */}
                  <TodayYouEntryRow
                    visible={hasUnreadTodayYou}
                    onOpen={() => {
                      setShowGameCenter(true);
                      onMarkTodayYouRead();
                    }}
                    onDismiss={onMarkTodayYouRead}
                  />
                  <div
                    className={`${styles.resizeHandle} ${activeFile ? styles.expanded : ""}`}
                    onMouseDown={handleMouseDown}
                  />
                  {isLoading && (
                    <div className={styles.loadingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  )}
                  {isWaitingResponse && (
                    <div className={styles.loadingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span className={styles.loadingIndicatorText}>{t('thinking')}</span>
                    </div>
                  )}
                  <form
                    onSubmit={handleHumanInputMessage}
                    className={`${styles.humanInputForm} ${isInExpertMode ? styles.expertMode : ''}`}
                  >
                    {/* 附件药丸区域 - 只在文档分析助手专家模式下显示 */}
                    {isInExpertMode && selectedExpert && selectedExpert.comd === "document_query" && attachedFiles.length > 0 && (
                      <div className={styles.attachmentPills}>
                        {attachedFiles.map((file, index) => (
                          <div key={index} className={styles.attachmentPill}>
                            <span className={styles.attachmentIcon}>📎</span>
                            <span className={styles.attachmentName}>{file.name}</span>
                            <button
                              type="button"
                              className={styles.removeAttachment}
                              onClick={() => handleRemoveAttachment(index)}
                              disabled={isFileUploading}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className={styles.inputContainer}>
                      {/* 上方：文本输入区域 */}
                      <div className={styles.textareaSection}>
                        <textarea
                          name="humanInput"
                          defaultValue=""
                          placeholder={isInExpertMode && selectedExpert ? t('input_expert_placeholder', { name: getLocalizedDisplayName(selectedExpert) }) : t('input_placeholder')}
                          disabled={!isMessageInputEnabled}
                          rows={3}
                          ref={inputRef}
                          onInput={handleInputChange}
                          onKeyDown={handleKeyDown}
                          className={styles.mainTextarea}
                        />
                      </div>
                      
                      {/* 下方：控制栏 */}
                      <div className={styles.controlsBar}>
                        {/* 左侧：模型选择器 */}
                        <div className={styles.modelSelectorCompact}>
                          {/* DeepThink 按钮已禁用 — 双倍LLM调用成本过高，默认隐藏 */}
                          <Tooltip 
                            title={t('tooltip_deepthink')} 
                            placement="top"
                            arrow
                            enterDelay={200}
                            leaveDelay={100}
                            classes={{ tooltip: styles.modelTooltip }}
                          >
                            <label className={modelType === "SimReasoningbygpt4o" ? styles.selected : ""} style={{ display: 'none' }}>
                              <input
                                type="radio"
                                name="modelChoice"
                                value="SimReasoningbygpt4o"
                                checked={modelType === "SimReasoningbygpt4o"}
                                onChange={handleModelTypeChange}
                              />
                              <span>DeepThink</span>
                            </label>
                          </Tooltip>
                          
                          <Tooltip 
                            title={currentUser?.user_type !== "guest" 
                              ? "使用DeepSeek进行深度思考，预计耗时2-3分钟，请耐心等待，期间不要重复发送。" 
                              : "使用DeepSeek进行深度思考（仅限校园用户和北邮统一认证用户使用）。"
                            }
                            placement="top"
                            arrow
                            enterDelay={200}
                            leaveDelay={100}
                            classes={{ tooltip: styles.modelTooltip }}
                          >
                            <label className={`${modelType === "deepseek" ? styles.selected : ""} ${currentUser?.user_type === "guest" ? styles.disabledOption : ""}`}>
                              <input
                                type="radio"
                                name="modelChoice"
                                value="deepseek"
                                checked={modelType === "deepseek"}
                                onChange={handleModelTypeChange}
                                disabled={currentUser?.user_type === "guest"}
                              />
                              <span>DeepSeek</span>
                            </label>
                          </Tooltip>

                          <Tooltip 
                            title={t('tooltip_basic')} 
                            placement="top"
                            arrow
                            enterDelay={200}
                            leaveDelay={100}
                            classes={{ tooltip: styles.modelTooltip }}
                          >
                            <label className={modelType === "noReasoning" ? styles.selected : ""}>
                              <input
                                type="radio"
                                name="modelChoice"
                                value="noReasoning"
                                checked={modelType === "noReasoning"}
                                onChange={handleModelTypeChange}
                              />
                              <span>Basic</span>
                            </label>
                          </Tooltip>

                          {ADVANCED_MODE_CONFIG.enabled && (
                            <Tooltip 
                              title={ADVANCED_MODE_CONFIG.tooltip}
                              placement="top"
                              arrow
                              enterDelay={200}
                              leaveDelay={100}
                              classes={{ tooltip: styles.modelTooltip }}
                            >
                              <label className={modelType === "advanced" ? styles.selected : ""}>
                                <input
                                  type="radio"
                                  name="modelChoice"
                                  value="advanced"
                                  checked={modelType === "advanced"}
                                  onChange={handleModelTypeChange}
                                />
                                <span>{ADVANCED_MODE_CONFIG.displayName}</span>
                              </label>
                            </Tooltip>
                          )}
                        </div>
                        
                        {/* 右侧：操作按钮 */}
                        <div className={styles.actionButtons}>
                          {/* 文件上传按钮 - 只在非专家模式或文档分析助手专家模式下显示 */}
                          {(!isInExpertMode || (selectedExpert && selectedExpert.comd === "document_query")) && (
                            <button
                              type="button"
                              className={styles.paperclipButtonCompact}
                              onClick={handlePaperclipClick}
                              disabled={!isMessageInputEnabled || isFileUploading}
                              title={t('btn_upload')}
                            >
                              {isFileUploading ? '⏳' : '📎'}
                            </button>
                          )}
                          
                          {/* 绘图按钮 - 只在非专家模式或绘图助手专家模式下显示 */}
                          {(!isInExpertMode || (selectedExpert && selectedExpert.comd === "diagram_draw")) && (
                            <button
                              type="button"
                              className={`${styles.paperclipButtonCompact} ${selectedExpert?.comd === "diagram_draw" ? styles.diagramButtonActive : ''}`}
                              onClick={handleDiagramClick}
                              disabled={!isMessageInputEnabled || isDiagramStreaming}
                              title={t('btn_drawing')}
                            >
                              {isDiagramStreaming ? '⏳' : '🎨'}
                            </button>
                          )}
                          
                          {/* 🎤 语音输入按钮 - 始终显示 */}
                          <button
                            type="button"
                            className={`${styles.paperclipButtonCompact} ${isVoiceRecording ? styles.voiceButtonActive : ''}`}
                            onClick={handleVoiceClick}
                            disabled={!isMessageInputEnabled || isVoiceProcessing}
                            title={isVoiceRecording ? t('btn_voice_stop') : t('btn_voice_start')}
                          >
                            {isVoiceProcessing ? '⏳' : isVoiceRecording ? '🔴' : '🎙️'}
                          </button>
                          
                          {/* 查看任务按钮 - 重构后的清晰逻辑 */}
                          {showTaskButton && (
                            <button
                              type="button"
                              className={styles.paperclipButtonCompact}
                              onClick={() => {
                                setTaskSelectionDialog({
                                  open: true,
                                  isRestore: false,
                                  isUserModification: true,
                                  updateId: Date.now()
                                });
                              }}
                              title={t('btn_task')}
                            >
                              📝
                            </button>
                          )}
                          
                          {/* 🏁 结束当前阶段按钮 - 仅在训练开始后显示 */}
                          {projectState && !isInExpertMode && (
                            <button
                              type="button"
                              className={styles.endPhaseButtonCompact}
                              onClick={handleEndPhase}
                              disabled={!isMessageInputEnabled || isProcessing}
                              title={t('btn_next_phase')}
                            >
                              ⏭
                            </button>
                          )}
                          
                          <button 
                            type="submit" 
                            disabled={!isMessageInputEnabled || isFileUploading}
                            className={styles.sendButtonCompact}
                            title={t('btn_send')}
                          >
                            {isProcessing || isFileUploading ? (
                              <span className={styles.loadingIcon}>
                                ⏳
                              </span>
                            ) : (
                              <span className={styles.sendIcon}></span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* 隐藏的文件输入框 */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md"
                      style={{ display: 'none' }}
                    />
                  </form>
                </div>
                )}

                {/* 添加主面板分隔线 */}
                {isChatPanelVisible && (
                  <div 
                    className={`${fileStyles.mainPanelDivider} ${isMainDragging ? fileStyles.mainPanelDividerActive : ''}`}
                    onMouseDown={handleMainDragStart}
                  ></div>
                )}

                <div
                  className={`${fileStyles.filePanel} ${activeFile ? fileStyles.expanded : ""} ${!fileContentExpanded ? fileStyles.contentCollapsed : ""}`}
                  style={{
                    width: isChatPanelVisible 
                      ? (fileContentExpanded ? `${100 - chatPanelWidth}%` : undefined)
                      : '100%',
                    maxWidth: isChatPanelVisible 
                      ? (fileContentExpanded && activeFile ? `${100 - chatPanelWidth}%` : undefined)
                      : '100%'
                  }}
                >
                  {activeView === 'ccma' && trainingTimer && trainingTimer.kind === 'ccma' && Number(trainingTimer.minutes) > 0 ? (
                    <TrainingCountdown
                      taskId={trainingTimer.taskId}
                      minutes={trainingTimer.minutes}
                      kind={trainingTimer.kind}
                      startedAt={trainingTimer.startedAt}
                      userKey={currentUser?.unified_user_id || currentUser?.studentID || currentUser?.username || 'anon'}
                      compact
                      placement="panel"
                    />
                  ) : null}
                  <div className={fileStyles.filePanelInner}>
                    {/* 文件标签列表 */}
                    <div className={fileStyles.fileTabsContainer} ref={fileListRef}>
                      <div className="file-list-sticky-container">
                        <div className={fileStyles.fileListHeader}>
                          <span>{t('file_list')}</span>
                          <button 
                            className={fileStyles.toggleChatButton}
                            onClick={toggleChatPanel}
                            title={isChatPanelVisible ? t('hide_chat') : t('show_chat')}
                          >
                            {isChatPanelVisible ? "◀" : "▶"}
                          </button>
                        </div>
                        
                        {/* 待调用专家区域 */}
                        {pendingExperts.length > 0 && (
                          <div className="expert-pending-panel">
                            <div className="expert-pending-header">
                              <span className="expert-pending-title">
                                📌 待调用专家
                              </span>
                              <button 
                                className="expert-pending-clear"
                                onClick={() => setPendingExperts([])}
                              >
                                清空
                              </button>
                            </div>
                            <div className="expert-pending-list">
                              {pendingExperts.map((expertName, idx) => {
                                const handleChipClick = () => {
                                  const availableExperts = expertDialogContent?.experts || experts || [];
                                  const normalizedName = expertName.replace(/专家|顾问|咨询/g, '').trim().toLowerCase();
                                  
                                  let matchedExpert = availableExperts.find(e => 
                                    (e.display_name || '').toLowerCase() === normalizedName
                                  );
                                  if (!matchedExpert) {
                                    matchedExpert = availableExperts.find(e => {
                                      const eName = (e.display_name || '').replace(/专家|顾问|咨询/g, '').trim().toLowerCase();
                                      return normalizedName.includes(eName) || eName.includes(normalizedName);
                                    });
                                  }
                                  
                                  if (matchedExpert) {
                                    handleExpertSelect(matchedExpert);
                                  } else {
                                    showNotification(`未找到专家：${expertName}`, 'warning');
                                  }
                                };
                                
                                return (
                                  <div 
                                    key={`${expertName}-${idx}`}
                                    className="expert-chip"
                                    onClick={handleChipClick}
                                    title={t('call_expert', { name: expertName })}
                                  >
                                    {expertName}
                                    <span className="expert-chip-remove">×</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* 角色过滤器 - 动态生成 */}
                      {(() => {
                        // 从文件中提取所有角色
                        const allRoles = Object.values(files)
                          .filter(file => file && file.meta && file.meta.role)
                          .map(file => file.meta.role);
                        
                        // 去重并排序
                        const uniqueRoles = [...new Set(allRoles)].sort();
                        
                        // 如果没有角色，不显示过滤器
                        if (uniqueRoles.length === 0) return null;
                        
                        // 对角色进行分类
                        const roleCategories = {
                          engineer: [],
                          qa: [],
                          hardware: [],
                          other: []
                        };
                        
                        uniqueRoles.forEach(role => {
                          const lowerRole = role.toLowerCase();
                          if (lowerRole.includes('工程师') || lowerRole.includes('engineer') || 
                              lowerRole.includes('开发') || lowerRole.includes('code')) {
                            roleCategories.engineer.push(role);
                          } else if (lowerRole.includes('测试') || lowerRole.includes('test') || 
                                    lowerRole.includes('qa')) {
                            roleCategories.qa.push(role);
                          } else if (lowerRole.includes('硬件') || lowerRole.includes('hardware')) {
                            roleCategories.hardware.push(role);
                          } else {
                            // 不再排除任何角色，让专家名称也能出现在过滤器中
                            roleCategories.other.push(role);
                          }
                        });
                        
                        return (
                          <div className={fileStyles.roleFilterBar}>
                          <button
                              className={`${fileStyles.roleFilterButton} ${!activeRoleFilter ? fileStyles.roleFilterActive : ''}`}
                              onClick={() => setActiveRoleFilter(null)}
                              title={t('show_all_files')}
                            >
                              {t('filter_all')}
                          </button>
                            
                            {/* 工程师角色 */}
                            {roleCategories.engineer.length > 0 && (
                              <button
                                className={`${fileStyles.roleFilterButton} ${activeRoleFilter === 'engineer' ? fileStyles.roleFilterActive : ''}`}
                                onClick={() => setActiveRoleFilter('engineer')}
                                title={t('engineer_roles', { roles: roleCategories.engineer.join(', ') })}
                              >
                                {t('filter_engineer')}
                              </button>
                            )}
                            
                            {/* QA角色 */}
                            {roleCategories.qa.length > 0 && (
                              <button
                                className={`${fileStyles.roleFilterButton} ${activeRoleFilter === 'qa' ? fileStyles.roleFilterActive : ''}`}
                                onClick={() => setActiveRoleFilter('qa')}
                                title={t('qa_roles', { roles: roleCategories.qa.join(', ') })}
                              >
                                QA
                              </button>
                            )}
                            
                            {/* 硬件角色 */}
                            {roleCategories.hardware.length > 0 && (
                              <button 
                                className={`${fileStyles.roleFilterButton} ${activeRoleFilter === 'hardware' ? fileStyles.roleFilterActive : ''}`}
                                onClick={() => setActiveRoleFilter('hardware')}
                                title={t('hardware_roles', { roles: roleCategories.hardware.join(', ') })}
                              >
                                {t('filter_hardware')}
                              </button>
                            )}
                            
                            {/* 移除固定的专家对话过滤选项，改为动态的专家名称过滤器 */}
                            
                            {/* 其他角色 - 单独显示每个角色（包括专家名称） */}
                            {roleCategories.other.map(role => (
                              <button 
                                key={role}
                                className={`${fileStyles.roleFilterButton} ${activeRoleFilter === role ? fileStyles.roleFilterActive : ''}`}
                                onClick={() => setActiveRoleFilter(role)}
                                title={role}
                              >
                                {role.length > 10 ? role.substring(0, 10) + '...' : role}
                              </button>
                            ))}
                            </div>
                        );
                      })()}
                      
                      <div className={fileStyles.fileTabs}>
                        {/* 将文件按名称分组 */}
                        {(() => {
                          // 分组文件
                          const fileGroups = {};
                          const filesList = Object.keys(files);
                          
                          // 按原始文件名分组，不考虑角色和时间戳
                          filesList.forEach(fileId => {
                            const fileData = files[fileId];
                            if (!fileData || !fileData.meta) return;
                            
                            const { role, filename } = fileData.meta;
                            
                            // 跳过不符合角色过滤器的文件
                            if (activeRoleFilter) {
                              const isExpertDialog = fileData?.meta?.isExpertDialog;
                              const lowerRole = role.toLowerCase();
                              
                              // 工程师角色过滤条件
                              if (activeRoleFilter === 'engineer' && 
                                 !(lowerRole.includes('代码') || lowerRole.includes('编写') || lowerRole.includes('开发') || 
                                   lowerRole.includes('工程师') || lowerRole.includes('engineer') || lowerRole.includes('code'))) {
                                return;
                              }
                              // QA角色过滤条件
                              else if (activeRoleFilter === 'qa' && 
                                 !(lowerRole.includes('测试') || lowerRole.includes('test') || lowerRole.includes('qa'))) {
                                return;
                              }
                              // 硬件角色过滤条件
                              else if (activeRoleFilter === 'hardware' && 
                                 !(lowerRole.includes('硬件') || lowerRole.includes('hardware'))) {
                                return;
                              }
                              // 具体角色名称过滤（包括专家名称）
                              else if (activeRoleFilter !== 'engineer' && activeRoleFilter !== 'qa' && activeRoleFilter !== 'hardware' &&
                                  role !== activeRoleFilter) {
                                return;
                              }
                            }
                            
                            // 使用原始文件名作为分组键
                            const groupKey = filename;
                            
                            if (!fileGroups[groupKey]) {
                              fileGroups[groupKey] = [];
                            }
                            
                            fileGroups[groupKey].push(fileId);
                          });
                          
                          // 对每个组内的文件按时间戳排序（最新的在前面）
                          Object.entries(fileGroups).forEach(([groupKey, fileIds]) => {
                            // 先按时间戳排序
                            fileIds.sort((a, b) => {
                              const timestampA = files[a]?.meta?.timestamp || 0;
                              const timestampB = files[b]?.meta?.timestamp || 0;
                              return timestampB - timestampA; // 降序排列，最新时间在前面
                            });
                            
                            // 收集所有不同的时间戳
                            const uniqueTimestamps = [...new Set(fileIds.map(id => files[id]?.meta?.timestamp || 0))];
                            uniqueTimestamps.sort((a, b) => b - a); // 降序排列
                            
                            // 为每个文件分配版本号，相同时间戳的文件共享相同的版本号
                            fileIds.forEach(fileId => {
                              if (files[fileId] && files[fileId].meta) {
                                const timestamp = files[fileId].meta.timestamp || 0;
                                // 找到时间戳在唯一时间戳数组中的索引位置+1作为版本号
                                const versionIndex = uniqueTimestamps.indexOf(timestamp);
                                files[fileId].meta.version = versionIndex + 1;
                                //files[fileId].meta.version = uniqueTimestamps.length - versionIndex; // 最新的是最大版本号
                              }
                            });
                          });
                          
                          // 渲染文件组或单个文件
                          return Object.entries(fileGroups).map(([groupKey, fileIds]) => {
                            // 如果组内只有一个文件，直接渲染
                            if (fileIds.length === 1) {
                              return renderFileCard(fileIds[0]);
                            }
                            
                            // 获取第一个文件的信息作为组标题
                            const firstFile = files[fileIds[0]];
                            const groupName = firstFile?.meta?.filename || groupKey;
                            
                            // 收集所有角色
                            const roles = fileIds
                              .map(id => files[id]?.meta?.role)
                              .filter(role => role) // 过滤掉空值
                              .filter((role, index, self) => self.indexOf(role) === index); // 去重
                            
                            // 获取文件类型样式
                            const fileExt = groupName.split('.').pop().toLowerCase();
                            let fileTypeClass = '';
                            if (fileExt === 'js' || fileExt === 'jsx') {
                              fileTypeClass = fileStyles.fileTypeJs;
                            } else if (fileExt === 'json') {
                              fileTypeClass = fileStyles.fileTypeJson;
                            } else if (fileExt === 'md') {
                              fileTypeClass = fileStyles.fileTypeMd;
                            } else if (fileExt === 'html' || fileExt === 'htm') {
                              fileTypeClass = fileStyles.fileTypeHtml;
                            } else if (fileExt === 'css') {
                              fileTypeClass = fileStyles.fileTypeCss;
                            }
                            
                            // 渲染文件组
                            return (
                              <div key={groupKey} className={`${fileStyles.fileGroup} ${expandedGroups[groupKey] ? fileStyles.fileGroupExpanded : ''}`}>
                                {/* 文件组标题 - 显示文件名和版本数量 */}
                                <div 
                                  className={`${fileStyles.fileGroupHeader} ${fileTypeClass}`}
                                  onClick={() => toggleGroupExpanded(groupKey)}
                                >
                                  <div className={fileStyles.fileTypeIndicator}></div>
                                  <div className={fileStyles.fileGroupHeaderTop}>
                                    {/* 显示角色标签 */}
                                    {roles.length > 0 && roles.map((role, index) => {
                                      // 根据角色选择合适的样式类
                                      let roleTagClass = fileStyles.roleTag;
                                      const lowerRole = role.toLowerCase();
                                      
                                      if (lowerRole.includes('代码') || lowerRole.includes('编写') || lowerRole.includes('开发') || 
                                          lowerRole.includes('工程师') || lowerRole.includes('engineer') || lowerRole.includes('code')) {
                                        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagEngineer}`;
                                      } else if (lowerRole.includes('测试') || lowerRole.includes('test') || lowerRole.includes('qa')) {
                                        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagQA}`;
                                      } else if (lowerRole.includes('硬件') || lowerRole.includes('hardware')) {
                                        roleTagClass = `${fileStyles.roleTag} ${fileStyles.roleTagHardware}`;
                                      }
                                      
                                      // 截短角色名
                                      const shortRole = role.length > 6 ? role.substring(0, 6) + '...' : role;
                                      
                                    return (
                                        <span key={index} className={roleTagClass} title={role}>
                                          {shortRole}
                                        </span>
                                      );
                                    })}
                                    <span className={fileStyles.versionCount}>
                                      {fileIds.length}个版本
                                    </span>
                                    <span className={fileStyles.fileGroupToggle}>▼</span>
                                  </div>
                                  <div className={fileStyles.fileGroupHeaderContent}>
                                    <span className={fileStyles.fileTypeIcon}>
                                      {getFileIcon(groupName, files[fileIds[0]]?.meta?.role || '')}
                                    </span>
                                    <span className={fileStyles.fileGroupName}>
                                      {groupName}
                                    </span>
                                  </div>
                                </div>
                                {/* 文件组内容 - 使用简化版本的文件卡片 */}
                                <div className={fileStyles.fileGroupItems} style={{
                                  maxHeight: expandedGroups[groupKey] ? `${fileIds.length * 33 + Math.max(0, new Set(fileIds.map(id => files[id]?.meta?.timestamp || 0)).size - 1) * 9}px` : '0'
                                }}>
                                  {(() => {
                                    // 按时间戳分组
                                    const filesByTimestamp = {};
                                    fileIds.forEach(fileId => {
                                      const timestamp = files[fileId]?.meta?.timestamp || 0;
                                      if (!filesByTimestamp[timestamp]) {
                                        filesByTimestamp[timestamp] = [];
                                      }
                                      filesByTimestamp[timestamp].push(fileId);
                                    });
                                    
                                    // 按时间戳降序渲染
                                    return Object.entries(filesByTimestamp)
                                      .sort(([timestampA], [timestampB]) => Number(timestampB) - Number(timestampA))
                                      .flatMap(([timestamp, ids], index, array) => {
                                        const cards = ids.map(fileId => renderFileCard(fileId, true));
                                        
                                        // 如果不是最后一组，添加分隔符
                                        if (index < array.length - 1) {
                                          cards.push(
                                            <div 
                                              key={`divider-${timestamp}`} 
                                                style={{
                                                height: '1px',
                                                backgroundColor: '#333',
                                                margin: '4px 10px',
                                                opacity: 0.5
                                              }}
                                            />
                                          );
                                        }
                                        
                                        return cards;
                                      });
                                  })()}
                                </div>
                                      </div>
                                    );
                          });
                        })()}
                      </div>
                    </div>

                    {/* 文件内容区域 */}
                    {fileContentExpanded ? (
                      <div className={fileStyles.fileContentWrapper} style={{ position: 'relative' }}>
                        {activeFile ? (
                          <>
                            {/* 🎭 文件面板专家数字人 */}
                            <Suspense fallback={null}>
                              <DigitalHumanDialog
                                isOpen={fileExpertDigitalHumanOpen && digitalHumanGlobalEnabled}
                                expertName={currentFileExpertName}
                                text={currentFileExpertText}
                                onClose={() => setFileExpertDigitalHumanOpen(false)}
                                position="file"
                                messageType="expert_dialog_update"
                                workflowName={FlowName}
                                currentUser={currentUser}
                                classMode={classMode}
                              />
                            </Suspense>
                            
                            {/* 🖨️ 打印根：只打印该容器内容（避免把主界面头部/侧边栏打印进去） */}
                            <div className={fileStyles.printRoot} data-print-root="file-panel">
                              <div className={fileStyles.fileHeader}>
                                <div className={fileStyles.fileNameHeader}>
                                  <span className={fileStyles.fileTypeIcon}>
                                    {getFileIcon(
                                      files[activeFile]?.meta?.filename || activeFile, 
                                      files[activeFile]?.meta?.role || ''
                                    )}
                                  </span>
                                  {files[activeFile]?.meta?.role && (
                                    <span className={fileStyles.roleIndicator}>
                                      {files[activeFile]?.meta?.role}:
                                    </span>
                                  )}
                                  {files[activeFile]?.meta?.filename || activeFile}
                                  {files[activeFile]?.meta?.version && (
                                    <span className={fileStyles.versionIndicator}>
                                      (v{files[activeFile].meta.version})
                                    </span>
                                  )}
                                </div>
                                <div className={fileStyles.fileActions}>
                                  <button
                                    className={fileStyles.fileCopyButton}
                                    onClick={() => {
                                      const content = files[activeFile].content;
                                      let textToCopy = typeof content === 'string' ? content : 
                                        (content && typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content || ''));
                                      copyToClipboard(textToCopy);
                                    }}
                                    title={t('btn_copy')}
                                  >
                                    <FiCopy />
                                  </button>
                                  <button
                                    className={fileStyles.filePrintButton}
                                    onClick={handlePrintFile}
                                    title={t('btn_print')}
                                  >
                                    <FiPrinter />
                                  </button>
                                  <button
                                    className={fileStyles.fileCloseButton}
                                    onClick={handleCloseFile}
                                    title={t('btn_close_file')}
                                  >
                                    <FiX />
                                  </button>
                                </div>
                              </div>
                              <div className={fileStyles.fileContent} ref={fileContentRef}> {/* 2. 附加ref */}
                              {files[activeFile]?.meta?.isClawDialog ? (
                                <div style={{ minHeight: "100%" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#58a6ff", fontSize: "12px", padding: "8px 16px 4px", fontFamily: "'Courier New', Consolas, monospace", borderBottom: "1px solid rgba(88,166,255,0.15)" }}>
                                    <span style={{ fontSize: "14px" }}>🔬</span>
                                    <span>{files[activeFile]?.meta?.expert_name || "YouLabber 实验记录"}</span>
                                    <span style={{ color: "#8b949e", fontSize: "11px", marginLeft: "4px" }}>· 历史记录</span>
                                  </div>
                                  {renderFileContent(
                                    files[activeFile]?.meta?.filename || activeFile,
                                    files[activeFile]?.content,
                                    {
                                      selectedFileLines,
                                      handleFileLineSelection,
                                      fileContentExpanded,
                                      selectedDiffLines: { [activeFile]: selectedDiffLines[activeFile] || [] },
                                      handleDiffLineClick: (file, lineIndex) => handleDiffLineClick(activeFile, lineIndex),
                                      diffComments: { [activeFile]: diffComments[activeFile] || {} },
                                      handleCommentChange: (file, lineIndex, comment) => handleCommentChange(activeFile, lineIndex, comment),
                                      ws: ws.current,
                                      messageId: activeFile,
                                      senderInfo: files[activeFile]?.meta?.role,
                                      fileId: activeFile,
                                      originalContent: files[activeFile]?.originalContent,
                                      currentPhase: projectState,
                                      documentPhase: files[activeFile]?.meta?.phase || projectState,
                                      addLocalMessage: addLocalMessage,
                                      onContentChange: () => {},
                                      readOnly: true,
                                    }
                                  )}
                                </div>
                              ) : renderFileContent(
                                files[activeFile]?.meta?.filename || activeFile,
                                files[activeFile]?.content,          // B变量：当前编辑内容
                                {
                                  selectedFileLines,
                                  handleFileLineSelection,
                                  fileContentExpanded,
                                  selectedDiffLines: { [activeFile]: selectedDiffLines[activeFile] || [] },
                                  handleDiffLineClick: (file, lineIndex) => handleDiffLineClick(activeFile, lineIndex),
                                  diffComments: { [activeFile]: diffComments[activeFile] || {} },
                                  handleCommentChange: (file, lineIndex, comment) => handleCommentChange(activeFile, lineIndex, comment),
                                  ws: ws.current,
                                  messageId: activeFile,
                                  senderInfo: files[activeFile]?.meta?.senderInfo || files[activeFile]?.meta?.role,
                                  messages: diagramMessages, // 🎨 绘图对话历史
                                  sendSelectedContentToBackend: () => handleSendDraftToBackend(activeFile, files[activeFile]?.meta?.filename || activeFile),
                                  fileId: activeFile,
                                  originalContent: files[activeFile]?.originalContent,  // A变量：原始内容
                                  currentPhase: projectState,          // 🔑 新增：当前项目阶段
                                  documentPhase: files[activeFile]?.meta?.phase || projectState, // 🔑 新增：文档阶段（从文件元数据获取，如无则使用当前阶段）
                                  addLocalMessage: addLocalMessage,    // 🔑 新增：本地消息添加函数
                                  onContentChange: (fileId, newContent, operation) => {
                                    console.log('App.js onContentChange:', { fileId, operation, contentLength: newContent?.length });
                                    
                                    if (operation === 'first-entry-standardize') {
                                      // 首次进入标准化：同时更新A和B变量，确保基准一致
                                      console.log('执行首次进入标准化，同时更新A和B变量');
                                      setFiles(prevFiles => ({
                                        ...prevFiles,
                                        [fileId]: {
                                          ...prevFiles[fileId],
                                          content: newContent,           // B变量：当前编辑内容
                                          originalContent: newContent    // A变量：更新为标准化后的内容
                                        }
                                      }));
                                      
                                      // 标记该文件已完成首次标准化
                                      setFileFirstEntryMap(prevMap => {
                                        const newMap = new Map(prevMap);
                                        newMap.set(fileId, true);
                                        return newMap;
                                      });
                                    } else if (operation === 'source-mode-standardize-original') {
                                      // 源码模式A变量标准化：只更新A变量
                                      console.log('执行源码模式A变量标准化');
                                      setFiles(prevFiles => ({
                                        ...prevFiles,
                                        [fileId]: {
                                          ...prevFiles[fileId],
                                          originalContent: newContent    // 只更新A变量
                                        }
                                      }));
                                    } else if (operation === 'reset-revision-baseline') {
                                      // 🔑 新增：重置修订基准 - 将B变量赋值给A变量
                                      console.log('执行重置修订基准，将B变量赋值给A变量');
                                      setFiles(prevFiles => ({
                                        ...prevFiles,
                                        [fileId]: {
                                          ...prevFiles[fileId],
                                          content: newContent,           // B变量：当前编辑内容
                                          originalContent: newContent    // A变量：更新为与B变量相同的内容
                                        }
                                      }));
                                    } else {
                                      // 正常编辑操作：只更新B变量（当前编辑内容），保持A变量不变
                                      // 防止 MDXEditor 初始化期间的残缺内容覆盖正确内容：
                                      // 若新内容长度不足当前内容的 10%，且当前内容超过 50 字符，则忽略本次更新
                                      setFiles(prevFiles => {
                                        const currentFile = prevFiles[fileId];
                                        const currentLen = currentFile?.content?.length || 0;
                                        const newLen = newContent?.length || 0;
                                        if (currentLen > 50 && newLen < currentLen * 0.1) {
                                          return prevFiles;
                                        }
                                        return {
                                          ...prevFiles,
                                          [fileId]: {
                                            ...prevFiles[fileId],
                                            content: newContent
                                          }
                                        };
                                      });
                                    }
                                  }
                                }
                              )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className={fileStyles.noFileOpen}>
                            <div className={fileStyles.noFileIcon}>📄</div>
                            <p>{t('file_none')}</p>
                            <p>{t('file_select_hint')}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className={fileStyles.fileContentCollapsed}
                        onClick={handleExpandFileContent}
                        title={t('expand_file')}
                      >
                        <span className={fileStyles.expandIcon}>
                          <FiChevronRight />
                        </span>
                        <span className={fileStyles.expandText}>
                          展开文件区域
                        </span>
                    </div>
                    )}
                  </div>
                </div>
              </div>
              <MicroSceneDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                scene={selectedMicroScene}
              />
              <AISecretaryPanel
                isLoggedIn={isLoggedIn}
                isVisible={isAISecretaryVisible}
                onClose={() => setAISecretaryVisible(false)}
                sessionId={sessionId}
                archiveId={archiveId}
                currentUser={currentUser}
              />
            </>
          ) : activeTrainingMode === "youlaber" ? (
            // YouLaber 智能实验员（独立 tab，不与综合训练耦合）
            // 外层 flex:1 + minHeight:0 给 YouLaberPanel 提供"可计算的父高"，
            // 否则 L3 内的 CCPanel 输入框会撑出容器底（.App 是 min-height:100vh + flex column）
            <div className="youlaber-view-shell" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <YouLaberPanel
              apiUrl={CCMA_API_URL}
              ccSelectedDevice={ytSelectedDevice}
              ccMessages={ytMessages}
              ccStreamingText={ytStreamingText}
              ccThinkingText={ytThinkingText}
              ccSessionState={ytSessionState}
              ccStatus={ytStatus}
              ccInitInfo={ytInitInfo}
              ccPendingQuestion={ytPendingQuestion}
              ccTaskInfo={ytTaskInfo}
              ccTokenUsage={ytTokenUsage}
              ccFileTree={ytFileTree}
              ccRoster={ytRoster}
              devicePasswordError={ytDevicePasswordError}
              onSelectDevice={(device) => {
                setYTDevicePasswordError("");
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({
                    type: "cc_select_device",
                    source: "youlaber",
                    device_id: device.device_id,
                    device_name: device.device_name,
                    device_password: device.device_password,
                  }));
                }
              }}
              onDisconnect={() => {
                setYTSelectedDevice(null);
                resetYTState && resetYTState();
              }}
              onSendMessage={(text) => {
                setYTMessages((prev) => [...prev, {
                  id: `yt-user-${Date.now()}`,
                  type: "user_input",
                  content: text,
                  timestamp: Date.now(),
                }]);
                ytStreamingTextRef.current = "";
                setYTStreamingText("");
                setYTThinkingText("");
                setYTSessionState("running");
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({
                    type: "cc_message",
                    source: "youlaber",
                    content: text,
                    expert_cmd: "cc_default",
                  }));
                }
              }}
              onInterrupt={() => {
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({ type: "cc_control", source: "youlaber", payload: {
                    type: "control_request",
                    request_id: `interrupt-${Date.now()}`,
                    request: { subtype: "interrupt" },
                  }}));
                }
              }}
              onQuestionRespond={(toolUseId, answers) => {
                setYTPendingQuestion(null);
                setYTSessionState("running");
                const summary = Array.isArray(answers)
                  ? answers.map(a => a.answer).join(" / ")
                  : answers;
                setYTMessages((prev) => [...prev, {
                  id: `yt-user-${Date.now()}`,
                  type: "user_input",
                  content: `[选择] ${summary}`,
                  timestamp: Date.now(),
                }]);
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({
                    type: "cc_control",
                    source: "youlaber",
                    payload: {
                      type: "ask_user_question_response",
                      tool_use_id: toolUseId,
                      answers: Array.isArray(answers) ? answers : [{ question: "", answer: answers }],
                    },
                  }));
                }
              }}
              onSendControl={(payload) => {
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({ type: "cc_control", source: "youlaber", payload }));
                }
              }}
              onCompact={() => {
                if (ytSessionState !== "running") {
                  setYTMessages((prev) => [...prev, {
                    id: `yt-user-${Date.now()}`,
                    type: "user_input",
                    content: "/compact",
                    timestamp: Date.now(),
                  }]);
                  setYTSessionState("running");
                  if (ws.current?.readyState === WebSocket.OPEN) {
                    ws.current.send(JSON.stringify({
                      type: "cc_message",
                      source: "youlaber",
                      content: "/compact",
                      expert_cmd: "cc_default",
                    }));
                  }
                }
              }}
              onClearHistory={() => {
                if (ytSessionState === "running") return;
                if (!window.confirm("对话将归档到本机 chatlogs 目录，导出时可选包含。确定清空当前面板并重置学生机模型上下文吗？")) return;
                if (ws.current?.readyState === WebSocket.OPEN) {
                  ws.current.send(JSON.stringify({ type: "cc_clear_history", source: "youlaber" }));
                }
              }}
              onExportChatlog={handleYouLaberChatlogExport}
            />
            </div>
          ) : (
            // 专项能力训练广场
            <div
              className="specialized-training-plaza"
              style={buildAccentThemeStyle(currentUser?.branding, 'plaza')}
            >
              {selectedTraining ? (
                // 显示选中的训练智能体 - 改为横向布局
                <div className="training-agent-container">
                  {/* 左侧边栏 - 包含返回按钮和智能体列表 */}
                  {/* 移除左侧边栏中的返回按钮，已整合到 AgentChat 头部 */}
                  <div className="training-agent-sidebar">
                    
                    <h3 className="sidebar-section-title">{t('available_agents')}</h3>
                    
                    {/* 智能体列表 */}
                    <div className="agent-list">
                      {plazaAgents
                        .filter(agent => !agent.comingSoon) // 过滤掉"即将上线"的智能体（API 已按 agent_commands 过滤）
                        .map(agent => (
                          <div 
                            key={agent.id}
                            className={`agent-list-item ${selectedTraining?.id === agent.id ? 'active' : ''}`}
                            onClick={() => handleAgentClick(agent)}
                          >
                            <div className="agent-icon">
                              <IconMapper iconName={agent.icon} fontSize="small" />
                            </div>
                            <span className="agent-name">{agent.name}</span>
                          </div>
                        ))}
                    </div>
                    
                    <div className="sidebar-footer">
                      <h2 className="training-title">{selectedTraining.name}</h2>
                    </div>
                  </div>
                  
                  {/* 右侧主内容区域 - 原生 AgentChat 组件替代 iframe */}
                  <div className="training-agent-main-content">
                    <AgentChat
                      agent={selectedTraining}
                      user={currentUser}
                      expertServiceUrl={EXPERT_SERVICE_URL}
                      onBack={() => setSelectedTraining(null)}
                      trainSessionId={sessionId}
                      flowName={selectedTraining?.attributions?.[0]?.processName || FlowName || selectedTraining?.flow?.[0] || selectedTraining?.flowTags?.[0] || ''}
                      projectName="训练广场"
                    />
                  </div>
                </div>
              ) : (
                // 课程相关智能体常显；其他公开/官方智能体默认折叠
                <div className="training-plaza-content">
                  <header className="plaza-hero">
                    <span className="plaza-eyebrow">COURSE AI STUDIO</span>
                    <h2 className="plaza-title">{t('plaza_title')}</h2>
                    <p className="plaza-description">
                      上方为已加入课程关联的 AI 助手；下方可展开浏览官方与公开智能体，点击即可开始专项训练。
                    </p>
                    <div className="plaza-summary">
                      <span>{plazaAgents.filter((agent) => !agent.comingSoon && agent.scopeType === 'course').length} 位课程助手</span>
                      <span>{plazaAgents.filter((agent) => !agent.comingSoon && agent.scopeType !== 'course').length} 位其他可用</span>
                    </div>
                  </header>

                  {(() => {
                    const available = plazaAgents.filter((agent) => !agent.comingSoon);
                    const courseAgents = available.filter((agent) => agent.scopeType === 'course');
                    const otherAgents = available.filter((agent) => agent.scopeType !== 'course');
                    const renderAgentCard = (agent) => (
                      <div
                        key={agent.id}
                        className="training-card"
                        onClick={() => handleAgentClick(agent)}
                      >
                        {agent.featured && <div className="card-badge">{t('plaza_recommended')}</div>}
                        <div className={`training-card-icon ${agent.iconColor}`}>
                          <IconMapper iconName={agent.icon} fontSize="large" />
                        </div>
                        <div className="training-card-content">
                          <h3>{agent.name}</h3>
                          <p>{agent.description}</p>
                          <div className="training-card-resource">
                            <span>单智能体资源</span>
                            {agent.plazaAgentReleaseId && <span>市集版本 #{agent.plazaAgentReleaseId}</span>}
                          </div>
                          {Array.isArray(agent.attributions) && agent.attributions.length > 0 && (
                            <div className="training-card-attributions">
                              {agent.attributions.map((item) => (
                                <div className="training-card-attribution" key={`${item.courseId}-${item.processName}`}>
                                  <span>{item.courseName}</span>
                                  <strong>{item.processDisplayName || item.processName}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="training-card-footer">
                            <span className="training-card-category">{agent.category}</span>
                            <span className="training-card-duration">{agent.duration}</span>
                          </div>
                        </div>
                      </div>
                    );
                    return (
                      <>
                        <section className="plaza-scope-section">
                          <div className="plaza-scope-title">
                            <span>我的课程智能体</span>
                            <small>{courseAgents.length} 个</small>
                          </div>
                          {courseAgents.length > 0 ? (
                            <div className="training-cards-grid">{courseAgents.map(renderAgentCard)}</div>
                          ) : (
                            <div className="plaza-empty-state">
                              当前已加入课程暂无关联的专项 AI 助手。教师可在课程入口面板布置专项训练关卡，或将智能体挂到对本课可见的综合训练流程上。
                            </div>
                          )}
                        </section>
                        {otherAgents.length > 0 && (
                          <section className="plaza-scope-section plaza-scope-other">
                            <button
                              type="button"
                              className="plaza-other-toggle"
                              onClick={() => setShowOtherPlazaAgents((v) => !v)}
                            >
                              <span>其他可用智能体</span>
                              <small>{otherAgents.length} 个 · {showOtherPlazaAgents ? '收起' : '展开'}</small>
                            </button>
                            {showOtherPlazaAgents && (
                              <div className="training-cards-grid">{otherAgents.map(renderAgentCard)}</div>
                            )}
                          </section>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </>
      )}
      <ModalLayer
        isArchivesModalOpen={isArchivesModalOpen}
        setIsArchivesModalOpen={setIsArchivesModalOpen}
        archivesList={archivesList}
        handleDeleteArchive={handleDeleteArchive}
        handleRecoverSpecificArchive={handleRecoverSpecificArchive}
        handleEditProjectName={handleEditProjectName}
        handleRequestArchives={handleRequestArchives}
        currentUser={currentUser}
        isSelfAssessmentDialogOpen={isSelfAssessmentDialogOpen}
        setIsSelfAssessmentDialogOpen={setIsSelfAssessmentDialogOpen}
        selfAssessmentStatus={selfAssessmentStatus}
        handleSelfAssessmentSubmitSuccess={handleSelfAssessmentSubmitSuccess}
        isCapabilityProfileDialogOpen={isCapabilityProfileDialogOpen}
        setIsCapabilityProfileDialogOpen={setIsCapabilityProfileDialogOpen}
        currentStudentId={currentStudentId}
        isRecruitEncouragementPanelOpen={isRecruitEncouragementPanelOpen}
        setIsRecruitEncouragementPanelOpen={setIsRecruitEncouragementPanelOpen}
        isTokenUsageLogsOpen={isTokenUsageLogsOpen}
        setIsTokenUsageLogsOpen={setIsTokenUsageLogsOpen}
        tokenGrant={tokenGrant}
        tokenBalance={tokenBalance}
        totalTokens={totalTokens}
        planName={planName}
        showGameCenter={showGameCenter}
        setShowGameCenter={setShowGameCenter}
        gameProfile={gameProfile}
        gameConfig={gameConfig}
        fetchAchievementWall={fetchAchievementWall}
        fetchGameLeaderboard={fetchGameLeaderboard}
        fetchSkillHistory={fetchSkillHistory}
        fetchGrowthMap={fetchGrowthMap}
        achievementWallData={achievementWallData}
        leaderboardData={leaderboardData}
        skillHistoryData={skillHistoryData}
        growthMapData={growthMapData}
        todayYouLiveCards={todayYouLiveCards}
        onMarkTodayYouRead={onMarkTodayYouRead}
      />

      {/* Advanced模式密码对话框 */}
      <Dialog 
        open={showAdvancedPasswordDialog} 
        onClose={handleAdvancedPasswordCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {ADVANCED_MODE_CONFIG.passwordPrompt}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            type="password"
            fullWidth
            variant="outlined"
            value={advancedPassword}
            onChange={(e) => setAdvancedPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAdvancedPasswordSubmit();
              }
            }}
            placeholder={t('password_placeholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdvancedPasswordCancel}>{t('btn_cancel')}</Button>
          <Button onClick={handleAdvancedPasswordSubmit} variant="contained" color="primary">
            确认
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={isEditNameModalOpen} 
        onClose={handleCloseEditModal}
      >
        <DialogTitle>
          修改项目名称
          <IconButton
            aria-label="close"
            onClick={handleCloseEditModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSaveProjectName}>
            <div className="project-name-input-container">
              <input
                type="text"
                value={newProjectName || ''}
                onChange={(e) => {
                  // 超长时截断写入，而不是拒绝 setState（否则 > 上限的旧名称无法删改）
                  setNewProjectName(clipProjectName(e.target.value));
                }}
                className="project-name-input"
                placeholder={t('edit_note_placeholder')}
                autoFocus
              />
              <div style={{ 
                fontSize: '0.85rem', 
                color: countProjectNameChars(newProjectName) >= MAX_PROJECT_NAME_CHARS ? '#ff9800' : '#888',
                marginTop: '4px',
                textAlign: 'right'
              }}>
                {countProjectNameChars(newProjectName)}/{MAX_PROJECT_NAME_CHARS} 字符
              </div>
            </div>
            <div className="button-group">
              <button type="submit">{t('btn_save')}</button>
              <button type="button" onClick={handleCloseEditModal}>{t('btn_cancel')}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ErrorModal 放在这里 */}
      {error && (
        <ErrorModal 
          open={!!error} 
          onClose={() => setError(null)} 
          message={error.message} 
        />
      )}

     

      {/* 添加作业对话框 */}
      {(currentUser?.isSchoolUser || currentUser?.isCasUser) && (
        <HomeworkDialog
          open={showHomeworkDialog}
          onClose={() => {
            setShowHomeworkDialog(false);
            setIsFromMenu(false);  // 关闭时重置状态
            setFocusHomeworkTaskId(null);
            setFocusHomeworkMode(null);
            setFocusHomeworkCourseId(null);
          }}
          username={currentUser.username || ''}
          userInfo={{
            name: currentUser.name,
            className: currentUser.class_name,
            studentNumber: currentUser.student_number,
            studentID: currentUser.studentID,
            employeeNumber: currentUser.employeeNumber,
            user_type: currentUser.user_type
          }}
          homeworks={homeworks}
          setHomeworks={setHomeworks}
          setFlowName={setFlowName}
          onLaunchTraining={resumeOrLaunchCcma}
          ws={ws.current}
          sessionId={sessionId}
          archivesList={archivesList}
          trainingInProgress={hasUserSentMessage}
          onResumeTraining={() => resumeOrLaunchCcma(FlowName)}
          isFromMenu={isFromMenu}  // 传递是否从菜单打开的标记
          focusTaskId={focusHomeworkTaskId}
          focusMode={focusHomeworkMode}
          focusCourseId={focusHomeworkCourseId}
          accentColor={currentUser?.branding?.themeColor}
        />
      )}

      {/* 专项/群聊倒计时改挂各自顶栏，避免被训练浮层盖住 */}
      {fullscreenAgent && activeView === 'plaza' && (
        <FullscreenAgentView 
          agent={fullscreenAgent} 
          onClose={() => setFullscreenAgent(null)}
          user={currentUser}
          expertServiceUrl={EXPERT_SERVICE_URL}
          flowName={fullscreenAgent?.attributions?.[0]?.processName || FlowName || fullscreenAgent?.flow?.[0] || ''}
          headerAccessory={activeView === 'plaza' && trainingTimer && trainingTimer.kind === 'plaza' && Number(trainingTimer.minutes) > 0 ? (
            <TrainingCountdown
              taskId={trainingTimer.taskId}
              minutes={trainingTimer.minutes}
              kind={trainingTimer.kind}
              startedAt={trainingTimer.startedAt}
              userKey={currentUser?.unified_user_id || currentUser?.studentID || currentUser?.username || 'anon'}
              compact
              placement="header"
            />
          ) : null}
        />
      )}

      {/* 邮百工导览员已融入「我的课程」右侧栏，不再使用全屏浮层 */}

      {/* 全屏绘图视图 —— 关闭统一回到 ccma 主页 */}
      {isDrawingFullscreen && (
        <FullscreenDrawingView 
          onClose={() => switchView('ccma')}
          currentUser={currentUser}
          sessionId={sessionId}
        />
      )}

      {/* 群聊协作面板 —— 关闭统一回到 ccma 主页 */}
      <GroupChatPanel
        isOpen={isGroupChatOpen}
        headerAccessory={activeView === 'groupchat' && trainingTimer && trainingTimer.kind === 'groupchat' && Number(trainingTimer.minutes) > 0 ? (
          <TrainingCountdown
            taskId={trainingTimer.taskId}
            minutes={trainingTimer.minutes}
            kind={trainingTimer.kind}
            startedAt={trainingTimer.startedAt}
            userKey={currentUser?.unified_user_id || currentUser?.studentID || currentUser?.username || 'anon'}
            compact
            placement="header"
          />
        ) : null}
        onClose={() => {
          setGcArchiveBreadcrumb(null);
          setTemplateId(null);
          switchView('portal');
        }}
        currentUser={currentUser}
        roomId={gcRoomId}
        setRoomId={setGcRoomId}
        projectName={gcProjectName}
        setProjectName={setGcProjectName}
        currentPhase={gcCurrentPhase}
        setCurrentPhase={setGcCurrentPhase}
        phaseLabel={gcPhaseLabel}
        setPhaseLabel={setGcPhaseLabel}
        phases={gcPhases}
        setPhases={setGcPhases}
        members={gcMembers}
        setMembers={setGcMembers}
        agents={gcAgents}
        setAgents={setGcAgents}
        messages={gcMessages}
        setMessages={setGcMessages}
        streamingContent={gcStreamingContent}
        setStreamingContent={setGcStreamingContent}
        roundsInPhase={gcRoundsInPhase}
        setRoundsInPhase={setGcRoundsInPhase}
        maxRounds={gcMaxRounds}
        setMaxRounds={setGcMaxRounds}
        status={groupChatStatus}
        setStatus={setGroupChatStatus}
        isWaiting={groupChatWaiting}
        setIsWaiting={setGroupChatWaiting}
        templateId={templateId}
        setTemplateId={setTemplateId}
        myUserId={gcMyUserId}
        setMyUserId={setGcMyUserId}
        groupChatWsRef={groupChatWsRef}
        onReset={resetGroupChatState}
        onArchiveViewChange={setGcArchiveBreadcrumb}
        onConnectionStatusChange={setGroupChatConnStatus}
        showNotification={showNotification}
        onArchiveSaved={() => {
          // 存档列表统一由 EduGate HTTP 刷新，不依赖当前是否建立 CCMA 会话。
          handleRequestArchives('groupchat', false);
        }}
      />

      {/* YouLaber 实时工作区浮层 —— 关闭只卸载视图，不清空 ccma 会话 */}
      <YouLaberFullscreenOverlay
        open={ccOverlayOpen}
        onClose={() => setCCOverlayOpen(false)}
        apiUrl={CCMA_API_URL}
        ccSelectedDevice={ccSelectedDevice}
        ccMessages={ccMessages}
        ccStreamingText={ccStreamingText}
        ccThinkingText={ccThinkingText}
        ccSessionState={ccSessionState}
        ccStatus={ccStatus}
        ccInitInfo={ccInitInfo}
        ccPendingQuestion={ccPendingQuestion}
        ccTaskInfo={ccTaskInfo}
        ccTokenUsage={ccTokenUsage}
        ccFileTree={ccFileTree}
        ccRoster={ccRoster}
        onSendMessage={handleCCMASendMessage}
        onInterrupt={handleCCMAInterrupt}
        onQuestionRespond={handleCCMAQuestionRespond}
        onSendControl={handleCCMASendControl}
        onCompact={handleCCMACompact}
        onClearHistory={handleCCMAClearHistory}
        onExportChatlog={handleYouLaberChatlogExport}
        onOpenArchive={() => {
          if (!ccOverlayFileId) return;
          setFiles(prev => ({
            ...prev,
            ...(prev[ccOverlayFileId] ? {
              [ccOverlayFileId]: {
                ...prev[ccOverlayFileId],
                meta: { ...prev[ccOverlayFileId].meta, isCCDevice: false },
              },
            } : {}),
          }));
          setCCOverlayOpen(false);
          handleFileCardClick(ccOverlayFileId);
        }}
      />

      {/* 研讨厅面板 —— 关闭统一回到 ccma 主页 */}
      <DiscussionPanel
        isOpen={isDiscussOpen}
        onClose={() => switchView('ccma')}
        currentUser={currentUser}
        sessionId={discussSessionId}
        setSessionId={setDiscussSessionId}
        phase={discussPhase}
        setPhase={setDiscussPhase}
        panelists={discussPanelists}
        setPanelists={setDiscussPanelists}
        messages={discussMessages}
        setMessages={setDiscussMessages}
        topicSummary={discussTopicSummary}
        setTopicSummary={setDiscussTopicSummary}
        isWaiting={isDiscussWaiting}
        setIsWaiting={setIsDiscussWaiting}
        status={discussStatus}
        setStatus={setDiscussStatus}
        wsRef={discussWsRef}
        onReset={resetDiscussState}
      />

      {/* 修改密码对话框 */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        aria-labelledby="password-dialog-title"
        className="workbench-dialog password-dialog"
        PaperProps={{
          className: 'workbench-dialog-paper',
          style: {
            "--workbench-accent": currentUser?.branding?.themeColor || "#4D8BFF",
            backgroundColor: "#1e1e1e",
            color: "#e7e7e7",
            minWidth: "450px",
            borderRadius: "10px",
            boxShadow: "0 18px 48px rgba(0, 0, 0, 0.38)",
          },
        }}
      >
        <DialogTitle id="password-dialog-title" className="workbench-dialog-title" sx={{
          borderBottom: '1px solid #3e3e42',
          borderTop: `2px solid ${currentUser?.branding?.themeColor || '#4D8BFF'}`,
          padding: '16px 24px',
          fontSize: '18px',
          fontWeight: 500
        }}>
          <KeyIcon sx={{ verticalAlign: 'middle', marginRight: '10px', color: currentUser?.branding?.themeColor || '#4D8BFF' }} />
          {t('menu_change_password')}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <form onSubmit={handlePasswordSubmit} style={{ width: "100%" }}>
            <TextField
              margin="dense"
              name="oldPassword"
              label={t('label_current_password')}
              type="password"
              fullWidth
              variant="outlined"
              value={passwordForm.oldPassword}
              onChange={handlePasswordChange}
              sx={{
                marginBottom: '16px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: currentUser?.branding?.themeColor || '#4D8BFF',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: "#aaa" },
                shrink: true,
              }}
              InputProps={{
                style: { color: "#fff" },
              }}
            />
            <TextField
              margin="dense"
              name="newPassword"
              label={t('label_new_password')}
              type="password"
              fullWidth
              variant="outlined"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              sx={{
                marginBottom: '16px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: currentUser?.branding?.themeColor || '#4D8BFF',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: "#aaa" },
                shrink: true,
              }}
              InputProps={{
                style: { color: "#fff" },
              }}
            />
            <TextField
              margin="dense"
              name="confirmPassword"
              label={t('label_confirm_password')}
              type="password"
              fullWidth
              variant="outlined"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              sx={{
                marginBottom: '16px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: currentUser?.branding?.themeColor || '#4D8BFF',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: "#aaa" },
                shrink: true,
              }}
              InputProps={{
                style: { color: "#fff" },
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '16px' 
            }}>
              <Button 
                onClick={() => setPasswordDialogOpen(false)} 
                sx={{ 
                  color: "#aaa",
                  borderRadius: '6px',
                  padding: '8px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                取消
              </Button>
              <Button 
                type="submit" 
                sx={{ 
                  backgroundColor: currentUser?.branding?.themeColor || '#4D8BFF',
                  color: "#fff",
                  borderRadius: '6px',
                  padding: '8px 16px',
                  '&:hover': {
                    backgroundColor: '#2980b9'
                  }
                }}
              >
                确认修改
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>



      {/* 课程管理对话框 */}
      <CourseManagementDialog
        open={isCourseManagementDialogOpen}
        onClose={() => {
          setIsCourseManagementDialogOpen(false);
          if (activeTrainingMode === 'portal') {
            setPortalRefreshKey((key) => key + 1);
          }
        }}
        currentUser={currentUser}
        showNotification={showNotification}
      />

      {/* 双创项目对话框 */}
      <YuyanProjectDialog
        open={isYuyanProjectDialogOpen}
        onClose={() => setIsYuyanProjectDialogOpen(false)}
        currentUser={currentUser}
      />

      {/* 流程类型选择对话框 */}
      <FlowTypeDialog
        open={isFlowTypeDialogOpen}
        onClose={() => setIsFlowTypeDialogOpen(false)}
        currentFlow={FlowName}
        flowTypes={availableFlowTypes}
        onSelect={handleFlowTypeSelect}
        accentColor={currentUser?.branding?.themeColor}
      />

      {/* 任务选择对话框 */}
              <TaskSelectionDialog
          open={taskSelectionDialog.open}
          onClose={() => setTaskSelectionDialog({ open: false, isRestore: false, isUserModification: false, updateId: null })}
          originalTasks={originalTaskList}
          currentSelection={userSelection}
          isRestore={taskSelectionDialog.isRestore}
          updateId={taskSelectionDialog.updateId}
          onConfirm={handleTaskSelectionConfirm}
        />

      {/* ModalLayer moved above for archives/assessment/token/game center */}

      {/* 等级提升动画 */}
      {levelUpInfo && (
        <LevelUpAnimation
          level={levelUpInfo.newLevel}
          open={!!levelUpInfo}
          onClose={() => setLevelUpInfo(null)}
        />
      )}

      {/* 成就解锁动画 */}
      {unlockedAchievements.length > 0 && (
        <AchievementUnlocked
          achievements={unlockedAchievements}
          open={unlockedAchievements.length > 0}
          onClose={() => setUnlockedAchievements([])}
        />
      )}

      {/* 🔑 新增：文档冲突解决对话框 */}
      <Dialog
        open={conflictDialog.isOpen}
        onClose={() => {}} // 禁用点击外部关闭，强制用户选择
        maxWidth="md"
        fullWidth
        className="conflict-resolution-dialog"
        PaperProps={{
          style: {
            backgroundColor: '#2c3e50',
            color: '#fff',
            borderRadius: '12px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle style={{ 
          backgroundColor: '#34495e', 
          color: '#fff',
          borderBottom: '1px solid #555',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          🚨 文档编辑冲突
        </DialogTitle>
        <DialogContent style={{ padding: '24px', backgroundColor: '#2c3e50' }}>
          <div style={{ marginBottom: '20px' }}>
            <Typography variant="h6" style={{ color: '#e74c3c', marginBottom: '12px' }}>
              检测到文档编辑冲突：{conflictDialog.filename}
            </Typography>
            <Typography variant="body1" style={{ color: '#bdc3c7', marginBottom: '16px' }}>
              后端发送了新版本的文档，但您在本地有未保存的编辑。请选择如何处理：
            </Typography>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px',
            marginBottom: '24px'
          }}>
            {/* 当前编辑内容预览 */}
            <div style={{ 
              border: '2px solid #f39c12', 
              borderRadius: '8px', 
              padding: '16px',
              backgroundColor: '#3a4a5c'
            }}>
              <Typography variant="subtitle1" style={{ 
                color: '#f39c12', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                📝 您的编辑内容 (B)
              </Typography>
              <Typography variant="body2" style={{ 
                color: '#bdc3c7',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                长度: {conflictDialog.currentContent?.length || 0} 字符
              </Typography>
              <div style={{
                backgroundColor: '#2c3e50',
                border: '1px solid #555',
                borderRadius: '4px',
                padding: '12px',
                maxHeight: '200px',
                overflow: 'auto',
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                color: '#ecf0f1'
              }}>
                {conflictDialog.currentContent?.substring(0, 500)}
                {(conflictDialog.currentContent?.length || 0) > 500 && '...'}
              </div>
            </div>

            {/* 新文档内容预览 */}
            <div style={{ 
              border: '2px solid #27ae60', 
              borderRadius: '8px', 
              padding: '16px',
              backgroundColor: '#3a4a5c'
            }}>
              <Typography variant="subtitle1" style={{ 
                color: '#27ae60', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                🆕 后端新文档 (C)
              </Typography>
              <Typography variant="body2" style={{ 
                color: '#bdc3c7',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                长度: {conflictDialog.newContent?.length || 0} 字符
              </Typography>
              <div style={{
                backgroundColor: '#2c3e50',
                border: '1px solid #555',
                borderRadius: '4px',
                padding: '12px',
                maxHeight: '200px',
                overflow: 'auto',
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                color: '#ecf0f1'
              }}>
                {conflictDialog.newContent?.substring(0, 500)}
                {(conflictDialog.newContent?.length || 0) > 500 && '...'}
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#34495e',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <Typography variant="body2" style={{ color: '#f1c40f', fontWeight: 'bold' }}>
              ⚠️ 注意事项：
            </Typography>
            <ul style={{ color: '#bdc3c7', fontSize: '14px', margin: '8px 0 0 20px' }}>
              <li>{t('conflict_adopt_new')}</li>
              <li>{t('conflict_keep_edit')}</li>
              <li>{t('conflict_warning')}</li>
            </ul>
          </div>
        </DialogContent>
        <DialogActions style={{ 
          backgroundColor: '#34495e', 
          padding: '16px 24px',
          borderTop: '1px solid #555'
        }}>
          <Button
            onClick={() => handleConflictResolution('adopt_new', conflictDialog.fileId)}
            variant="contained"
            style={{
              backgroundColor: '#27ae60',
              color: '#fff',
              fontWeight: 'bold',
              padding: '10px 24px',
              borderRadius: '6px',
              marginRight: '12px'
            }}
            startIcon={<span>🆕</span>}
          >
            采用新文档
          </Button>
          <Button
            onClick={() => handleConflictResolution('keep_current', conflictDialog.fileId)}
            variant="contained"
            style={{
              backgroundColor: '#f39c12',
              color: '#fff',
              fontWeight: 'bold',
              padding: '10px 24px',
              borderRadius: '6px'
            }}
            startIcon={<span>📝</span>}
          >
            保留我的编辑
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Toast通知提示 */}
      {currentToastNotification && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          background: '#2c3e50',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          minWidth: '300px',
          maxWidth: '400px',
          zIndex: 10000,
          borderLeft: `4px solid ${currentToastNotification.level === 'urgent' ? '#e74c3c' : '#3498db'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600
            }}>
              <span>🔔</span>
              <span>{t('new_message')}</span>
            </div>
            <button
              onClick={() => setCurrentToastNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#95a5a6',
                cursor: 'pointer',
                fontSize: '18px',
                padding: 0
              }}
            >
              ×
            </button>
          </div>
          <div style={{
            fontSize: '13px',
            color: '#ecf0f1',
            marginBottom: '4px',
            fontWeight: 'bold'
          }}>
            {currentToastNotification.title}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#bdc3c7'
          }}>
            {currentToastNotification.content}
          </div>
        </div>
      )}

      {/* ✅ 通知中心对话框 */}
      <NotificationCenter
        open={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        currentUser={currentUser}
      />

      {/* 🔬 深度研究中心 */}
      {currentUser && (
        <Suspense fallback={null}>
          <ResearchCenter
            open={researchCenterOpen}
            onClose={() => setResearchCenterOpen(false)}
            userId={currentUser.unified_user_id || currentUser.username}
            tokenBalance={tokenBalance}
            onTasksChange={setResearchTasks}
            onUnreadCountChange={setUnreadResearchCount}
          />
        </Suspense>
      )}

      {/* 🎭 数字人模拟配置对话框 */}
      <Suspense fallback={null}>
        <DigitalHumanSimulationDialog
          open={isSimDialogOpen}
          onClose={() => setIsSimDialogOpen(false)}
          onStart={handleStartSimulation}
        />
      </Suspense>

      {/* 🤖 CC 智能实验员选择弹窗 */}
      <CCDeviceSelector
        open={ccDeviceSelectorOpen}
        courseId={""}
        deviceType={ccDeviceTypeFilter}
        apiUrl={CCMA_API_URL}
        onSelect={(device) => {
          setCCSelectedDevice(device);
          setCCDeviceSelectorOpen(false);
          // 通知 GoMAown 记录选择的设备
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: "cc_select_device",
              source: "ccma",
              device_id: device.device_id,
              device_name: device.device_name,
              device_password: device.device_password,
            }));
          }
        }}
        onClose={() => setCCDeviceSelectorOpen(false)}
      />

      {/* 🔒 CC 工具调用权限审批弹窗 */}
      <CCPermissionDialog
        permission={ccPendingPermission}
        onRespond={(allowed, requestId) => {
          setCCPendingPermission(null);
          setCCSessionState("running");
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: "cc_control",
              source: "ccma",
              payload: {
                type: "control_response",
                response: {
                  subtype: "success",
                  request_id: requestId,
                  response: allowed
                    ? { behavior: "allow", updatedInput: {} }
                    : { behavior: "deny", message: "用户拒绝" },
                },
              },
            }));
          }
        }}
      />

      {/* 🔐 Claw 实验机接入密码弹窗 */}
      <Dialog
        open={clawPasswordDialogOpen}
        onClose={() => {
          if (!clawPasswordLoading) {
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({ type: "claw_password_cancel" }));
            }
            resetClawPasswordState();
          }
        }}
        aria-labelledby="claw-password-dialog-title"
        PaperProps={{
          style: {
            backgroundColor: "#1e1e2e",
            color: "#fff",
            minWidth: "380px",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <DialogTitle id="claw-password-dialog-title" sx={{
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          padding: "16px 24px",
          fontSize: "18px",
          fontWeight: 500,
        }}>
          🖥️ 实验机接入密码
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="接入密码"
            type="password"
            fullWidth
            variant="outlined"
            value={clawPasswordInput}
            error={!!clawPasswordError}
            helperText={clawPasswordError}
            onChange={(e) => { setClawPasswordInput(e.target.value); if (clawPasswordError) setClawPasswordError(""); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !clawPasswordLoading) {
                if (clawPasswordInput && ws.current && ws.current.readyState === WebSocket.OPEN) {
                  setClawPasswordLoading(true);
                  setClawPasswordError("");
                  ws.current.send(JSON.stringify({ type: "claw_password", password: clawPasswordInput }));
                }
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "rgba(255,255,255,0.2)", borderRadius: "8px" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&.Mui-focused fieldset": { borderColor: "#6c63ff" },
              },
            }}
            InputLabelProps={{ style: { color: "#aaa" }, shrink: true }}
            InputProps={{ style: { color: "#fff" } }}
            FormHelperTextProps={{ style: { color: "#f44336" } }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Button
            onClick={() => {
              if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                ws.current.send(JSON.stringify({ type: "claw_password_cancel" }));
              }
              resetClawPasswordState();
            }}
            disabled={clawPasswordLoading}
            sx={{ color: "#aaa", "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" } }}
          >
            取消
          </Button>
          <Button
            onClick={() => {
              if (clawPasswordInput && ws.current && ws.current.readyState === WebSocket.OPEN && !clawPasswordLoading) {
                setClawPasswordLoading(true);
                setClawPasswordError("");
                ws.current.send(JSON.stringify({ type: "claw_password", password: clawPasswordInput }));
              }
            }}
            disabled={clawPasswordLoading || !clawPasswordInput}
            variant="contained"
            sx={{
              backgroundColor: "#6c63ff",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#5a52e0" },
              "&.Mui-disabled": { backgroundColor: "rgba(108,99,255,0.4)" },
            }}
          >
            {clawPasswordLoading ? "验证中..." : "确认"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  )
}

export default AppRouter;